import { useState, useEffect, useRef, useCallback } from "react";
import type {
  Supplier,
  User,
  AuditEntry,
  Company,
  Role,
  SupplierStatus,
  SlaStatus,
  GeneralFormData,
  FormDataSnapshot,
  BankReference,
  Signatory,
  Client,
} from "./types";
import EasyCallLogo from "./Images/EasyCallLogo.png";

type AuthUser = User & { token?: string };

function parseStoredCurrentUser(): AuthUser | null {
  try {
    const saved = sessionStorage.getItem("currentUser");
    if (!saved) return null;
    const parsed = JSON.parse(saved) as AuthUser;
    if (!parsed || typeof parsed !== "object") return null;
    if (!parsed.token || typeof parsed.token !== "string" || !parsed.token.trim()) {
      sessionStorage.removeItem("currentUser");
      return null;
    }
    return parsed;
  } catch {
    sessionStorage.removeItem("currentUser");
    return null;
  }
}

// ── Type Definitions ──────────────────────────────────────────────────────────

interface CompanyNamesType {
  ECP: string;
  TESI: string;
}

interface StatusMetaType {
  [key: string]: {
    color: string;
    dot: string;
  };
}

interface SlaMetaType {
  "on-time": { label: string; icon: string; cls: string };
  warning: { label: string; icon: string; cls: string };
  delayed: { label: string; icon: string; cls: string };
}

interface CategoriesType {
  [key: string]: string[];
}

const COMPANIES = ["ECP", "TESI"] as const;
const COMPANY_NAMES: CompanyNamesType = {
  ECP: "Easycall Communications Philippines, Inc.",
  TESI: "Transnational E-Business Solutions Inc.",
};

const CATEGORIES: CategoriesType = {
  "Agriculture, Forestry & Fishing": [
    "Crop farming (rice, corn, vegetables, fruits)",
    "Livestock & poultry",
    "Aquaculture / fish farming",
    "Logging / forestry",
    "Agricultural trading",
    "Other",
  ],
  Manufacturing: [
    "Food processing (bakery, meat processing, beverages)",
    "Garments & textile production",
    "Furniture manufacturing",
    "Electronics manufacturing",
    "Plastic products manufacturing",
    "Construction materials production",
    "Cosmetics manufacturing",
    "Other",
  ],
  "Wholesale & Retail Trade": [
    "Sari-sari store",
    "Grocery / supermarket",
    "Hardware store",
    "Pharmacy",
    "Online retail / e-commerce",
    "Auto parts trading",
    "Import-export trading",
    "Other",
  ],
  Construction: [
    "General construction",
    "Electrical contractor",
    "Plumbing contractor",
    "Renovation services",
    "Real estate development",
    "Other",
  ],
  "Transportation & Storage": [
    "Trucking services",
    "Delivery / courier services",
    "Logistics services",
    "Warehousing",
    "Taxi / TNVS operations",
    "Other",
  ],
  "Accommodation & Food Service": [
    "Restaurant",
    "Fast food",
    "Café / coffee shop",
    "Catering services",
    "Hotel / hostel / resort",
    "Food cart / kiosk",
    "Other",
  ],
  "Information & Communication": [
    "IT services",
    "Software development",
    "Web design",
    "Digital marketing",
    "Call center / BPO",
    "Telecommunications services",
    "Content creation / vlogging business",
    "Other",
  ],
  "Financial & Insurance Activities": [
    "Lending company",
    "Financing company",
    "Insurance agency",
    "Remittance center",
    "Pawnshop",
    "Microfinance",
    "Other",
  ],
  "Real Estate Activities": [
    "Real estate brokerage",
    "Property leasing",
    "Property management",
    "Apartment rental",
    "Other",
  ],
  "Professional, Scientific & Technical Services": [
    "Accounting services",
    "Law firm",
    "Engineering services",
    "Architectural services",
    "Consultancy services",
    "Advertising agency",
    "Other",
  ],
  "Administrative & Support Services": [
    "Security agency",
    "Janitorial services",
    "Recruitment agency",
    "Travel agency",
    "Event management",
    "Other",
  ],
  Education: [
    "Private school",
    "Tutorial center",
    "Training center",
    "Online education services",
    "Other",
  ],
  "Human Health & Social Work": [
    "Clinic",
    "Diagnostic laboratory",
    "Pharmacy",
    "Wellness spa",
    "Home care services",
    "Other",
  ],
  "Arts, Entertainment & Recreation": [
    "Gym / fitness center",
    "Gaming center",
    "Photography studio",
    "Film production",
    "Amusement center",
    "Other",
  ],
  "Other Service Activities": [
    "Beauty salon / barbershop",
    "Laundry shop",
    "Repair services (appliances, gadgets)",
    "Car wash",
    "Funeral services",
    "Other",
  ],
};

const STATUS_META: StatusMetaType = {
  Enrolled: { color: "bg-blue-100 text-blue-800", dot: "bg-blue-500" },
  "Form Completion": {
    color: "bg-yellow-100 text-yellow-800",
    dot: "bg-yellow-500",
  },
  "Under Review": {
    color: "bg-orange-100 text-orange-800",
    dot: "bg-orange-500",
  },
  "Recommended for Approval": {
    color: "bg-cyan-100 text-cyan-800",
    dot: "bg-cyan-500",
  },
  "For Compliance": {
    color: "bg-purple-100 text-purple-800",
    dot: "bg-purple-500",
  },
  Approved: { color: "bg-green-100 text-green-800", dot: "bg-green-500" },
  Disapproved: { color: "bg-red-100 text-red-800", dot: "bg-red-500" },
  "Returned with Remarks": {
    color: "bg-rose-100 text-rose-800",
    dot: "bg-rose-500",
  },
  "Modification Endorsement Pending": {
    color: "bg-yellow-100 text-yellow-800",
    dot: "bg-yellow-500",
  },
  "Modification Requested": {
    color: "bg-indigo-100 text-indigo-800",
    dot: "bg-indigo-500",
  },
  "Modification Pre-Approved": {
    color: "bg-teal-100 text-teal-800",
    dot: "bg-teal-500",
  },
  "Modification Approved": {
    color: "bg-emerald-100 text-emerald-800",
    dot: "bg-emerald-500",
  },
};

const STATUS_DISPLAY_NAMES: Partial<Record<SupplierStatus, string>> = {
  "Under Review": "Under Compliance Review",
  "For Compliance": "For Compliance (Revision Requested)",
  "Recommended for Approval": "Recommended for Approval (Pending Final)",
};

function getStatusLabel(status: SupplierStatus): string {
  return STATUS_DISPLAY_NAMES[status] ?? status;
}

const SLA: SlaMetaType = {
  "on-time": {
    label: "On Time",
    icon: "",
    cls: "text-green-700 bg-green-50 border-green-200",
  },
  warning: {
    label: "Warning",
    icon: "",
    cls: "text-yellow-700 bg-yellow-50 border-yellow-200",
  },
  delayed: {
    label: "Delayed",
    icon: "",
    cls: "text-red-700 bg-red-50 border-red-200",
  },
};

const TODAY = new Date().toISOString().split("T")[0];

// ── Formatting Utilities ──────────────────────────────────────────────────────
/** Converts YYYY-MM-DD (or ISO timestamp) → MM/DD/YYYY */
const fmtDate = (d: string | undefined): string => {
  if (!d) return "N/A";
  const dateOnly = d.split("T")[0];
  const parts = dateOnly.split("-");
  if (parts.length !== 3) return d;
  const [y, m, day] = parts;
  return `${m}/${day}/${y}`;
};

/** Converts ISO timestamp → MM/DD/YYYY, HH:MM AM/PM */
const fmtDateTime = (d: string | undefined): string => {
  if (!d) return "N/A";
  const date = new Date(d);
  if (isNaN(date.getTime())) return fmtDate(d);
  return (
    date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    }) +
    ", " +
    date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
  );
};

/** Formats a number or numeric string as ₱1,234,567.89 */
const fmtCurrency = (val: string | number | undefined): string => {
  if (val === undefined || val === null || val === "") return "";
  const num =
    typeof val === "string" ? parseFloat(val.replace(/[^0-9.]/g, "")) : val;
  if (isNaN(num)) return val?.toString() || "";
  return `\u20b1${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

function getAuthHeaders(): Record<string, string> {
  try {
    const saved = sessionStorage.getItem("currentUser");
    if (!saved) return {};
    const parsed = JSON.parse(saved as string);
    return parsed?.token ? { Authorization: `Bearer ${parsed.token}` } : {};
  } catch {
    return {};
  }
}

/** Fetches a blob-proxied file and saves it to disk — works reliably across all browsers */
async function triggerDownload(blobUrl: string): Promise<void> {
  try {
    const proxyUrl = `/api/blob-url?url=${encodeURIComponent(blobUrl)}&dl=1`;
    const res = await fetch(proxyUrl, { headers: getAuthHeaders() });
    if (!res.ok) throw new Error("File not available");
    const blob = await res.blob();
    const disposition = res.headers.get("Content-Disposition") ?? "";
    const nameMatch = disposition.match(/filename="([^"]+)"/);
    const filename = nameMatch ? nameMatch[1] : "document";
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  } catch {
    alert("Download failed. Please try again.");
  }
}

/** Opens a print-to-PDF dialog for virtual (non-stored) content like forms and certificates */
function printHtml(title: string, bodyHtml: string): void {
  const w = window.open("", "_blank", "width=800,height=600");
  if (!w) {
    alert("Please allow popups to download this document.");
    return;
  }
  w.document.write(
    `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title}</title><style>body{font-family:Arial,sans-serif;padding:40px;color:#1a1a1a;max-width:800px;margin:auto}@media print{body{padding:20px}}</style></head><body>${bodyHtml}<script>window.onload=function(){setTimeout(function(){window.print();},500)}<\/script></body></html>`,
  );
  w.document.close();
}

/** Generates printable HTML for the accreditation certificate */
function buildCertHtml(s: Supplier): string {
  const row = (label: string, val: string) =>
    `<tr><td style="padding:8px 16px;border:1px solid #d1fae5;color:#15803d;font-size:12px;width:40%">${label}</td><td style="padding:8px 16px;border:1px solid #d1fae5;font-weight:600;font-size:13px">${val}</td></tr>`;
  return `<div style="text-align:center;border:3px solid #16a34a;padding:48px;border-radius:12px;max-width:680px;margin:auto">
    <div style="font-size:56px">🏆</div>
    <h1 style="color:#166534;font-size:22px;text-transform:uppercase;letter-spacing:3px;margin:12px 0 4px">Accreditation Certificate</h1>
    <p style="color:#15803d;font-size:11px;margin:0">ECPG Compliance Management System</p>
    <hr style="border:1px solid #16a34a;margin:20px 0">
    <p style="color:#15803d;font-size:13px;margin:0 0 6px">This certifies that</p>
    <h2 style="color:#166534;font-size:24px;font-weight:bold;margin:4px 0">${s.supplierName}</h2>
    <p style="color:#374151;font-size:13px;margin:8px 0">has successfully completed the supplier accreditation process<br>and is authorized to transact with ECPG companies</p>
    <hr style="border:1px solid #16a34a;margin:20px 0">
    <table style="width:100%;border-collapse:collapse;text-align:left;margin:auto">
      ${row("Supplier ID", s.id)}
      ${row("Date Enrolled", fmtDate(s.enrollDate))}
      ${row("Date Approved", fmtDate(s.approvalDate ?? ""))}
    </table>
    <p style="color:#15803d;font-size:10px;margin-top:20px">Valid for all transactions with Easycall Communications Philippines, Inc. and Transnational E-Business Solutions Inc.</p>
  </div>`;
}

/** Generates printable HTML for the Supplier Information Form */
function buildFormHtml(s: Supplier): string {
  const g = s.formData?.gen;
  if (!g)
    return `<p style="color:#6b7280;text-align:center;padding:40px">No form data available.</p>`;
  const cell = (v: string) =>
    `<td style="padding:6px 10px;border:1px solid #e5e7eb">${v}</td>`;
  const hrow = (...cols: string[]) =>
    `<tr style="background:#f9fafb">${cols.map((c) => `<th style="padding:6px 10px;border:1px solid #e5e7eb;text-align:left;font-size:11px">${c}</th>`).join("")}</tr>`;
  const row = (label: string, val: string | undefined) =>
    val
      ? `<tr>${cell(`<span style="color:#6b7280">${label}</span>`) + cell(`<strong>${val}</strong>`)}</tr>`
      : "";
  const banks = (
    (s.formData?.banks ?? []) as {
      name: string;
      acctName: string;
      acctNo: string;
    }[]
  ).filter((b) => b.name);
  const sigs = (
    (s.formData?.sigs ?? []) as {
      name: string;
      position: string;
      contact: string;
      email: string;
    }[]
  ).filter((sg) => sg.name);
  const clients = (
    (s.formData?.clients ?? []) as {
      company: string;
      contact: string;
      email: string;
    }[]
  ).filter((cl) => cl.company);
  const section = (title: string, hdr: string, body: string) =>
    body
      ? `<h2 style="font-size:13px;text-transform:uppercase;color:#374151;margin:20px 0 6px">${title}</h2><table style="width:100%;border-collapse:collapse;font-size:12px">${hdr}${body}</table>`
      : "";
  return `<h1 style="color:#1e40af;font-size:18px;margin:0 0 4px">Supplier Information Form</h1>
    <p style="color:#6b7280;font-size:12px;margin:0 0 12px">ID: ${s.id} &nbsp;|&nbsp; Company: ${s.company} &nbsp;|&nbsp; Status: ${s.status}</p>
    <hr style="margin:0 0 12px">
    <h2 style="font-size:13px;text-transform:uppercase;color:#374151;margin:0 0 6px">General Information</h2>
    <table style="width:100%;border-collapse:collapse;font-size:12px">
      ${row("Company Name", g.companyName)}${row("Business Type", g.bizType)}${row("SEC", g.sec)}
      ${row("TIN", g.tin)}${row("Address", g.address)}${row("Telephone", g.tel)}
      ${row("Date Established", fmtDate(g.dateEst))}${row("Years Operating", g.yearsOp)}
      ${row("Capital (₱)", fmtCurrency(g.capital))}${row("Payment Terms", g.payTerms)}
      ${row("Category", g.mainCat)}${row("Sub-Category", g.subCat)}
    </table>
    ${section("Bank References", hrow("Bank", "Account Name", "Account No."), banks.map((b) => `<tr>${cell(b.name) + cell(b.acctName) + cell(b.acctNo)}</tr>`).join(""))}
    ${section("Authorized Signatories", hrow("Name", "Position", "Contact", "Email"), sigs.map((sg) => `<tr>${cell(sg.name) + cell(sg.position) + cell(sg.contact) + cell(sg.email)}</tr>`).join(""))}
    ${section("Client References", hrow("Company", "Contact", "Email"), clients.map((cl) => `<tr>${cell(cl.company) + cell(cl.contact) + cell(cl.email)}</tr>`).join(""))}
    <p style="color:#9ca3af;font-size:10px;margin-top:24px;text-align:center">Generated from ECPG Supplier Accreditation Portal</p>`;
}

// Total Process Target: 10 working days
// - Compliance Officer Review: 5 working days (Days 1-5)
// - Head of Compliance Approval: 5 working days (Days 6-10)
// Color-coded status:
// Green (on-time): 0-7 days - On track
// Yellow (warning): 8-10 days - Approaching deadline
// Red (delayed): 11+ days - Exceeded SLA target

/** Anonymous Gregorian algorithm — returns Easter Sunday as a UTC-midnight Date. */
function easterSunday(year: number): Date {
  const a = year % 19,
    b = Math.floor(year / 100),
    c = year % 100;
  const d = Math.floor(b / 4),
    e = b % 4,
    f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3),
    h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4),
    k = c % 4,
    l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(Date.UTC(year, month - 1, day));
}

/** Returns a Set of Philippine public holiday date strings (YYYY-MM-DD) for the given year.
 *  Covers Regular Holidays, Special Non-Working Holidays, and moveable holidays. */
function getPhHolidays(year: number): Set<string> {
  const ymd = (y: number, m: number, d: number) =>
    new Date(Date.UTC(y, m - 1, d)).toISOString().slice(0, 10);
  const s = new Set<string>();

  // ── Fixed Regular Holidays ────────────────────────────────────────────────
  const regularHolidays = [
    [1, 1], // New Year's Day
    [4, 9], // Araw ng Kagitingan (Day of Valor)
    [5, 1], // Labor Day
    [6, 12], // Independence Day
    [11, 30], // Bonifacio Day
    [12, 25], // Christmas Day
    [12, 30], // Rizal Day
  ];
  for (const [m, d] of regularHolidays) {
    const date = ymd(year, m, d);
    s.add(date);
    const dt = new Date(Date.UTC(year, m - 1, d));
    if (dt.getUTCDay() === 0) {
      const observed = new Date(dt.getTime() + 86400000);
      s.add(observed.toISOString().slice(0, 10));
    }
  }

  // ── Fixed Special Non-Working Holidays ────────────────────────────────────
  s.add(ymd(year, 2, 25)); // EDSA People Power Revolution Anniversary
  s.add(ymd(year, 8, 21)); // Ninoy Aquino Day
  s.add(ymd(year, 11, 1)); // All Saints' Day
  s.add(ymd(year, 12, 8)); // Feast of the Immaculate Conception
  s.add(ymd(year, 12, 24)); // Christmas Eve
  s.add(ymd(year, 12, 31)); // New Year's Eve

  // ── Maundy Thursday & Good Friday (computed from Easter) ─────────────────
  const easter = easterSunday(year);
  s.add(new Date(easter.getTime() - 3 * 86400000).toISOString().slice(0, 10)); // Maundy Thursday
  s.add(new Date(easter.getTime() - 2 * 86400000).toISOString().slice(0, 10)); // Good Friday

  // ── National Heroes Day — last Monday of August ───────────────────────────
  let heroesDay = new Date(Date.UTC(year, 7, 31)); // Start from Aug 31
  while (heroesDay.getUTCDay() !== 1)
    heroesDay = new Date(heroesDay.getTime() - 86400000);
  s.add(heroesDay.toISOString().slice(0, 10));

  // ── Chinese New Year (hardcoded 2024–2030) ────────────────────────────────
  const cny: Record<number, string> = {
    2024: "2024-02-10",
    2025: "2025-01-29",
    2026: "2026-02-17",
    2027: "2027-02-06",
    2028: "2028-01-26",
    2029: "2029-02-13",
    2030: "2030-02-03",
  };
  if (cny[year]) s.add(cny[year]);

  // ── Eid'l Fitr (hardcoded 2024–2030) ─────────────────────────────────────
  const eidFitr: Record<number, string> = {
    2024: "2024-04-10",
    2025: "2025-03-31",
    2026: "2026-03-20",
    2027: "2027-03-10",
    2028: "2028-02-27",
    2029: "2029-02-15",
    2030: "2030-02-05",
  };
  if (eidFitr[year]) s.add(eidFitr[year]);

  // ── Eid'l Adha (hardcoded 2024–2030) ─────────────────────────────────────
  const eidAdha: Record<number, string> = {
    2024: "2024-06-17",
    2025: "2025-06-07",
    2026: "2026-05-27",
    2027: "2027-05-17",
    2028: "2028-05-05",
    2029: "2029-04-25",
    2030: "2030-04-14",
  };
  if (eidAdha[year]) s.add(eidAdha[year]);

  return s;
}

/** Counts Mon–Fri working days from startDate up to endDate (defaults to today),
 *  excluding Philippine public holidays. */
function countWorkingDays(
  startDate: string | undefined,
  endDate?: string,
): number {
  if (!startDate) return 0;
  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : new Date(TODAY);
  if (isNaN(start.getTime())) return 0;
  let count = 0;
  const cur = new Date(start.getTime());
  const holidayCache: Record<number, Set<string>> = {};
  while (cur <= end) {
    const dow = cur.getUTCDay();
    if (dow !== 0 && dow !== 6) {
      const yr = cur.getUTCFullYear();
      if (!holidayCache[yr]) holidayCache[yr] = getPhHolidays(yr);
      if (!holidayCache[yr].has(cur.toISOString().slice(0, 10))) count++;
    }
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
  return count;
}

/** Returns the live working-day count for a supplier. Uses reviewStartDate if set, else stored daysInProcess. */
function liveDays(s: {
  reviewStartDate?: string;
  daysInProcess: number;
}): number {
  return s.reviewStartDate
    ? countWorkingDays(s.reviewStartDate)
    : s.daysInProcess;
}

function calculateSLA(daysInProcess: number): SlaStatus {
  if (daysInProcess >= 11) return "delayed"; // Exceeded 10-day SLA target
  if (daysInProcess >= 8) return "warning"; // Approaching deadline (days 8-10)
  return "on-time"; // On track (days 0-7)
}

// Stage-specific SLA for any 5-day review window (pass stage-relative days)
// CO stage: stageDays = countWorkingDays(reviewStartDate)
// CH stage: stageDays = countWorkingDays(chStartDate)  -- clock starts when CO passes forward
function calculateStageSLA(stageDays: number): SlaStatus {
  if (stageDays > 5) return "delayed"; // Exceeded 5-day stage allocation
  if (stageDays >= 4) return "warning"; // Days 4-5: last stretch
  return "on-time"; // Days 1-3: on track
}

/** Returns the stage-aware SLA status for a supplier based on their current review phase. */
function stageSlaOf(s: {
  status: string;
  chStartDate?: string | null;
  reviewStartDate?: string | null;
  daysInProcess: number;
}): SlaStatus {
  if (
    ["Under Review", "For Compliance", "Returned with Remarks"].includes(
      s.status,
    )
  )
    return calculateStageSLA(liveDays(s));
  if (
    ["Recommended for Approval", "Modification Pre-Approved"].includes(s.status)
  )
    return calculateStageSLA(
      s.chStartDate
        ? countWorkingDays(s.chStartDate)
        : Math.max(0, liveDays(s) - 5),
    );
  return calculateSLA(liveDays(s));
}

function Badge({ status }: { status: SupplierStatus }) {
  const m = STATUS_META[status] || { color: "bg-gray-100 text-gray-700" };
  return (
    <span
      className={`px-2 py-0.5 rounded-full text-xs font-semibold ${m.color}`}
    >
      {getStatusLabel(status)}
    </span>
  );
}
function SlaBadge({ sla }: { sla: SlaStatus }) {
  const s = SLA[sla] || SLA["on-time"];
  return (
    <span
      className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${s.cls}`}
    >
      {s.icon} {s.label}
    </span>
  );
}
function Toast({ notes }: { notes: string[] }) {
  if (!notes.length) return null;
  return (
    <div className="fixed top-4 right-4 z-50 space-y-2 max-w-md">
      {notes.map((n: string, i: number) => {
        // Classify by content — checked in priority order (error > warning > success > info)
        const lower = n.toLowerCase();

        const isError =
          lower.includes("failed") ||
          lower.includes("error") ||
          lower.includes("invalid") ||
          lower.includes("unable") ||
          lower.includes("could not");

        const isWarning =
          !isError &&
          (lower.includes("returned with remarks") ||
            lower.includes("for compliance") ||
            lower.includes("disapproved") ||
            lower.includes("rejected") ||
            lower.includes("denied") ||
            lower.includes("resubmission disapproved"));

        const isSuccess =
          !isError &&
          !isWarning &&
          (lower.includes("successfully") ||
            lower.includes("approved") ||
            lower.includes("saved") ||
            lower.includes("uploaded") ||
            lower.includes("enrolled") ||
            lower.includes("submitted") ||
            lower.includes("recommended") ||
            lower.includes("endorsed") ||
            lower.includes("welcome back") ||
            lower.includes("signed out") ||
            lower.includes("password changed") ||
            lower.includes("step "));

        const { bg, border, text, icon } = isError
          ? {
              bg: "bg-red-50",
              border: "border-red-500",
              text: "text-red-900",
              icon: "✕",
            }
          : isWarning
            ? {
                bg: "bg-amber-50",
                border: "border-amber-500",
                text: "text-amber-900",
                icon: "⚠",
              }
            : isSuccess
              ? {
                  bg: "bg-green-50",
                  border: "border-green-500",
                  text: "text-green-900",
                  icon: "✓",
                }
              : {
                  bg: "bg-blue-50",
                  border: "border-blue-500",
                  text: "text-blue-900",
                  icon: "ℹ",
                };

        return (
          <div
            key={i}
            className={`${bg} border-l-4 ${border} shadow-lg rounded-lg p-4 text-xs flex gap-3 items-start`}
          >
            <span
              className={`font-bold text-sm leading-none mt-0.5 flex-shrink-0 ${text}`}
            >
              {icon}
            </span>
            <span className={`whitespace-pre-line leading-relaxed ${text}`}>
              {n}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ── Pagination ───────────────────────────────────────────────────────────────
const DASH_PAGE_SIZE = 10;
function Pager({
  page,
  total,
  count,
  onChange,
}: {
  page: number;
  total: number;
  count?: number;
  onChange: (p: number) => void;
}) {
  if (total <= 1) return null;
  const from = (page - 1) * DASH_PAGE_SIZE + 1;
  const to =
    count != null
      ? Math.min(page * DASH_PAGE_SIZE, count)
      : page * DASH_PAGE_SIZE;
  const btnCls =
    "px-3 py-1.5 rounded border border-gray-300 disabled:opacity-30 hover:bg-gray-200 active:bg-gray-300 transition text-lg leading-none font-semibold";
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50 text-sm text-gray-500">
      <span className="text-xs">
        {count != null ? (
          <>
            Showing <strong>{from}</strong>–<strong>{to}</strong> of{" "}
            <strong>{count}</strong> entries
          </>
        ) : (
          <>
            Page <strong>{page}</strong> of <strong>{total}</strong>
          </>
        )}
      </span>
      <div className="flex gap-1.5">
        <button
          disabled={page === 1}
          onClick={() => onChange(1)}
          className={btnCls}
        >
          «
        </button>
        <button
          disabled={page === 1}
          onClick={() => onChange(page - 1)}
          className={btnCls}
        >
          ‹
        </button>
        <button
          disabled={page === total}
          onClick={() => onChange(page + 1)}
          className={btnCls}
        >
          ›
        </button>
        <button
          disabled={page === total}
          onClick={() => onChange(total)}
          className={btnCls}
        >
          »
        </button>
      </div>
    </div>
  );
}

// ── Empty Dashboard State ─────────────────────────────────────────────────────
function EmptyDashboard() {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center text-gray-400 space-y-4">
      <h3 className="text-xl font-semibold text-gray-500">
        No suppliers enrolled yet
      </h3>
    </div>
  );
}

// ── Dashboard ─────────────────────────────────────────────────────────────────
function Dashboard({
  suppliers,
  onSelectSupplier,
  onNav,
  role,
  loading,
}: {
  suppliers: Supplier[];
  onSelectSupplier: (id: string) => void;
  onNav: (view: string) => void;
  role: Role;
  loading?: boolean;
}) {
  const [dashSearch, setDashSearch] = useState("");
  const [dashStatusFilter, setDashStatusFilter] = useState("All");
  const [dashCompanyFilter, setDashCompanyFilter] = useState("All");
  const [dashSlaFilter, setDashSlaFilter] = useState("All");
  // Pagination — keyed by table name
  const [pages, setPages] = useState<Record<string, number>>({});
  const pg = (k: string): number => pages[k] || 1;
  const setPg = (k: string, n: number) =>
    setPages((prev) => ({ ...prev, [k]: n }));
  const pagedSlice = <T,>(arr: T[], k: string): T[] =>
    arr.slice((pg(k) - 1) * DASH_PAGE_SIZE, pg(k) * DASH_PAGE_SIZE);
  const tpages = (arr: unknown[]): number =>
    Math.max(1, Math.ceil(arr.length / DASH_PAGE_SIZE));
  // Reset All Suppliers page on filter change
  useEffect(() => {
    setPages((prev) => ({ ...prev, all: 1 }));
  }, [dashSearch, dashStatusFilter, dashCompanyFilter, dashSlaFilter]); // eslint-disable-line

  const total = suppliers.length;
  if (loading)
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center text-gray-400 space-y-4">
        <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
        <p className="text-sm text-gray-400">Loading suppliers...</p>
      </div>
    );
  if (total === 0) return <EmptyDashboard />;

  const dashFiltered = suppliers.filter((s: Supplier) => {
    const q = dashSearch.toLowerCase();
    const matchText =
      !q ||
      s.id.toLowerCase().includes(q) ||
      s.supplierName.toLowerCase().includes(q) ||
      s.rep.toLowerCase().includes(q) ||
      s.email.toLowerCase().includes(q);
    const matchStatus =
      dashStatusFilter === "All" || s.status === dashStatusFilter;
    const matchCompany =
      dashCompanyFilter === "All" || s.company === dashCompanyFilter;
    const matchSla = dashSlaFilter === "All" || stageSlaOf(s) === dashSlaFilter;
    return matchText && matchStatus && matchCompany && matchSla;
  });

  const approved = suppliers.filter(
    (s: Supplier) => s.status === "Approved",
  ).length;
  const underReview = suppliers.filter((s: Supplier) =>
    ["Under Review", "For Compliance"].includes(s.status),
  ).length;
  const delayed = suppliers.filter(
    (s: Supplier) => stageSlaOf(s) === "delayed",
  ).length;
  const warning = suppliers.filter(
    (s: Supplier) => stageSlaOf(s) === "warning",
  ).length;
  // avgDays only counts active/in-progress suppliers (excludes pre-submission and terminal statuses)
  const activeSuppliers = suppliers.filter(
    (s: Supplier) =>
      !["Enrolled", "Form Completion", "Approved", "Disapproved"].includes(
        s.status,
      ),
  );
  const avgDays = activeSuppliers.length
    ? Math.round(
        activeSuppliers.reduce((a: number, s: Supplier) => a + liveDays(s), 0) /
          activeSuppliers.length,
      )
    : 0;
  const byStatus = suppliers.reduce(
    (a: { [key: string]: number }, s: Supplier) => {
      a[s.status] = (a[s.status] || 0) + 1;
      return a;
    },
    {},
  );
  const byCompany = COMPANIES.map((c: Company) => ({
    name: c,
    count: suppliers.filter((s: Supplier) => s.company === c).length,
  }));

  // Role-specific queues
  const enrolledQueue = suppliers.filter((s: Supplier) =>
    ["Enrolled", "Form Completion"].includes(s.status),
  );
  const pendingEndorse = suppliers.filter(
    (s: Supplier) => s.status === "Modification Endorsement Pending",
  ); // RD dashboard: awaiting RD endorsement
  const coModQueue = suppliers.filter(
    (s: Supplier) => s.status === "Modification Requested",
  ); // CO dashboard: endorsed, ready for CO pre-approval
  const reviewQueue = suppliers.filter(
    (s: Supplier) => s.status === "Under Review",
  );
  const regularReviewQueue = reviewQueue.filter(
    (s: Supplier) => !s.formData?.isModificationResubmission,
  );
  const modReviewQueue = reviewQueue.filter(
    (s: Supplier) => !!s.formData?.isModificationResubmission,
  );
  const complianceQueue = suppliers.filter(
    (s: Supplier) => s.status === "For Compliance",
  );
  const returnedQueue = suppliers.filter(
    (s: Supplier) => s.status === "Returned with Remarks",
  );
  const approvalQueue = suppliers.filter(
    (s: Supplier) => s.status === "Recommended for Approval",
  );
  const regularApprovalQueue = approvalQueue.filter(
    (s: Supplier) => !s.formData?.isModificationResubmission,
  );
  const modApprovalQueue = approvalQueue.filter(
    (s: Supplier) => !!s.formData?.isModificationResubmission,
  );
  const modPreQueue = suppliers.filter(
    (s: Supplier) => s.status === "Modification Pre-Approved",
  );
  const disapprovedList = suppliers.filter(
    (s: Supplier) => s.status === "Disapproved",
  );

  const metrics = [
    {
      label: "Total Suppliers",
      val: total,
      color: "from-blue-600 to-blue-700",
      sub: "All registered",
    },
    {
      label: "Approved",
      val: approved,
      color: "from-green-600 to-green-700",
      sub: `${total ? Math.round((approved / total) * 100) : 0}% of total`,
    },
    {
      label: "Under Compliance Review",
      val: underReview,
      color: "from-orange-500 to-orange-600",
      sub: "Compliance officer stage",
    },
    {
      label: "SLA Delayed",
      val: delayed,
      color: "from-red-600 to-red-700",
      sub: "Exceeded stage limit",
    },
    {
      label: "SLA Warning",
      val: warning,
      color: "from-yellow-500 to-yellow-600",
      sub: "Approaching stage deadline",
    },
    {
      label: "Avg. Processing",
      val: `${avgDays}d`,
      color: "from-purple-600 to-purple-700",
      sub: "Working days",
    },
  ];

  // ── Role-specific mini table helper ──────────────────────────────────────────
  const QueueRow = ({
    s,
    label,
    labelColor,
    stageLimit,
    stageOffset = 0,
    stageStart,
  }: {
    s: Supplier;
    label?: string;
    labelColor?: string;
    stageLimit?: number;
    stageOffset?: number;
    stageStart?: string;
  }) => {
    const totalDays = liveDays(s);
    const stageDays = stageStart
      ? countWorkingDays(stageStart)
      : Math.max(0, totalDays - stageOffset);
    const stageSla: SlaStatus =
      stageLimit != null
        ? calculateStageSLA(stageDays)
        : calculateSLA(totalDays);
    const daysLeft: number | null =
      stageLimit != null ? stageLimit - stageDays : null;
    const daysLeftColor =
      daysLeft == null
        ? ""
        : daysLeft < 0
          ? "text-red-600 font-bold"
          : daysLeft <= 1
            ? "text-yellow-600 font-semibold"
            : "text-gray-500";
    const displayLabel = label ?? s.status;
    const displayLabelColor =
      labelColor ?? STATUS_META[s.status]?.color ?? "bg-gray-100 text-gray-700";
    return (
      <tr
        onClick={() => {
          onSelectSupplier(s.id);
          onNav("review");
        }}
        className="hover:bg-blue-50 cursor-pointer transition-colors"
      >
        <td className="px-3 py-2 font-mono text-xs font-semibold text-blue-700">
          {s.id}
        </td>
        <td className="px-3 py-2 font-medium text-gray-800 text-sm">
          {s.supplierName}
        </td>
        <td className="px-3 py-2">
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-semibold ${displayLabelColor}`}
          >
            {displayLabel}
          </span>
        </td>
        <td className="px-3 py-2">
          <SlaBadge sla={stageSla} />
        </td>
        <td className="px-3 py-2 text-xs">
          {stageLimit != null ? (
            <>
              <span
                className={
                  stageSla === "delayed"
                    ? "text-red-600 font-bold"
                    : stageSla === "warning"
                      ? "text-yellow-600 font-semibold"
                      : "text-gray-700 font-medium"
                }
              >
                {stageDays}
              </span>
              <span className="text-gray-400"> / {stageLimit}d</span>
            </>
          ) : (
            <span className="text-gray-400">{totalDays}d</span>
          )}
        </td>
        {stageLimit != null && (
          <td className="px-3 py-2 text-xs">
            <span className={daysLeftColor}>
              {daysLeft! < 0
                ? `${Math.abs(daysLeft!)}d over`
                : daysLeft === 0
                  ? "Due today"
                  : `${daysLeft}d left`}
            </span>
          </td>
        )}
      </tr>
    );
  };

  return (
    <div className="space-y-6">
      {/* ── Requesting Department Dashboard ───────────────────────────────── */}
      {role === "Requesting Department" && (
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-xl p-5 flex items-center justify-between">
            <div>
              <h2 className="font-bold text-lg">
                Requesting Department Dashboard
              </h2>
              <p className="text-xs opacity-80 mt-0.5">
                Enroll suppliers and track your submissions through the
                accreditation pipeline
              </p>
            </div>
            <button
              onClick={() => onNav("enroll")}
              className="bg-white text-blue-700 font-semibold text-sm px-4 py-2.5 rounded-lg hover:bg-blue-50 transition shrink-0"
            >
              + Enroll New Supplier
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              {
                label: "Active Pipeline",
                val: total - approved - disapprovedList.length,
                color: "bg-blue-50 text-blue-800 border-blue-200",
              },
              {
                label: "Awaiting Submission",
                val: enrolledQueue.length,
                color: "bg-yellow-50 text-yellow-800 border-yellow-200",
              },
              {
                label: "Endorsements Needed",
                val: pendingEndorse.length,
                color:
                  pendingEndorse.length > 0
                    ? "bg-red-50 text-red-800 border-red-200"
                    : "bg-gray-50 text-gray-600 border-gray-200",
              },
              {
                label: "Approved Total",
                val: approved,
                color: "bg-green-50 text-green-800 border-green-200",
              },
            ].map((m) => (
              <div key={m.label} className={`rounded-xl border p-4 ${m.color}`}>
                <div className="text-2xl font-bold">{m.val}</div>
                <div className="text-xs font-medium mt-1">{m.label}</div>
              </div>
            ))}
          </div>

          {pendingEndorse.length > 0 && (
            <div className="bg-white rounded-xl shadow border overflow-hidden">
              <div className="px-4 py-3 bg-red-50 border-b border-red-200 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
                <h3 className="font-bold text-red-800 text-sm">
                  Action Required — Modification Endorsements (
                  {pendingEndorse.length})
                </h3>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b text-xs text-gray-500 uppercase">
                  <tr>
                    <th className="px-3 py-2 text-left">ID</th>
                    <th className="px-3 py-2 text-left">Supplier</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-left">Stage SLA</th>
                    <th className="px-3 py-2 text-left">Days (of 5)</th>
                    <th className="px-3 py-2 text-left">Days Left</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {pagedSlice(pendingEndorse, "pendingEndorse").map((s) => (
                    <QueueRow
                      key={s.id}
                      s={s}
                      label={s.status}
                      labelColor={STATUS_META[s.status]?.color}
                      stageLimit={5}
                      stageStart={s.reviewStartDate ?? undefined}
                    />
                  ))}
                </tbody>
              </table>
              <Pager
                page={pg("pendingEndorse")}
                total={tpages(pendingEndorse)}
                count={pendingEndorse.length}
                onChange={(n) => setPg("pendingEndorse", n)}
              />
            </div>
          )}

          {enrolledQueue.length > 0 && (
            <div className="bg-white rounded-xl shadow border overflow-hidden">
              <div className="px-4 py-3 bg-yellow-50 border-b border-yellow-200">
                <h3 className="font-bold text-yellow-800 text-sm">
                  Awaiting Supplier Submission ({enrolledQueue.length})
                </h3>
                <p className="text-xs text-yellow-700 mt-0.5">
                  These suppliers have been enrolled but have not yet completed
                  their application form.
                </p>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b text-xs text-gray-500 uppercase">
                  <tr>
                    <th className="px-3 py-2 text-left">ID</th>
                    <th className="px-3 py-2 text-left">Supplier</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-left">SLA</th>
                    <th className="px-3 py-2 text-left">Days</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {pagedSlice(enrolledQueue, "enrolledQueue").map((s) => (
                    <QueueRow
                      key={s.id}
                      s={s}
                      label={s.status}
                      labelColor={STATUS_META[s.status]?.color}
                    />
                  ))}
                </tbody>
              </table>
              <Pager
                page={pg("enrolledQueue")}
                total={tpages(enrolledQueue)}
                count={enrolledQueue.length}
                onChange={(n) => setPg("enrolledQueue", n)}
              />
            </div>
          )}
        </div>
      )}

      {/* ── Compliance Officer Dashboard ───────────────────────────────────── */}
      {role === "Compliance Officer" && (
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-orange-500 to-amber-600 text-white rounded-xl p-5">
            <h2 className="font-bold text-lg">Compliance Officer Dashboard</h2>
            <p className="text-xs opacity-80 mt-0.5">
              Review submitted applications and manage compliance requirements
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              {
                label: "Pending Review",
                val: reviewQueue.length,
                color:
                  reviewQueue.length > 0
                    ? "bg-orange-50 text-orange-800 border-orange-200"
                    : "bg-gray-50 text-gray-600 border-gray-200",
              },
              {
                label: "Returned by CH",
                val: returnedQueue.length,
                color:
                  returnedQueue.length > 0
                    ? "bg-rose-50 text-rose-800 border-rose-200"
                    : "bg-gray-50 text-gray-600 border-gray-200",
              },
              {
                label: "For Compliance",
                val: complianceQueue.length,
                color:
                  complianceQueue.length > 0
                    ? "bg-yellow-50 text-yellow-800 border-yellow-200"
                    : "bg-gray-50 text-gray-600 border-gray-200",
              },
              {
                label: "Mod. Requests",
                val: coModQueue.length,
                color:
                  coModQueue.length > 0
                    ? "bg-indigo-50 text-indigo-800 border-indigo-200"
                    : "bg-gray-50 text-gray-600 border-gray-200",
              },
            ].map((m) => (
              <div key={m.label} className={`rounded-xl border p-4 ${m.color}`}>
                <div className="text-2xl font-bold">{m.val}</div>
                <div className="text-xs font-medium mt-1">{m.label}</div>
              </div>
            ))}
          </div>

          {returnedQueue.length > 0 && (
            <div className="bg-white rounded-xl shadow border overflow-hidden">
              <div className="px-4 py-3 bg-rose-50 border-b border-rose-200 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-rose-500 animate-pulse"></div>
                <h3 className="font-bold text-rose-800 text-sm">
                  Returned by Compliance Head — Action Required (
                  {returnedQueue.length})
                </h3>
              </div>
              <p className="text-xs text-rose-700 px-4 py-2 bg-rose-50 border-b border-rose-100">
                These applications were returned by the Compliance Head with
                remarks. Review the remarks and decide next action.
              </p>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b text-xs text-gray-500 uppercase">
                  <tr>
                    <th className="px-3 py-2 text-left">ID</th>
                    <th className="px-3 py-2 text-left">Supplier</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-left">Stage SLA</th>
                    <th className="px-3 py-2 text-left">Days (of 5)</th>
                    <th className="px-3 py-2 text-left">Days Left</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {pagedSlice(returnedQueue, "returnedQueue").map((s) => (
                    <QueueRow
                      key={s.id}
                      s={s}
                      label={s.status}
                      labelColor={STATUS_META[s.status]?.color}
                      stageLimit={5}
                    />
                  ))}
                </tbody>
              </table>
              <Pager
                page={pg("returnedQueue")}
                total={tpages(returnedQueue)}
                count={returnedQueue.length}
                onChange={(n) => setPg("returnedQueue", n)}
              />
            </div>
          )}

          {regularReviewQueue.length > 0 && (
            <div className="bg-white rounded-xl shadow border overflow-hidden">
              <div className="px-4 py-3 bg-orange-50 border-b border-orange-200 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-orange-500 animate-pulse"></div>
                <h3 className="font-bold text-orange-800 text-sm">
                  Accreditation Review Queue ({regularReviewQueue.length})
                </h3>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b text-xs text-gray-500 uppercase">
                  <tr>
                    <th className="px-3 py-2 text-left">ID</th>
                    <th className="px-3 py-2 text-left">Supplier</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-left">Stage SLA</th>
                    <th className="px-3 py-2 text-left">Days (of 5)</th>
                    <th className="px-3 py-2 text-left">Days Left</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {pagedSlice(regularReviewQueue, "regularReviewQueue").map(
                    (s) => (
                      <QueueRow
                        key={s.id}
                        s={s}
                        label={s.status}
                        labelColor={STATUS_META[s.status]?.color}
                        stageLimit={5}
                      />
                    ),
                  )}
                </tbody>
              </table>
              <Pager
                page={pg("regularReviewQueue")}
                total={tpages(regularReviewQueue)}
                count={regularReviewQueue.length}
                onChange={(n) => setPg("regularReviewQueue", n)}
              />
            </div>
          )}

          {modReviewQueue.length > 0 && (
            <div className="bg-white rounded-xl shadow border overflow-hidden">
              <div className="px-4 py-3 bg-purple-50 border-b border-purple-200 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-purple-500 animate-pulse"></div>
                <h3 className="font-bold text-purple-800 text-sm">
                  Modification Update Review Queue ({modReviewQueue.length})
                </h3>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b text-xs text-gray-500 uppercase">
                  <tr>
                    <th className="px-3 py-2 text-left">ID</th>
                    <th className="px-3 py-2 text-left">Supplier</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-left">Stage SLA</th>
                    <th className="px-3 py-2 text-left">Days (of 5)</th>
                    <th className="px-3 py-2 text-left">Days Left</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {pagedSlice(modReviewQueue, "modReviewQueue").map((s) => (
                    <QueueRow
                      key={s.id}
                      s={s}
                      label={s.status}
                      labelColor={STATUS_META[s.status]?.color}
                      stageLimit={5}
                    />
                  ))}
                </tbody>
              </table>
              <Pager
                page={pg("modReviewQueue")}
                total={tpages(modReviewQueue)}
                count={modReviewQueue.length}
                onChange={(n) => setPg("modReviewQueue", n)}
              />
            </div>
          )}

          {complianceQueue.length > 0 && (
            <div className="bg-white rounded-xl shadow border overflow-hidden">
              <div className="px-4 py-3 bg-yellow-50 border-b border-yellow-200">
                <h3 className="font-bold text-yellow-800 text-sm">
                  Awaiting Compliance Resubmission ({complianceQueue.length})
                </h3>
                <p className="text-xs text-yellow-700 mt-0.5">
                  Suppliers have been notified of compliance requirements and
                  are expected to resubmit.
                </p>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b text-xs text-gray-500 uppercase">
                  <tr>
                    <th className="px-3 py-2 text-left">ID</th>
                    <th className="px-3 py-2 text-left">Supplier</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-left">Stage SLA</th>
                    <th className="px-3 py-2 text-left">Days (of 5)</th>
                    <th className="px-3 py-2 text-left">Days Left</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {pagedSlice(complianceQueue, "complianceQueue").map((s) => (
                    <QueueRow
                      key={s.id}
                      s={s}
                      label={s.status}
                      labelColor={STATUS_META[s.status]?.color}
                      stageLimit={5}
                    />
                  ))}
                </tbody>
              </table>
              <Pager
                page={pg("complianceQueue")}
                total={tpages(complianceQueue)}
                count={complianceQueue.length}
                onChange={(n) => setPg("complianceQueue", n)}
              />
            </div>
          )}

          {coModQueue.length > 0 && (
            <div className="bg-white rounded-xl shadow border overflow-hidden">
              <div className="px-4 py-3 bg-indigo-50 border-b border-indigo-200 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse"></div>
                <h3 className="font-bold text-indigo-800 text-sm">
                  Endorsed Modification Requests — Awaiting Pre-Approval (
                  {coModQueue.length})
                </h3>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b text-xs text-gray-500 uppercase">
                  <tr>
                    <th className="px-3 py-2 text-left">ID</th>
                    <th className="px-3 py-2 text-left">Supplier</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-left">Stage SLA</th>
                    <th className="px-3 py-2 text-left">Days (of 5)</th>
                    <th className="px-3 py-2 text-left">Days Left</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {pagedSlice(coModQueue, "coModQueue").map((s) => (
                    <QueueRow
                      key={s.id}
                      s={s}
                      label={s.status}
                      labelColor={STATUS_META[s.status]?.color}
                      stageLimit={5}
                      stageStart={s.reviewStartDate ?? undefined}
                    />
                  ))}
                </tbody>
              </table>
              <Pager
                page={pg("coModQueue")}
                total={tpages(coModQueue)}
                count={coModQueue.length}
                onChange={(n) => setPg("coModQueue", n)}
              />
            </div>
          )}

          {regularReviewQueue.length === 0 &&
            modReviewQueue.length === 0 &&
            returnedQueue.length === 0 &&
            complianceQueue.length === 0 &&
            coModQueue.length === 0 && (
              <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-5 text-center">
                <p className="font-semibold text-green-800 text-sm">
                  Your review queue is clear
                </p>
                <p className="text-green-600 text-xs mt-1">
                  No applications are currently awaiting your review.
                </p>
              </div>
            )}
        </div>
      )}

      {/* ── Compliance Head Dashboard ──────────────────────────────────────── */}
      {role === "Compliance Head" && (
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-purple-600 to-violet-700 text-white rounded-xl p-5">
            <h2 className="font-bold text-lg">Compliance Head Dashboard</h2>
            <p className="text-xs opacity-80 mt-0.5">
              Grant final accreditation approvals and oversee compliance quality
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              {
                label: "Pending Final Approval",
                val: approvalQueue.length,
                color:
                  approvalQueue.length > 0
                    ? "bg-cyan-50 text-cyan-800 border-cyan-200"
                    : "bg-gray-50 text-gray-600 border-gray-200",
              },
              {
                label: "Mod. Final Approval",
                val: modPreQueue.length,
                color:
                  modPreQueue.length > 0
                    ? "bg-indigo-50 text-indigo-800 border-indigo-200"
                    : "bg-gray-50 text-gray-600 border-gray-200",
              },
              {
                label: "Total Approved",
                val: approved,
                color: "bg-green-50 text-green-800 border-green-200",
              },
              {
                label: "Stage SLA Delayed",
                val: [...approvalQueue, ...modPreQueue].filter(
                  (s: Supplier) =>
                    (s.chStartDate
                      ? countWorkingDays(s.chStartDate)
                      : Math.max(0, liveDays(s) - 5)) > 5,
                ).length,
                color:
                  [...approvalQueue, ...modPreQueue].filter(
                    (s: Supplier) =>
                      (s.chStartDate
                        ? countWorkingDays(s.chStartDate)
                        : Math.max(0, liveDays(s) - 5)) > 5,
                  ).length > 0
                    ? "bg-red-50 text-red-800 border-red-200"
                    : "bg-gray-50 text-gray-600 border-gray-200",
              },
            ].map((m) => (
              <div key={m.label} className={`rounded-xl border p-4 ${m.color}`}>
                <div className="text-2xl font-bold">{m.val}</div>
                <div className="text-xs font-medium mt-1">{m.label}</div>
              </div>
            ))}
          </div>

          {regularApprovalQueue.length > 0 && (
            <div className="bg-white rounded-xl shadow border overflow-hidden">
              <div className="px-4 py-3 bg-cyan-50 border-b border-cyan-200 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-cyan-500 animate-pulse"></div>
                <h3 className="font-bold text-cyan-800 text-sm">
                  Pending Final Certification ({regularApprovalQueue.length})
                </h3>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b text-xs text-gray-500 uppercase">
                  <tr>
                    <th className="px-3 py-2 text-left">ID</th>
                    <th className="px-3 py-2 text-left">Supplier</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-left">Stage SLA</th>
                    <th className="px-3 py-2 text-left">Days (of 5)</th>
                    <th className="px-3 py-2 text-left">Days Left</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {pagedSlice(regularApprovalQueue, "regularApprovalQueue").map(
                    (s) => (
                      <QueueRow
                        key={s.id}
                        s={s}
                        label={s.status}
                        labelColor={STATUS_META[s.status]?.color}
                        stageLimit={5}
                        stageOffset={5}
                        stageStart={s.chStartDate ?? undefined}
                      />
                    ),
                  )}
                </tbody>
              </table>
              <Pager
                page={pg("regularApprovalQueue")}
                total={tpages(regularApprovalQueue)}
                count={regularApprovalQueue.length}
                onChange={(n) => setPg("regularApprovalQueue", n)}
              />
            </div>
          )}

          {modApprovalQueue.length > 0 && (
            <div className="bg-white rounded-xl shadow border overflow-hidden">
              <div className="px-4 py-3 bg-purple-50 border-b border-purple-200 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-purple-500 animate-pulse"></div>
                <h3 className="font-bold text-purple-800 text-sm">
                  Pending Modification Approval ({modApprovalQueue.length})
                </h3>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b text-xs text-gray-500 uppercase">
                  <tr>
                    <th className="px-3 py-2 text-left">ID</th>
                    <th className="px-3 py-2 text-left">Supplier</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-left">Stage SLA</th>
                    <th className="px-3 py-2 text-left">Days (of 5)</th>
                    <th className="px-3 py-2 text-left">Days Left</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {pagedSlice(modApprovalQueue, "modApprovalQueue").map((s) => (
                    <QueueRow
                      key={s.id}
                      s={s}
                      label={s.status}
                      labelColor={STATUS_META[s.status]?.color}
                      stageLimit={5}
                      stageOffset={5}
                      stageStart={s.chStartDate ?? undefined}
                    />
                  ))}
                </tbody>
              </table>
              <Pager
                page={pg("modApprovalQueue")}
                total={tpages(modApprovalQueue)}
                count={modApprovalQueue.length}
                onChange={(n) => setPg("modApprovalQueue", n)}
              />
            </div>
          )}

          {modPreQueue.length > 0 && (
            <div className="bg-white rounded-xl shadow border overflow-hidden">
              <div className="px-4 py-3 bg-indigo-50 border-b border-indigo-200">
                <h3 className="font-bold text-indigo-800 text-sm">
                  Modification Approvals Pending ({modPreQueue.length})
                </h3>
                <p className="text-xs text-indigo-700 mt-0.5">
                  Pre-approved by Compliance Officer — awaiting your final
                  authorization.
                </p>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b text-xs text-gray-500 uppercase">
                  <tr>
                    <th className="px-3 py-2 text-left">ID</th>
                    <th className="px-3 py-2 text-left">Supplier</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-left">Stage SLA</th>
                    <th className="px-3 py-2 text-left">Days (of 5)</th>
                    <th className="px-3 py-2 text-left">Days Left</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {pagedSlice(modPreQueue, "modPreQueue").map((s) => (
                    <QueueRow
                      key={s.id}
                      s={s}
                      label={s.status}
                      labelColor={STATUS_META[s.status]?.color}
                      stageLimit={5}
                      stageOffset={5}
                      stageStart={s.chStartDate ?? undefined}
                    />
                  ))}
                </tbody>
              </table>
              <Pager
                page={pg("modPreQueue")}
                total={tpages(modPreQueue)}
                count={modPreQueue.length}
                onChange={(n) => setPg("modPreQueue", n)}
              />
            </div>
          )}

          {regularApprovalQueue.length === 0 &&
            modApprovalQueue.length === 0 &&
            modPreQueue.length === 0 && (
              <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-5 text-center">
                <p className="font-semibold text-green-800 text-sm">
                  No pending approvals
                </p>
                <p className="text-green-600 text-xs mt-1">
                  All recommended applications have been actioned.
                </p>
              </div>
            )}
        </div>
      )}

      {/* ── General Manager Dashboard ──────────────────────────────────────── */}
      {role === "General Manager" && (
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-emerald-600 to-teal-700 text-white rounded-xl p-5">
            <h2 className="font-bold text-lg">
              General Manager — Executive Overview
            </h2>
            <p className="text-xs opacity-80 mt-0.5">
              System-wide accreditation status and compliance performance at a
              glance
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              {
                label: "Total Suppliers",
                val: total,
                color: "bg-blue-50 text-blue-800 border-blue-200",
              },
              {
                label: "Approval Rate",
                val: `${total ? Math.round((approved / total) * 100) : 0}%`,
                color: "bg-green-50 text-green-800 border-green-200",
              },
              {
                label: "Avg. Cycle Time",
                val: `${avgDays} days`,
                color: "bg-purple-50 text-purple-800 border-purple-200",
              },
              {
                label: "SLA Exceptions",
                val: delayed + warning,
                color:
                  delayed + warning > 0
                    ? "bg-red-50 text-red-800 border-red-200"
                    : "bg-gray-50 text-gray-600 border-gray-200",
              },
            ].map((m) => (
              <div key={m.label} className={`rounded-xl border p-4 ${m.color}`}>
                <div className="text-2xl font-bold">{m.val}</div>
                <div className="text-xs font-medium mt-1">{m.label}</div>
              </div>
            ))}
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl shadow border p-4">
              <h3 className="font-bold text-gray-700 text-sm uppercase tracking-wide mb-3">
                Pipeline at a Glance
              </h3>
              <div className="space-y-2 text-sm">
                {[
                  {
                    label: "Awaiting Supplier Submission",
                    count: enrolledQueue.length,
                    color: "bg-yellow-400",
                  },
                  {
                    label: "Under Compliance Review",
                    count: reviewQueue.length + complianceQueue.length,
                    color: "bg-orange-400",
                  },
                  {
                    label: "Pending Final Approval",
                    count: approvalQueue.length,
                    color: "bg-cyan-400",
                  },
                  {
                    label: "Approved & Accredited",
                    count: approved,
                    color: "bg-green-400",
                  },
                  {
                    label: "Disapproved",
                    count: disapprovedList.length,
                    color: "bg-red-400",
                  },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-3">
                    <div
                      className={`w-2 h-2 rounded-full ${item.color} shrink-0`}
                    ></div>
                    <span className="flex-1 text-gray-600 text-xs">
                      {item.label}
                    </span>
                    <span className="font-bold text-gray-800 text-sm">
                      {item.count}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-xl shadow border p-4">
              <h3 className="font-bold text-gray-700 text-sm uppercase tracking-wide mb-3">
                SLA Performance
              </h3>
              <div className="space-y-3">
                {Object.entries(SLA).map(([k, v]: [string, any]) => {
                  const cnt = suppliers.filter(
                    (s: Supplier) => stageSlaOf(s) === k,
                  ).length;
                  const pct = total ? Math.round((cnt / total) * 100) : 0;
                  return (
                    <div key={k}>
                      <div className="flex justify-between text-xs mb-1">
                        <span
                          className={`font-semibold ${v.cls.split(" ")[0]}`}
                        >
                          {v.label}
                        </span>
                        <span className="text-gray-500">
                          {cnt} ({pct}%)
                        </span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-current rounded-full transition-all"
                          style={{ width: `${pct}%`, color: "inherit" }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-white rounded-xl shadow border p-4">
              <h3 className="font-bold text-gray-700 text-sm uppercase tracking-wide mb-3">
                By Requesting Company
              </h3>
              {byCompany
                .filter((c) => c.count > 0)
                .map((c) => (
                  <div key={c.name} className="mb-3">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="font-medium text-gray-700">
                        {c.name}
                      </span>
                      <span className="text-gray-400">
                        {c.count} supplier{c.count !== 1 ? "s" : ""}
                      </span>
                    </div>
                    <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-emerald-500 rounded-full"
                        style={{
                          width: `${Math.round((c.count / total) * 100)}%`,
                        }}
                      ></div>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {/* ── GM SLA Deep-Dive ─────────────────────────────────────────────── */}
          {(() => {
            // Active = submitted at least once (SLA is running)
            const active = suppliers.filter(
              (s: Supplier) =>
                ![
                  "Approved",
                  "Disapproved",
                  "Enrolled",
                  "Form Completion",
                ].includes(s.status),
            );
            // Pre-submission (SLA not yet started)
            const preSubmission = suppliers.filter((s: Supplier) =>
              ["Enrolled", "Form Completion"].includes(s.status),
            );
            const slaDelayed = active.filter(
              (s: Supplier) => stageSlaOf(s) === "delayed",
            );
            const slaWarning = active.filter(
              (s: Supplier) => stageSlaOf(s) === "warning",
            );
            const slaOnTime = active.filter(
              (s: Supplier) => stageSlaOf(s) === "on-time",
            );

            // Responsible-team mapping
            const ownerOf = (s: Supplier): string => {
              if (["Enrolled", "Form Completion"].includes(s.status))
                return "Supplier Company";
              if (
                [
                  "Under Review",
                  "For Compliance",
                  "Returned with Remarks",
                ].includes(s.status)
              )
                return "Compliance Officer";
              if (
                [
                  "Recommended for Approval",
                  "Modification Pre-Approved",
                ].includes(s.status)
              )
                return "Compliance Head";
              if (["Modification Endorsement Pending"].includes(s.status))
                return "Requesting Dept.";
              return "—";
            };

            // Stage bottleneck counts
            const stageCounts: {
              stage: string;
              owner: string;
              count: number;
              delayedCount: number;
              bgColor: string;
            }[] = [
              {
                stage: "Form Completion",
                owner: "Supplier Company",
                count: suppliers.filter((s) =>
                  ["Enrolled", "Form Completion"].includes(s.status),
                ).length,
                delayedCount: suppliers.filter(
                  (s) =>
                    ["Enrolled", "Form Completion"].includes(s.status) &&
                    stageSlaOf(s) === "delayed",
                ).length,
                bgColor: "bg-yellow-500",
              },
              {
                stage: "Compliance Review",
                owner: "Compliance Officer",
                count: suppliers.filter((s) =>
                  [
                    "Under Review",
                    "For Compliance",
                    "Returned with Remarks",
                  ].includes(s.status),
                ).length,
                delayedCount: suppliers.filter(
                  (s) =>
                    [
                      "Under Review",
                      "For Compliance",
                      "Returned with Remarks",
                    ].includes(s.status) && stageSlaOf(s) === "delayed",
                ).length,
                bgColor: "bg-orange-500",
              },
              {
                stage: "Final Approval",
                owner: "Compliance Head",
                count: suppliers.filter((s) =>
                  [
                    "Recommended for Approval",
                    "Modification Pre-Approved",
                  ].includes(s.status),
                ).length,
                delayedCount: suppliers.filter(
                  (s) =>
                    [
                      "Recommended for Approval",
                      "Modification Pre-Approved",
                    ].includes(s.status) && stageSlaOf(s) === "delayed",
                ).length,
                bgColor: "bg-cyan-500",
              },
              {
                stage: "Mod. Endorsement",
                owner: "Requesting Dept.",
                count: suppliers.filter(
                  (s) => s.status === "Modification Endorsement Pending",
                ).length,
                delayedCount: suppliers.filter(
                  (s) =>
                    s.status === "Modification Endorsement Pending" &&
                    stageSlaOf(s) === "delayed",
                ).length,
                bgColor: "bg-yellow-500",
              },
            ];

            // Sorted: delayed first, then warning, then on-time; within tier sort by live business days desc
            const slaTableRows = [...active].sort((a, b) => {
              const order: Record<SlaStatus, number> = {
                delayed: 0,
                warning: 1,
                "on-time": 2,
              };
              if (order[stageSlaOf(a)] !== order[stageSlaOf(b)])
                return order[stageSlaOf(a)] - order[stageSlaOf(b)];
              return liveDays(b) - liveDays(a);
            });

            return (
              <div className="space-y-4">
                {/* SLA alert banner */}
                {slaDelayed.length > 0 && (
                  <div className="bg-red-50 border-l-4 border-red-500 rounded-xl px-5 py-4 flex items-start gap-3">
                    <div className="flex-1">
                      <p className="font-bold text-red-800 text-sm">
                        {slaDelayed.length} active application
                        {slaDelayed.length > 1 ? "s" : ""} have exceeded their
                        5-day stage SLA limit
                      </p>
                      <p className="text-xs text-red-700 mt-1">
                        Immediate escalation recommended. Responsible teams are
                        listed in the tracker below.
                      </p>
                    </div>
                  </div>
                )}

                {/* Bottleneck by Stage */}
                <div className="bg-white rounded-xl shadow border p-5">
                  <h3 className="font-bold text-gray-700 text-sm uppercase tracking-wide mb-4">
                    Bottleneck Analysis — Active Stages
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {stageCounts.map((st) => (
                      <div
                        key={st.stage}
                        className={`rounded-xl border p-4 ${st.count === 0 ? "bg-gray-50 border-gray-200" : st.delayedCount > 0 ? "bg-red-50 border-red-200" : "bg-white border-gray-200"}`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <div
                            className={`w-2 h-2 rounded-full ${st.bgColor} shrink-0`}
                          ></div>
                          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                            {st.stage}
                          </span>
                        </div>
                        <div className="text-2xl font-bold text-gray-800">
                          {st.count}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {st.owner}
                        </div>
                        {st.delayedCount > 0 && (
                          <div className="mt-1.5 text-xs font-semibold text-red-600">
                            {st.delayedCount} delayed
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Real-time SLA Tracker */}
                <div className="bg-white rounded-xl shadow border overflow-hidden">
                  <div className="px-5 py-4 bg-gray-50 border-b flex justify-between items-center">
                    <div>
                      <h3 className="font-bold text-gray-700 text-sm uppercase tracking-wide">
                        Real-Time SLA Tracker
                      </h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        SLA runs from supplier submission — {active.length}{" "}
                        application{active.length !== 1 ? "s" : ""} in progress
                        {preSubmission.length > 0 && (
                          <span className="text-gray-400">
                            {" "}
                            · {preSubmission.length} awaiting supplier
                            submission (SLA not started)
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-red-500 inline-block"></span>
                        <span className="text-gray-600">
                          Delayed: {slaDelayed.length}
                        </span>
                      </span>
                      <span className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-yellow-400 inline-block"></span>
                        <span className="text-gray-600">
                          Warning: {slaWarning.length}
                        </span>
                      </span>
                      <span className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span>
                        <span className="text-gray-600">
                          On Time: {slaOnTime.length}
                        </span>
                      </span>
                    </div>
                  </div>
                  {active.length === 0 && preSubmission.length === 0 ? (
                    <div className="px-5 py-10 text-center text-gray-400 text-sm">
                      No active applications at this time.
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b text-xs text-gray-500 uppercase">
                          <tr>
                            <th className="px-4 py-2.5 text-left">
                              Supplier ID
                            </th>
                            <th className="px-4 py-2.5 text-left">
                              Supplier Name
                            </th>
                            <th className="px-4 py-2.5 text-left">Company</th>
                            <th className="px-4 py-2.5 text-left">
                              Current Stage
                            </th>
                            <th className="px-4 py-2.5 text-left">
                              Responsible Team
                            </th>
                            <th className="px-4 py-2.5 text-center">Days</th>
                            <th className="px-4 py-2.5 text-center">
                              Days Left
                            </th>
                            <th className="px-4 py-2.5 text-left">
                              SLA Status
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {active.length === 0 && (
                            <tr>
                              <td
                                colSpan={8}
                                className="px-4 py-6 text-center text-gray-400 text-sm"
                              >
                                No applications currently in review.
                              </td>
                            </tr>
                          )}
                          {pagedSlice(slaTableRows, "slaTracker").map(
                            (s: Supplier) => {
                              const days = liveDays(s);
                              const daysLeft = 10 - days;
                              const daysLeftColor =
                                daysLeft < 0
                                  ? "text-red-600 font-bold"
                                  : daysLeft <= 2
                                    ? "text-yellow-600 font-semibold"
                                    : "text-gray-600";
                              const sla = stageSlaOf(s);
                              const rowBg =
                                sla === "delayed"
                                  ? "bg-red-50"
                                  : sla === "warning"
                                    ? "bg-yellow-50"
                                    : "";
                              return (
                                <tr
                                  key={s.id}
                                  className={`${rowBg} hover:brightness-95 transition-all`}
                                >
                                  <td className="px-4 py-2.5 font-mono text-xs font-semibold text-blue-700">
                                    {s.id}
                                  </td>
                                  <td className="px-4 py-2.5 font-medium text-gray-800">
                                    {s.supplierName}
                                  </td>
                                  <td className="px-4 py-2.5">
                                    <span className="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full font-medium">
                                      {s.company}
                                    </span>
                                  </td>
                                  <td className="px-4 py-2.5">
                                    <Badge status={s.status} />
                                  </td>
                                  <td className="px-4 py-2.5 text-xs text-gray-600">
                                    {ownerOf(s)}
                                  </td>
                                  <td className="px-4 py-2.5 text-center">
                                    <span
                                      className={`font-bold ${sla === "delayed" ? "text-red-600" : sla === "warning" ? "text-yellow-600" : "text-gray-700"}`}
                                    >
                                      {days}
                                    </span>
                                    <span className="text-gray-400 text-xs">
                                      {" "}
                                      / 10
                                    </span>
                                  </td>
                                  <td className="px-4 py-2.5 text-center">
                                    <span
                                      className={`text-sm ${daysLeftColor}`}
                                    >
                                      {daysLeft < 0
                                        ? `+${Math.abs(daysLeft)} over`
                                        : `${daysLeft} left`}
                                    </span>
                                  </td>
                                  <td className="px-4 py-2.5">
                                    <SlaBadge sla={sla} />
                                  </td>
                                </tr>
                              );
                            },
                          )}
                          {/* Pre-submission rows — SLA not yet started */}
                          {preSubmission.length > 0 && (
                            <>
                              <tr className="bg-gray-100">
                                <td
                                  colSpan={8}
                                  className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide"
                                >
                                  Awaiting Supplier Submission — SLA not started
                                  ({preSubmission.length})
                                </td>
                              </tr>
                              {preSubmission.map((s: Supplier) => (
                                <tr
                                  key={s.id}
                                  className="bg-gray-50 opacity-70"
                                >
                                  <td className="px-4 py-2.5 font-mono text-xs font-semibold text-blue-700">
                                    {s.id}
                                  </td>
                                  <td className="px-4 py-2.5 font-medium text-gray-700">
                                    {s.supplierName}
                                  </td>
                                  <td className="px-4 py-2.5">
                                    <span className="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full font-medium">
                                      {s.company}
                                    </span>
                                  </td>
                                  <td className="px-4 py-2.5">
                                    <Badge status={s.status} />
                                  </td>
                                  <td className="px-4 py-2.5 text-xs text-gray-500">
                                    Supplier Company
                                  </td>
                                  <td className="px-4 py-2.5 text-center text-xs text-gray-400">
                                    —
                                  </td>
                                  <td className="px-4 py-2.5 text-center text-xs text-gray-400">
                                    SLA not started
                                  </td>
                                  <td className="px-4 py-2.5">
                                    <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-500 border border-gray-200">
                                      Pending
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </>
                          )}
                        </tbody>
                      </table>
                      <Pager
                        page={pg("slaTracker")}
                        total={tpages(slaTableRows)}
                        count={slaTableRows.length}
                        onChange={(n) => setPg("slaTracker", n)}
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* ── Shared General Analytics ───────────────────────────────────────── */}
      {delayed > 0 && (
        <div className="bg-red-50 border border-red-300 rounded-xl px-4 py-3 flex items-center gap-3">
          <div>
            <p className="font-semibold text-red-800 text-sm">
              {delayed} supplier{delayed > 1 ? "s" : ""} exceeded their 5-day
              stage SLA limit
            </p>
            <p className="text-red-600 text-xs">Immediate action required.</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {metrics.map((m: any) => (
          <div
            key={m.label}
            className={`bg-gradient-to-br ${m.color} text-white rounded-xl p-4 shadow-md`}
          >
            <div className="flex justify-between items-start">
              <div>
                <div className="text-3xl font-bold">{m.val}</div>
                <div className="text-sm font-medium mt-1">{m.label}</div>
                <div className="text-xs opacity-75 mt-0.5">{m.sub}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl shadow border border-blue-200 p-5">
        <div className="flex items-start gap-3 mb-4">
          <div>
            <h3 className="font-bold text-gray-800 text-sm uppercase tracking-wide">
              SLA Timeline: 10 Working Days Target
            </h3>
            <p className="text-xs text-gray-600 mt-1">
              Two-stage approval process with automated status tracking
            </p>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg p-4 border-l-4 border-blue-500">
            <div className="flex items-center gap-2 mb-2">
              <h4 className="font-bold text-gray-800 text-sm">
                Stage 1: Compliance Officer Review
              </h4>
            </div>
            <div className="space-y-1 text-xs text-gray-600">
              <div className="flex justify-between">
                <span>Timeline:</span>
                <span className="font-semibold text-blue-700">Days 1-5</span>
              </div>
              <div className="flex justify-between">
                <span>Target:</span>
                <span className="font-semibold">5 working days</span>
              </div>
              <div className="flex justify-between">
                <span>Status:</span>
                <span className="text-green-600 font-semibold">
                  On-time (Days 0-7)
                </span>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 border-l-4 border-purple-500">
            <div className="flex items-center gap-2 mb-2">
              <h4 className="font-bold text-gray-800 text-sm">
                Stage 2: Head of Compliance Approval
              </h4>
            </div>
            <div className="space-y-1 text-xs text-gray-600">
              <div className="flex justify-between">
                <span>Timeline:</span>
                <span className="font-semibold text-purple-700">Days 6-10</span>
              </div>
              <div className="flex justify-between">
                <span>Target:</span>
                <span className="font-semibold">5 working days</span>
              </div>
              <div className="flex justify-between">
                <span>Status:</span>
                <span className="text-yellow-600 font-semibold">
                  Warning (Days 8-10)
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-3 bg-white rounded-lg p-3 border border-red-200">
          <div className="flex items-center gap-2 text-xs">
            <span className="text-red-600 font-bold">SLA Exceeded:</span>
            <span className="text-gray-600">
              Day 11+ indicates process delays requiring immediate attention
            </span>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl shadow border p-4">
          <h3 className="font-bold text-gray-700 mb-3 text-sm uppercase tracking-wide">
            Status Breakdown
          </h3>
          <div className="space-y-2">
            {Object.entries(byStatus).map(([st, cnt]: [string, any]) => {
              const pct = Math.round(((cnt as number) / total) * 100);
              const dot = STATUS_META[st]?.dot || "bg-gray-400";
              return (
                <div key={st}>
                  <div className="flex justify-between text-sm mb-1">
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${dot}`}></span>
                      <span className="text-gray-700">{st}</span>
                    </div>
                    <span className="font-semibold text-gray-800">
                      {cnt as number}{" "}
                      <span className="text-gray-400 font-normal text-xs">
                        ({pct}%)
                      </span>
                    </span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${dot} rounded-full transition-all`}
                      style={{ width: `${pct}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow border p-4">
          <h3 className="font-bold text-gray-700 mb-3 text-sm uppercase tracking-wide">
            By Requesting Company
          </h3>
          {byCompany
            .filter((c: any) => c.count > 0)
            .map((c: any) => (
              <div key={c.name} className="mb-3">
                <div className="flex justify-between text-sm mb-1">
                  <span className="font-medium text-gray-700">{c.name}</span>
                  <span className="text-gray-400 text-xs">
                    {COMPANY_NAMES[c.name as Company]}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 rounded-full transition-all"
                      style={{
                        width: `${Math.round((c.count / total) * 100)}%`,
                      }}
                    ></div>
                  </div>
                  <span className="font-bold text-gray-800 text-sm w-5 text-right">
                    {c.count}
                  </span>
                </div>
              </div>
            ))}
          <div className="mt-4 pt-3 border-t">
            <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">
              SLA Summary
            </h4>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(SLA).map(([k, v]: [string, any]) => (
                <div
                  key={k}
                  className={`text-center rounded-lg border p-2 ${v.cls}`}
                >
                  <div className="text-lg">{v.icon}</div>
                  <div className="font-bold text-sm">
                    {
                      suppliers.filter((s: Supplier) => stageSlaOf(s) === k)
                        .length
                    }
                  </div>
                  <div className="text-xs">{v.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow border overflow-hidden">
        <div className="px-4 pt-4 pb-3 bg-gray-50 border-b space-y-3">
          <div className="flex justify-between items-center">
            <h3 className="font-bold text-gray-700">All Suppliers</h3>
            <span className="text-xs text-gray-400">
              {dashFiltered.length !== total
                ? `${dashFiltered.length} of ${total}`
                : `${total}`}{" "}
              record{total !== 1 ? "s" : ""}
              {(dashSearch ||
                dashStatusFilter !== "All" ||
                dashCompanyFilter !== "All" ||
                dashSlaFilter !== "All") && (
                <button
                  onClick={() => {
                    setDashSearch("");
                    setDashStatusFilter("All");
                    setDashCompanyFilter("All");
                    setDashSlaFilter("All");
                  }}
                  className="ml-2 text-blue-600 hover:underline"
                >
                  Clear
                </button>
              )}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            <input
              type="text"
              placeholder="Search by ID, name, rep, email…"
              value={dashSearch}
              onChange={(e) => setDashSearch(e.target.value)}
              className="md:col-span-2 border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            <select
              value={dashStatusFilter}
              onChange={(e) => setDashStatusFilter(e.target.value)}
              className="border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              <option value="All">All Statuses</option>
              {[...new Set(suppliers.map((s: Supplier) => s.status))].map(
                (st) => (
                  <option key={st} value={st}>
                    {getStatusLabel(st)}
                  </option>
                ),
              )}
            </select>
            <div className="flex gap-2">
              <select
                value={dashCompanyFilter}
                onChange={(e) => setDashCompanyFilter(e.target.value)}
                className="flex-1 border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                <option value="All">All Companies</option>
                {COMPANIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <select
                value={dashSlaFilter}
                onChange={(e) => setDashSlaFilter(e.target.value)}
                className="flex-1 border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                <option value="All">All SLA</option>
                <option value="on-time">On Time</option>
                <option value="warning">Warning</option>
                <option value="delayed">Delayed</option>
              </select>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr>
                {[
                  "Supplier ID",
                  "Company",
                  "Supplier Name",
                  "Representative",
                  "Status",
                  "SLA",
                  "Days",
                  "Enrolled",
                ].map((h: string) => (
                  <th key={h} className="px-3 py-2 text-left font-semibold">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {dashFiltered.length === 0 && (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-8 text-center text-gray-400 text-sm"
                  >
                    No suppliers match your search or filters.
                  </td>
                </tr>
              )}
              {pagedSlice(dashFiltered, "all").map((s: Supplier) => (
                <tr
                  key={s.id}
                  onClick={() => {
                    onSelectSupplier(s.id);
                    onNav("review");
                  }}
                  className="hover:bg-blue-50 cursor-pointer transition-colors"
                >
                  <td className="px-3 py-2.5 font-mono text-xs font-semibold text-blue-700">
                    {s.id}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className="bg-gray-100 text-gray-700 text-xs px-2 py-0.5 rounded-full font-medium">
                      {s.company}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 font-medium text-gray-800">
                    {s.supplierName}
                  </td>
                  <td className="px-3 py-2.5 text-gray-500">{s.rep}</td>
                  <td className="px-3 py-2.5">
                    <Badge status={s.status} />
                  </td>
                  <td className="px-3 py-2.5">
                    <SlaBadge sla={stageSlaOf(s)} />
                  </td>
                  <td className="px-3 py-2.5">
                    <span
                      className={`font-bold text-sm ${stageSlaOf(s) === "delayed" ? "text-red-600" : stageSlaOf(s) === "warning" ? "text-yellow-600" : "text-gray-700"}`}
                    >
                      {liveDays(s)}
                    </span>
                    <span className="text-gray-400 text-xs"> days</span>
                  </td>
                  <td className="px-3 py-2.5 text-gray-400 text-xs">
                    {fmtDateTime(s.createdAt ?? s.enrollDate)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager
            page={pg("all")}
            total={tpages(dashFiltered)}
            count={dashFiltered.length}
            onChange={(n) => setPg("all", n)}
          />
        </div>
      </div>
    </div>
  );
}

// ── Enroll Form ───────────────────────────────────────────────────────────────
function EnrollForm({
  setSuppliers,
  addNote,
  onDone,
  actorName,
  actorRole,
}: {
  setSuppliers: React.Dispatch<React.SetStateAction<Supplier[]>>;
  addNote: (msg: string) => void;
  onDone: () => void;
  actorName: string;
  actorRole: string;
}) {
  const [form, setForm] = useState<{
    company: Company;
    supplierName: string;
    repFirstName: string;
    repLastName: string;
    email: string;
  }>({
    company: "ECP",
    supplierName: "",
    repFirstName: "",
    repLastName: "",
    email: "",
  });
  const [done, setDone] = useState<{
    id: string;
    company: Company;
    supplierName: string;
    rep: string;
    email: string;
    tempPassword: string;
  } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (
      !form.supplierName.trim() ||
      !form.repFirstName.trim() ||
      !form.repLastName.trim() ||
      !form.email.trim()
    )
      return alert("All fields are required.");
    if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(form.email))
      return alert(
        "Please enter a valid email address (e.g. name@company.com).",
      );

    const rep = `${form.repFirstName.trim()} ${form.repLastName.trim()}`.trim();

    setSubmitting(true);
    try {
      const daysInProcess = 0;
      const newS: Supplier = {
        id: "",
        company: form.company,
        supplierName: form.supplierName,
        rep,
        email: form.email,
        status: "Enrolled",
        enrollDate: new Date().toISOString().split("T")[0],
        daysInProcess,
        slaStatus: calculateSLA(daysInProcess),
        remarks: "",
        formData: {},
        docs: {},
      };

      const tempPassword = Array.from(crypto.getRandomValues(new Uint8Array(6)))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
      const { api } = await import("./api-client");
      const created = await api.createSupplier({
        ...newS,
        actorName,
        actorRole,
        tempPassword,
        repFirstName: form.repFirstName,
        repLastName: form.repLastName,
      });
      setSuppliers((prev) => [...prev, created]);
      addNote(
        `Supplier "${form.supplierName}" enrolled as ${created.id}. Portal credentials sent to ${form.email}.`,
      );
      setDone({
        id: created.id,
        company: form.company,
        supplierName: form.supplierName,
        rep,
        email: form.email,
        tempPassword,
      });
    } catch (error: any) {
      console.error("Failed to create supplier:", error);
      alert(error.message || "Failed to enroll supplier. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done)
    return (
      <div className="max-w-md mx-auto bg-white rounded-2xl shadow border p-8 text-center space-y-4">
        <h3 className="text-xl font-bold text-gray-800">
          Supplier Successfully Enrolled!
        </h3>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-left space-y-2">
          <p className="text-sm font-semibold text-green-800 mb-1">
            Portal Credentials
          </p>
          <p className="text-xs text-green-700">
            A welcome email with these credentials has been sent to the
            supplier. Share them manually if needed.
          </p>
          <div className="bg-white rounded-lg border border-green-200 p-3 space-y-1.5 text-sm mt-2">
            <div className="flex gap-2">
              <span className="text-gray-400 w-28 shrink-0 text-xs">Email</span>
              <span className="text-gray-800">{done.email}</span>
            </div>
            <div className="flex gap-2 pt-1.5 border-t border-green-100">
              <span className="text-gray-400 w-28 shrink-0 text-xs">
                Temp Password
              </span>
              <span className="font-mono font-bold text-blue-700">
                {done.tempPassword}
              </span>
            </div>
          </div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-1">
          <p className="text-xs text-gray-500 uppercase tracking-wide">
            Supplier ID
          </p>
          <p className="font-mono font-bold text-blue-700 text-xl">{done.id}</p>
          <div className="pt-2 border-t border-blue-200 mt-2">
            <p className="text-xs text-gray-600">Requesting Company</p>
            <p className="text-sm font-semibold text-gray-800">
              {done.company} – {COMPANY_NAMES[done.company]}
            </p>
          </div>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-left">
          <p className="text-xs font-semibold text-gray-700 mb-2">
            Next Steps:
          </p>
          <ol className="text-xs text-gray-600 space-y-1 ml-4 list-decimal">
            <li>
              Authorized Representative receives portal access link via email
            </li>
            <li>
              Supplier logs in and completes the Supplier Information Form
            </li>
            <li>Supplier uploads required documents</li>
            <li>Application is submitted for Compliance Review (Steps 5-7)</li>
          </ol>
        </div>
        <p className="text-xs text-gray-500 italic">
          Note: The authorized representative ({done.rep}) will use the portal
          link to access their dedicated supplier portal and complete the
          accreditation application.
        </p>
        <div className="flex gap-2 pt-2">
          <button
            onClick={() => {
              setDone(null);
              setForm({
                company: "ECP",
                supplierName: "",
                repFirstName: "",
                repLastName: "",
                email: "",
              });
            }}
            className="flex-1 bg-blue-600 text-white py-2.5 rounded-lg font-semibold text-sm hover:bg-blue-700"
          >
            Enroll Another Supplier
          </button>
          <button
            onClick={() => onDone()}
            className="flex-1 bg-gray-100 text-gray-700 py-2.5 rounded-lg font-semibold text-sm hover:bg-gray-200"
          >
            View Dashboard
          </button>
        </div>
      </div>
    );

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <div>
            <h3 className="font-bold text-blue-900 text-sm">
              Step 1: Supplier Enrollment
            </h3>
            <p className="text-xs text-blue-700 mt-1">
              Authorized staff of the Requesting Department can enroll new
              supplier companies using this form. A system-generated portal
              access link will be automatically sent to the Authorized
              Representative's email.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow border p-6 space-y-5">
        <div className="border-b pb-4">
          <h2 className="text-xl font-bold text-gray-800">
            Enroll New Supplier Company
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Complete the primary information below. The authorized
            representative will receive portal access credentials via email.
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Requesting Company <span className="text-red-500">*</span>
          </label>
          <select
            className="w-full border rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
            value={form.company}
            onChange={(e) =>
              setForm({ ...form, company: e.target.value as Company })
            }
          >
            {COMPANIES.map((c: Company) => (
              <option key={c} value={c}>
                {c} – {COMPANY_NAMES[c]}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Select which ECPG company is requesting this supplier accreditation
          </p>
        </div>
        {(
          [
            [
              "supplierName",
              "Name of the Supplier Company",
              "text",
              "e.g. ABC Tech Solutions, Inc.",
            ],
            [
              "repFirstName",
              "Authorized Representative's First Name",
              "text",
              "e.g. Juan",
            ],
            [
              "repLastName",
              "Authorized Representative's Last Name",
              "text",
              "e.g. dela Cruz",
            ],
            [
              "email",
              "Email Address of Authorized Representative",
              "email",
              "e.g. juan@supplier.com",
            ],
          ] as const
        ).map(([k, l, t, ph]) => (
          <div key={k}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {l} <span className="text-red-500">*</span>
            </label>
            <input
              type={t}
              placeholder={ph}
              className="w-full border rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
              value={(form as any)[k]}
              onChange={(e) => setForm({ ...form, [k]: e.target.value })}
            />
            {k === "email" && (
              <p className="text-xs text-gray-500 mt-1">
                A system-generated access link will be sent to this email
                address
              </p>
            )}
          </div>
        ))}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <p className="text-xs text-yellow-800">
            <strong>Note:</strong> Upon enrollment, a system-generated email
            containing the portal access link will be automatically sent to the
            Authorized Representative's email address. They will use this link
            to access the Supplier Portal and complete the Supplier Information
            Form.
          </p>
        </div>
        <button
          onClick={submit}
          disabled={submitting}
          className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span>
            {submitting
              ? "Enrolling Supplier..."
              : "Enroll Supplier & Send Portal Access Link"}
          </span>
        </button>
      </div>
    </div>
  );
}

// ── Supplier Info Form ────────────────────────────────────────────────────────
function SupplierInfoForm({
  supplier,
  setSuppliers,
  addNote,
}: {
  supplier: Supplier;
  setSuppliers: React.Dispatch<React.SetStateAction<Supplier[]>>;
  addNote: (msg: string) => void;
}) {
  const editable = [
    "Enrolled",
    "Form Completion",
    "For Compliance",
    "Modification Approved",
  ].includes(supplier.status);
  const modScope = supplier.formData?.modScope;
  // When modScope is set (from a modification request), restrict editability per scope.
  // This applies both during "Modification Approved" and during "For Compliance" that results
  // from a modification resubmission (isModificationResubmission flag is preserved in formData).
  const isModApproved = supplier.status === "Modification Approved";
  const isModForCompliance =
    supplier.status === "For Compliance" &&
    !!supplier.formData?.isModificationResubmission;
  const isModContext = isModApproved || isModForCompliance;
  const editableForm = editable && !(isModContext && modScope === "documents");
  const editableDocs = editable && !(isModContext && modScope === "form");
  const [tab, setTab] = useState<number>(0);

  // If the active tab is no longer in the visible tabs list (e.g. after a scope change), reset to 0
  useEffect(() => {
    if (!tabs[tab]) setTab(0);
  }, [modScope, supplier.status]);
  const init: GeneralFormData = supplier.formData?.gen || {
    bizType: "",
    companyName: "",
    sec: "",
    address: "",
    mainCat: "",
    subCat: "",
    subCatOther: "",
    dateEst: "",
    tel: "",
    capital: "",
    payTerms: "",
    tin: "",
    yearsOp: "",
  };
  const [fd, setFd] = useState<GeneralFormData>(init);
  const [banks, setBanks] = useState<BankReference[]>(
    supplier.formData?.banks || [
      { name: "", acctName: "", acctNo: "" },
      { name: "", acctName: "", acctNo: "" },
    ],
  );
  const [sigs, setSigs] = useState<Signatory[]>(
    supplier.formData?.sigs || [
      { name: "", position: "", contact: "", email: "" },
      { name: "", position: "", contact: "", email: "" },
    ],
  );
  const [clients, setClients] = useState<Client[]>(
    supplier.formData?.clients || [
      { company: "", contact: "", email: "" },
      { company: "", contact: "", email: "" },
      { company: "", contact: "", email: "" },
    ],
  );
  const [docs, setDocs] = useState<{ [key: string]: string }>(
    supplier.docs || {},
  );
  // Capture the original form data & docs at mount time so the resubmission snapshot
  // reflects the true "before" state — even after doc uploads persist to the DB mid-session.
  const originalRef = useRef<FormDataSnapshot>({
    gen: supplier.formData?.gen,
    banks: supplier.formData?.banks,
    sigs: supplier.formData?.sigs,
    clients: supplier.formData?.clients,
    docs: supplier.docs ? { ...supplier.docs } : {},
  });
  const [certified, setCertified] = useState<boolean>(false);
  const [showConsent, setShowConsent] = useState<boolean>(false);
  const [privacyOk, setPrivacyOk] = useState<boolean>(false);
  const [hasScrolledPolicy, setHasScrolledPolicy] = useState<boolean>(false);
  const [saved, setSaved] = useState<boolean>(false);
  const [wasResubmission, setWasResubmission] = useState<boolean>(false);
  const [uploading, setUploading] = useState<string | null>(null);

  const mandatoryDocs = [
    "Company Profile",
    "SEC Registration and Latest Amended Articles of Incorporation",
    "Latest Business Permit",
    "Latest BIR Form 2302",
    "Latest General Information Sheet",
    "BIR Tax Clearance Certificate",
  ];
  const optionalDocs = [
    "DTI Certificate",
    "Certifications (e.g., ISO, PEZA)",
    "Other Supporting Documents",
  ];
  const allMandatory = mandatoryDocs.every((d: string) => docs[d]);

  const sf = (k: keyof GeneralFormData, v: string) =>
    setFd((p: GeneralFormData) => ({ ...p, [k]: v }));

  const persist = async (extra: Partial<Supplier> = {}): Promise<boolean> => {
    try {
      if (extra.status && extra.status === supplier.status) {
        delete extra.status;
      }
      const { api } = await import("./api-client");
      const updated = await api.updateSupplier(supplier.id, {
        ...extra,
        formData: {
          gen: fd,
          banks,
          sigs,
          clients,
          ...(supplier.formData?.modScope
            ? { modScope: supplier.formData.modScope }
            : {}),
          ...(supplier.formData?.isModificationResubmission
            ? { isModificationResubmission: true }
            : {}),
          // Always carry preModSnapshot through saves — it is the authoritative original and must never be lost
          ...(supplier.formData?.preModSnapshot
            ? { preModSnapshot: supplier.formData.preModSnapshot }
            : {}),
          // Preserve snapshot so the reviewer diff stays intact even after mid-edit saves
          ...(supplier.formData?.snapshot
            ? { snapshot: supplier.formData.snapshot }
            : {}),
        },
        docs,
      });
      setSuppliers((prev) =>
        prev.map((s) => (s.id === supplier.id ? updated : s)),
      );
      return true;
    } catch (error: any) {
      console.error("Failed to persist supplier data:", error);
      addNote(
        `Failed to save data to database: ${error?.message || String(error)}`,
      );
      return false;
    }
  };

  const saveForm = async () => {
    const mandatoryFields: { key: keyof GeneralFormData; label: string }[] = [
      { key: "bizType", label: "Business Type" },
      { key: "companyName", label: "Company Name" },
      { key: "sec", label: "SEC Registration Number" },
      { key: "tin", label: "Tax Identification Number (TIN)" },
      { key: "address", label: "Registered Address" },
      { key: "mainCat", label: "Main Category" },
      { key: "subCat", label: "Sub-Category" },
      { key: "dateEst", label: "Date Established" },
      { key: "tel", label: "Telephone Number" },
      { key: "capital", label: "Estimated Capital" },
      { key: "payTerms", label: "Payment Terms" },
      { key: "yearsOp", label: "Years in Operation" },
    ];
    if (fd.subCat === "Other")
      mandatoryFields.push({
        key: "subCatOther",
        label: "Sub-Category (specify)",
      });
    const missing = mandatoryFields.filter(
      (f) => !fd[f.key]?.toString().trim(),
    );
    if (missing.length > 0)
      return alert(
        `Cannot Save — Missing Required Fields\n\nPlease fill in the following mandatory fields before saving:\n\n${missing.map((f, i) => `${i + 1}. ${f.label}`).join("\n")}`,
      );
    if (fd.yearsOp && Number(fd.yearsOp) < 0)
      return alert(
        "Cannot Save — Invalid Value\n\nYears in Operation cannot be a negative number.",
      );
    if (fd.capital && Number(fd.capital) < 0)
      return alert(
        "Cannot Save — Invalid Value\n\nEstimated Capital cannot be a negative number.",
      );
    if (fd.dateEst && new Date(fd.dateEst) > new Date())
      return alert(
        "Cannot Save — Invalid Date\n\nDate Established cannot be a future date.",
      );
    const extra: Partial<Supplier> = {};
    if (supplier.status === "Enrolled") {
      extra.status = "Form Completion";
    }
    const ok = await persist(extra);
    if (!ok) return;
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
    addNote(`Form data saved for ${supplier.supplierName}.`);
    // Advance to the next tab automatically after saving (unless already on the last tab)
    setTab((prev: number) => (prev < tabs.length - 1 ? prev + 1 : prev));
  };

  // Helper: extract a human-readable filename from a Vercel Blob URL
  const docLabel = (val: string) =>
    val.startsWith("https://")
      ? decodeURIComponent(val.split("/").pop() ?? "file.pdf").replace(
          /^\d+-/,
          "",
        )
      : val;
  const docIsUrl = (val: string) => val.startsWith("https://");
  const docViewHref = (val: string) =>
    `/api/blob-url?url=${encodeURIComponent(val)}`;

  const uploadDoc = async (name: string, file: File | null | undefined) => {
    if (!file) return;
    if (file.type !== "application/pdf")
      return alert(
        "Invalid file format!\n\nOnly PDF files are accepted. Please convert your file to PDF and try again.",
      );
    if (file.size > 25 * 1024 * 1024)
      return alert(
        `File size too large!\n\nMaximum file size is 25MB. Your file is ${(file.size / (1024 * 1024)).toFixed(2)}MB.\nPlease compress the file and try again.`,
      );

    setUploading(name);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("supplierId", supplier.id);
      const res = await fetch("/api/upload", {
        method: "POST",
        headers: getAuthHeaders(),
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Upload failed");
      }
      const { url } = await res.json();
      const updated = { ...docs, [name]: url };
      setDocs(updated);
      const { api } = await import("./api-client");
      const updatedSupplier = await api.updateSupplier(supplier.id, {
        docs: updated,
      });
      setSuppliers((prev) =>
        prev.map((s) => (s.id === supplier.id ? updatedSupplier : s)),
      );
      addNote(`"${name}" uploaded successfully.`);
    } catch (error: any) {
      console.error("Failed to upload document:", error);
      addNote(`Upload failed: ${error?.message || "Unknown error"}`);
    } finally {
      setUploading(null);
    }
  };

  const submitApp = () => {
    const mandatoryFields: { key: keyof GeneralFormData; label: string }[] = [
      { key: "bizType", label: "Business Type" },
      { key: "companyName", label: "Company Name" },
      { key: "sec", label: "SEC Registration Number" },
      { key: "tin", label: "Tax Identification Number (TIN)" },
      { key: "address", label: "Registered Address" },
      { key: "mainCat", label: "Main Category" },
      { key: "subCat", label: "Sub-Category" },
      { key: "dateEst", label: "Date Established" },
      { key: "tel", label: "Telephone Number" },
      { key: "capital", label: "Estimated Capital" },
      { key: "payTerms", label: "Payment Terms" },
      { key: "yearsOp", label: "Years in Operation" },
    ];
    if (fd.subCat === "Other")
      mandatoryFields.push({
        key: "subCatOther",
        label: "Sub-Category (specify)",
      });
    const missingFields = mandatoryFields.filter(
      (f) => !fd[f.key]?.toString().trim(),
    );
    if (missingFields.length > 0)
      return alert(
        `Cannot Submit — Missing Required Fields\n\nPlease complete the following mandatory fields in the General Info tab before submitting:\n\n${missingFields.map((f, i) => `${i + 1}. ${f.label}`).join("\n")}`,
      );
    if (!allMandatory) {
      const missing = mandatoryDocs.filter((d: string) => !docs[d]);
      return alert(
        `Missing Required Documents\n\nYou must upload all ${mandatoryDocs.length} mandatory documents before submission.\n\nMissing documents:\n${missing.map((d, i) => `${i + 1}. ${d}`).join("\n")}`,
      );
    }
    if (!certified)
      return alert(
        "Certification Required\n\nPlease tick the certification checkbox to confirm that all information provided is true and correct.",
      );
    setShowConsent(true);
  };

  const confirmConsent = async () => {
    if (!privacyOk)
      return alert(
        "Consent Required\n\nPlease tick the checkbox to confirm that you have read, understood, and agree to the Data Privacy Consent Form before proceeding.",
      );
    const isResubmission = supplier.status === "For Compliance";
    setWasResubmission(isResubmission);
    // On first submission the SLA clock starts at day 1.
    // On re-submission after "For Compliance", the clock simply RESUMES from where it
    // was paused — the supplier fix period must not count against reviewer SLA.
    // On first submission and re-submission, always reset reviewStartDate to today
    // so the SLA clock starts fresh from the date the supplier (re-)submits.
    const reviewStartDate = TODAY;
    const daysInProcess = reviewStartDate
      ? countWorkingDays(reviewStartDate)
      : 1;
    // Capture snapshot of previous formData/docs when resubmitting so reviewers
    // can see exactly which fields/files changed.
    // Also treat "For Compliance" as a mod-resubmission when the formData flag is set —
    // this preserves isModificationResubmission + modScope when CO returns the submission
    // for corrections and the supplier re-submits again.
    const isModResubmission =
      supplier.status === "Modification Approved" ||
      (supplier.status === "For Compliance" &&
        !!supplier.formData?.isModificationResubmission);
    const needsSnapshot = isResubmission || isModResubmission;
    // For mod resubmissions, prefer preModSnapshot (saved server-side when modification was approved —
    // guaranteed to be the real original). Fall back to snapshot (client-captured), then originalRef.
    const snapshot: FormDataSnapshot | undefined = needsSnapshot
      ? isModResubmission
        ? (supplier.formData?.preModSnapshot ??
          supplier.formData?.snapshot ??
          originalRef.current)
        : originalRef.current
      : undefined;

    try {
      const { api } = await import("./api-client");
      const updated = await api.updateSupplier(supplier.id, {
        status: "Under Review",
        reviewStartDate,
        daysInProcess,
        slaStatus: calculateSLA(daysInProcess),
        // Stamp isModificationResubmission so CO/CH can reliably detect the resubmission
        // type without relying solely on approvalDate being preserved in-state.
        formData: {
          gen: fd,
          banks,
          sigs,
          clients,
          ...(snapshot ? { snapshot } : {}),
          ...(isModResubmission
            ? {
                isModificationResubmission: true,
                ...(supplier.formData?.modScope
                  ? { modScope: supplier.formData.modScope }
                  : {}),
                ...(supplier.formData?.preModSnapshot
                  ? { preModSnapshot: supplier.formData.preModSnapshot }
                  : {}),
              }
            : {}),
        },
        docs,
        remarks: "",
      });
      setSuppliers((prev) =>
        prev.map((s) => (s.id === supplier.id ? updated : s)),
      );

      if (isResubmission) {
        addNote(
          `Application RE-SUBMITTED for ${supplier.supplierName} (${supplier.id}). Status changed back to "Under Review". Compliance Officer has been notified to review the corrections and additional documents.`,
        );
      } else {
        addNote(
          `Application successfully submitted for ${supplier.supplierName}. Status changed to "Under Review". Compliance Officer has been notified for evaluation.`,
        );
      }
    } catch (error) {
      console.error("Failed to submit application:", error);
      alert("Failed to submit application. Please try again.");
    }

    setShowConsent(false);
    setPrivacyOk(false);
    setHasScrolledPolicy(false);
  };

  const tabs =
    isModContext && modScope === "form"
      ? ["General Info", "Bank References", "Signatories", "Major Clients"]
      : isModContext && modScope === "documents"
        ? ["Documents & Submit"]
        : [
            "General Info",
            "Bank References",
            "Signatories",
            "Major Clients",
            "Documents & Submit",
          ];

  return (
    <div className="space-y-4">
      {supplier.status === "Enrolled" && (
        <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <h3 className="font-bold text-blue-900 mb-1">
                Step 2: Supplier Information Form Completion
              </h3>
              <p className="text-sm text-blue-800 mb-2">
                Welcome to your Supplier Portal! You have successfully accessed
                your portal using the link sent to your registered email
                address. Please complete the Supplier Information Form below to
                proceed with your accreditation.
              </p>
              <ul className="text-xs text-blue-700 space-y-1 ml-4 list-disc">
                <li>
                  <strong>General Information:</strong> All fields are mandatory
                  - please fill out all required business details
                </li>
                <li>
                  <strong>Bank References, Signatories & Clients:</strong>{" "}
                  Optional but recommended for faster processing
                </li>
                <li>
                  <strong>Documents:</strong> Upload all 6 mandatory documents
                  before final submission
                </li>
                <li>
                  <strong>Save Regularly:</strong> Click "Save" buttons to
                  preserve your progress as you work through each section
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {supplier.status === "For Compliance" && (
        <div className="bg-amber-50 border-l-4 border-amber-500 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <h3 className="font-bold text-amber-900 mb-1">
                Action Required: Edit & Re-Submit
              </h3>
              <p className="text-sm text-amber-800 mb-2">
                Your application requires corrections or additional information.
                Please review the compliance remarks below, make the necessary
                changes, and re-submit your application.
              </p>
              <div className="bg-white rounded-lg border border-amber-300 p-3 mb-2">
                <p className="text-xs font-semibold text-amber-900 mb-1">
                  Compliance Officer's Remarks:
                </p>
                <p className="text-sm text-gray-800 font-medium">
                  {supplier.remarks || "No specific remarks provided."}
                </p>
              </div>
              <ul className="text-xs text-amber-700 space-y-1 ml-4 list-disc">
                <li>
                  <strong>Edit Form:</strong> Make corrections to any incorrect
                  information in the tabs below
                </li>
                <li>
                  <strong>Update Documents:</strong> Go to "Documents & Submit"
                  tab to replace or add documents
                </li>
                <li>
                  <strong>Save Changes:</strong> Click "Save" after editing each
                  section
                </li>
                <li>
                  <strong>Re-Submit:</strong> Once all corrections are complete,
                  re-submit via the Documents tab
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow border p-4 flex justify-between items-center">
        <div>
          <h2 className="font-bold text-gray-800 text-lg">
            Supplier Information Form
          </h2>
          <p className="text-sm text-gray-500">
            {supplier.id} • {supplier.supplierName}
          </p>
        </div>
        <Badge status={supplier.status} />
      </div>

      {supplier.remarks && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl px-4 py-3 text-sm text-amber-800">
          <strong>Compliance Remarks:</strong> {supplier.remarks}
        </div>
      )}

      <div className="flex gap-1 flex-wrap">
        {tabs.map((t: string, i: number) => (
          <button
            key={t}
            onClick={() => setTab(i)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${tab === i ? "bg-blue-600 text-white" : "bg-white border text-gray-600 hover:bg-gray-50"}`}
          >
            {t}
          </button>
        ))}
      </div>

      {tabs[tab] === "General Info" && (
        <div className="bg-white rounded-xl shadow border p-5 space-y-4">
          <h3 className="font-bold text-gray-800 border-b pb-2">
            I. General Information{" "}
            <span className="text-red-500 text-xs">(All fields mandatory)</span>
          </h3>
          {isModContext && modScope === "documents" && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-xs text-amber-800">
              <strong>Locked:</strong> Your modification request is for
              documents only. Form fields cannot be edited.
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Business Type *
              </label>
              <select
                disabled={!editableForm}
                className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                value={fd.bizType}
                onChange={(e) => sf("bizType", e.target.value)}
              >
                <option value="">Select...</option>
                {["Sole Proprietorship", "Partnership", "Corporation"].map(
                  (o: string) => (
                    <option key={o}>{o}</option>
                  ),
                )}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Company Name *
              </label>
              <input
                disabled={!editableForm}
                className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                value={fd.companyName}
                onChange={(e) => sf("companyName", e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                SEC Registration Number *
              </label>
              <input
                disabled={!editableForm}
                className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                maxLength={11}
                value={fd.sec}
                onChange={(e) => sf("sec", e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Tax Identification Number (TIN) *
              </label>
              <input
                disabled={!editableForm}
                className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                maxLength={9}
                value={fd.tin}
                onChange={(e) => sf("tin", e.target.value)}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Registered Address *
              </label>
              <input
                disabled={!editableForm}
                className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                value={fd.address}
                onChange={(e) => sf("address", e.target.value)}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-2">
                Nature of Business / Category *
              </label>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Main Category *
              </label>
              <select
                disabled={!editableForm}
                className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                value={fd.mainCat}
                onChange={(e) => {
                  sf("mainCat", e.target.value);
                  sf("subCat", "");
                }}
              >
                <option value="">Select...</option>
                {Object.keys(CATEGORIES).map((c: string) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Sub-Category *
              </label>
              <select
                disabled={!editableForm || !fd.mainCat}
                className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                value={fd.subCat}
                onChange={(e) => sf("subCat", e.target.value)}
              >
                <option value="">Select...</option>
                {(CATEGORIES[fd.mainCat] || []).map((s: string) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </div>
            {fd.subCat === "Other" && (
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Please specify (Sub-Category: Other) *
                </label>
                <input
                  disabled={!editableForm}
                  className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                  placeholder="Specify your nature of business"
                  value={fd.subCatOther}
                  onChange={(e) => sf("subCatOther", e.target.value)}
                />
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Date Established *
              </label>
              <input
                type="date"
                disabled={!editableForm}
                max={new Date().toISOString().split("T")[0]}
                className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                value={fd.dateEst}
                onChange={(e) => sf("dateEst", e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Telephone Number *
              </label>
              <input
                disabled={!editableForm}
                className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                maxLength={8}
                value={fd.tel}
                onChange={(e) => sf("tel", e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Estimated Capital in PHP *
              </label>
              <input
                type="text"
                disabled={!editableForm}
                className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                placeholder="e.g. 500,000"
                value={
                  !editableForm && fd.capital
                    ? fmtCurrency(fd.capital)
                    : fd.capital
                }
                onChange={(e) =>
                  sf("capital", e.target.value.replace(/[^0-9.]/g, ""))
                }
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Payment Terms *
              </label>
              <select
                disabled={!editableForm}
                className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                value={fd.payTerms}
                onChange={(e) => sf("payTerms", e.target.value)}
              >
                <option value="">— Select Payment Terms —</option>
                <option value="COD">COD</option>
                <option value="30 days">30 days</option>
                <option value="60 days">60 days</option>
                <option value="90 days">90 days</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Years in Operation *
              </label>
              <input
                type="number"
                min="0"
                disabled={!editableForm}
                className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                placeholder="e.g. 5"
                value={fd.yearsOp}
                onChange={(e) => sf("yearsOp", e.target.value)}
              />
            </div>
          </div>
          {editableForm && (
            <button
              onClick={saveForm}
              className={`px-6 py-2 rounded-lg font-semibold text-sm transition ${saved ? "bg-green-600 text-white" : "bg-blue-600 text-white hover:bg-blue-700"}`}
            >
              {saved ? "Saved!" : "Save Information"}
            </button>
          )}
        </div>
      )}

      {tabs[tab] === "Bank References" && (
        <div className="bg-white rounded-xl shadow border p-5 space-y-4">
          <h3 className="font-bold text-gray-800 border-b pb-2">
            II. Bank References{" "}
            <span className="text-gray-400 text-sm">
              (Optional - Provide at least 2 bank accounts)
            </span>
          </h3>
          {isModContext && modScope === "documents" && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-xs text-amber-800">
              <strong>Locked:</strong> Your modification request is for
              documents only. Form fields cannot be edited.
            </div>
          )}
          {[0, 1].map((i: number) => (
            <div key={i} className="border rounded-lg p-4 bg-gray-50">
              <p className="text-sm font-semibold text-gray-700 mb-3">
                Bank Account {i + 1}
              </p>
              <div className="grid grid-cols-3 gap-3">
                {(
                  [
                    ["name", "Bank Name"],
                    ["acctName", "Account Name"],
                    ["acctNo", "Account Number"],
                  ] as const
                ).map(([k, l]) => (
                  <div key={k}>
                    <label className="block text-xs text-gray-500 mb-1">
                      {l}
                    </label>
                    <input
                      disabled={!editableForm}
                      className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                      value={(banks[i] as any)[k]}
                      onChange={(e) => {
                        const b = [...banks];
                        b[i] = { ...b[i], [k]: e.target.value };
                        setBanks(b);
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
          {editableForm && (
            <button
              onClick={saveForm}
              className={`px-6 py-2 rounded-lg font-semibold text-sm transition ${saved ? "bg-green-600 text-white" : "bg-blue-600 text-white hover:bg-blue-700"}`}
            >
              {saved ? "Saved!" : "Save"}
            </button>
          )}
        </div>
      )}

      {tabs[tab] === "Signatories" && (
        <div className="bg-white rounded-xl shadow border p-5 space-y-4">
          <h3 className="font-bold text-gray-800 border-b pb-2">
            III. Authorized Signatories{" "}
            <span className="text-gray-400 text-sm">
              (Optional - Provide up to 2 signatories)
            </span>
          </h3>
          {isModContext && modScope === "documents" && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-xs text-amber-800">
              <strong>Locked:</strong> Your modification request is for
              documents only. Form fields cannot be edited.
            </div>
          )}
          {[0, 1].map((i: number) => (
            <div key={i} className="border rounded-lg p-4 bg-gray-50">
              <p className="text-sm font-semibold text-gray-700 mb-3">
                Signatory {i + 1}
              </p>
              <div className="grid grid-cols-2 gap-3">
                {(
                  [
                    ["name", "Full Name"],
                    ["position", "Position"],
                    ["contact", "Contact Number"],
                    ["email", "Email Address"],
                  ] as const
                ).map(([k, l]) => (
                  <div key={k}>
                    <label className="block text-xs text-gray-500 mb-1">
                      {l}
                      {k === "contact" && (
                        <span className="text-gray-400"> (max 12 digits)</span>
                      )}
                    </label>
                    <input
                      disabled={!editableForm}
                      className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                      {...(k === "contact" ? { maxLength: 12 } : {})}
                      value={(sigs[i] as any)[k]}
                      onChange={(e) => {
                        const v =
                          k === "contact"
                            ? e.target.value.replace(/[^0-9]/g, "")
                            : e.target.value;
                        const s = [...sigs];
                        s[i] = { ...s[i], [k]: v };
                        setSigs(s);
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
          {editableForm && (
            <button
              onClick={saveForm}
              className={`px-6 py-2 rounded-lg font-semibold text-sm transition ${saved ? "bg-green-600 text-white" : "bg-blue-600 text-white hover:bg-blue-700"}`}
            >
              {saved ? "Saved!" : "Save"}
            </button>
          )}
        </div>
      )}

      {tabs[tab] === "Major Clients" && (
        <div className="bg-white rounded-xl shadow border p-5 space-y-4">
          <h3 className="font-bold text-gray-800 border-b pb-2">
            IV. Major Clients – Top 3{" "}
            <span className="text-gray-400 text-sm">
              (Optional - List up to 3 major clients)
            </span>
          </h3>
          {isModContext && modScope === "documents" && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-xs text-amber-800">
              <strong>Locked:</strong> Your modification request is for
              documents only. Form fields cannot be edited.
            </div>
          )}
          {[0, 1, 2].map((i: number) => (
            <div key={i} className="border rounded-lg p-4 bg-gray-50">
              <p className="text-sm font-semibold text-gray-700 mb-3">
                Client {i + 1}
              </p>
              <div className="grid grid-cols-3 gap-3">
                {(
                  [
                    ["company", "Company Name"],
                    ["contact", "Contact Number"],
                    ["email", "Email"],
                  ] as const
                ).map(([k, l]) => (
                  <div key={k}>
                    <label className="block text-xs text-gray-500 mb-1">
                      {l}
                    </label>
                    <input
                      disabled={!editableForm}
                      className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                      value={(clients[i] as any)[k]}
                      onChange={(e) => {
                        const c = [...clients];
                        c[i] = { ...c[i], [k]: e.target.value };
                        setClients(c);
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
          {editableForm && (
            <button
              onClick={saveForm}
              className={`px-6 py-2 rounded-lg font-semibold text-sm transition ${saved ? "bg-green-600 text-white" : "bg-blue-600 text-white hover:bg-blue-700"}`}
            >
              {saved ? "Saved!" : "Save"}
            </button>
          )}
          {isModContext && modScope === "form" && (
            <div className="border-t pt-4 space-y-3">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <label className="flex items-start gap-3 cursor-pointer select-none group">
                  <input
                    type="checkbox"
                    className="mt-1 w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    checked={certified}
                    onChange={(e) => setCertified(e.target.checked)}
                  />
                  <div>
                    <span className="text-sm font-semibold text-gray-800 group-hover:text-blue-700">
                      I hereby certify and declare:
                    </span>
                    <p className="text-sm text-gray-700 mt-1">
                      That all information provided in this Supplier Information
                      Form is <strong>true, correct, and complete</strong> to
                      the best of my knowledge and belief.
                    </p>
                  </div>
                </label>
              </div>
              <div className="flex gap-3 flex-wrap items-center">
                <div className="relative group/resubmit">
                  <button
                    onClick={submitApp}
                    disabled={!allMandatory}
                    className={`px-8 py-3 rounded-lg font-semibold text-sm transition flex items-center gap-2 ${allMandatory ? "bg-green-600 text-white hover:bg-green-700 shadow-md hover:shadow-lg" : "bg-gray-300 text-gray-500 cursor-not-allowed"}`}
                  >
                    Re-Submit Application
                  </button>
                  {!allMandatory && (
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 bg-gray-800 text-white text-xs rounded-lg px-3 py-2 opacity-0 group-hover/resubmit:opacity-100 transition-opacity pointer-events-none z-10 text-center shadow-lg">
                      {`${mandatoryDocs.filter((d: string) => !docs[d]).length} mandatory document(s) still missing. Upload all 6 required documents in the Documents tab before re-submitting.`}
                      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
                    </div>
                  )}
                </div>
              </div>
              {!certified && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
                  <strong>Reminder:</strong> Please tick the certification
                  checkbox above before submitting.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {tabs[tab] === "Documents & Submit" && (
        <div className="space-y-4">
          {supplier.status === "For Compliance" && (
            <div className="bg-yellow-50 border-l-4 border-yellow-500 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <h3 className="font-bold text-yellow-900 mb-1">
                    Document Update Required
                  </h3>
                  <p className="text-sm text-yellow-800 mb-2">
                    Please review your uploaded documents and replace any that
                    were identified as missing, expired, or incorrect in the
                    compliance remarks. You can also add additional supporting
                    documents if requested.
                  </p>
                  <ul className="text-xs text-yellow-700 space-y-1 ml-4 list-disc">
                    <li>
                      <strong>Replace Documents:</strong> Click "Replace" button
                      to upload a new version of any document
                    </li>
                    <li>
                      <strong>Add Missing:</strong> Upload any documents that
                      were identified as missing
                    </li>
                    <li>
                      <strong>Check Validity:</strong> Ensure all permits and
                      registrations are current and not expired
                    </li>
                    <li>
                      <strong>Re-Submit:</strong> After updating documents,
                      scroll down and re-submit your application
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}
          <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <h3 className="font-bold text-green-900 mb-1">
                  Step 3: Uploading of Accreditation Documents
                </h3>
                <p className="text-sm text-green-800 mb-2">
                  After completing and saving your Supplier Information Form,
                  proceed to upload all required documents. All mandatory
                  documents must be uploaded before you can submit your
                  application.
                </p>
                <ul className="text-xs text-green-700 space-y-1 ml-4 list-disc">
                  <li>
                    <strong>File Format:</strong> PDF only
                  </li>
                  <li>
                    <strong>Maximum Size:</strong> 25MB per document
                  </li>
                  <li>
                    <strong>Mandatory Documents:</strong> 6 documents must be
                    uploaded to proceed
                  </li>
                  <li>
                    <strong>Optional Documents:</strong> Recommended but not
                    required for submission
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow border p-5">
            <div className="flex justify-between items-start mb-4 pb-2 border-b">
              <div>
                <h3 className="font-bold text-gray-800">
                  Upload Requirements{" "}
                  <span className="text-xs text-gray-400 font-normal">
                    PDF only • Max 25MB per file
                  </span>
                </h3>
              </div>
              <div className="text-right">
                <div className="text-xs text-gray-500">Upload Progress</div>
                <div
                  className={`text-lg font-bold ${allMandatory ? "text-green-600" : "text-gray-800"}`}
                >
                  {mandatoryDocs.filter((d: string) => docs[d]).length}/
                  {mandatoryDocs.length}
                </div>
                <div className="text-xs text-gray-400">mandatory docs</div>
              </div>
            </div>
            {isModContext && modScope === "form" && (
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-xs text-amber-800 mb-2">
                <strong>Locked:</strong> Your modification request is for
                form/profile only. Documents cannot be changed.
              </div>
            )}
            <p className="text-xs font-bold text-red-600 uppercase mb-2">
              Mandatory Documents (Required)
            </p>
            <div className="space-y-2 mb-4">
              {mandatoryDocs.map((d: string) => (
                <div
                  key={d}
                  className={`flex items-center justify-between rounded-lg border px-4 py-3 transition ${docs[d] ? "border-green-300 bg-green-50" : "border-gray-200 hover:border-blue-300"}`}
                >
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-700">{d}</p>
                    {docs[d] ? (
                      <p className="text-xs text-green-600 mt-0.5 flex items-center gap-2 flex-wrap">
                        <span>{docLabel(docs[d])}</span>
                        {docIsUrl(docs[d]) && (
                          <button
                            type="button"
                            onClick={() => triggerDownload(docs[d])}
                            className="underline text-blue-600"
                          >
                            View ↗
                          </button>
                        )}
                      </p>
                    ) : (
                      <p className="text-xs text-red-400 mt-0.5">
                        Not uploaded yet
                      </p>
                    )}
                  </div>
                  {editableDocs && (
                    <div className="flex items-center gap-2">
                      {docs[d] && (
                        <button
                          type="button"
                          disabled={!!uploading}
                          onClick={async () => {
                            if (
                              !confirm(
                                `Remove "${d}"?\n\nYou will need to re-upload this document before submitting.`,
                              )
                            )
                              return;
                            const previous = { ...docs };
                            const updated = { ...docs };
                            delete updated[d];
                            setDocs(updated);
                            try {
                              const { api } = await import("./api-client");
                              const updatedSupplier = await api.updateSupplier(
                                supplier.id,
                                { docs: updated },
                              );
                              setSuppliers((prev: Supplier[]) =>
                                prev.map((s: Supplier) =>
                                  s.id === supplier.id ? updatedSupplier : s,
                                ),
                              );
                              addNote(
                                `"${d}" removed from ${supplier.supplierName}.`,
                              );
                            } catch {
                              alert(
                                "Failed to remove document. Please try again.",
                              );
                              setDocs(previous);
                            }
                          }}
                          className="text-xs px-3 py-1.5 rounded-lg bg-red-100 text-red-700 hover:bg-red-200 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                        >
                          Remove
                        </button>
                      )}
                      <label
                        className={`cursor-pointer text-white text-xs px-3 py-1.5 rounded-lg transition whitespace-nowrap flex items-center gap-1 ${uploading === d ? "bg-gray-400 pointer-events-none" : "bg-blue-600 hover:bg-blue-700"}`}
                      >
                        <span>
                          {uploading === d
                            ? "Uploading..."
                            : docs[d]
                              ? "Replace"
                              : "Upload PDF"}
                        </span>
                        <input
                          type="file"
                          accept="application/pdf"
                          className="hidden"
                          disabled={!!uploading}
                          onClick={(e) => {
                            (e.target as HTMLInputElement).value = "";
                          }}
                          onChange={(e) => uploadDoc(d, e.target.files?.[0])}
                        />
                      </label>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {allMandatory && editableDocs && (
              <div className="bg-green-50 border border-green-300 rounded-lg p-3 flex items-center gap-2 mb-4">
                <div className="text-sm text-green-800">
                  <strong>All mandatory documents uploaded!</strong> You can now
                  proceed to certification and submit your application.
                </div>
              </div>
            )}
            <p className="text-xs font-bold text-gray-400 uppercase mb-2 mt-6">
              Non-Mandatory Documents (Optional - Recommended)
            </p>
            <p className="text-xs text-gray-500 mb-3 italic">
              These documents are not required for submission but may help
              expedite the evaluation process.
            </p>
            <div className="space-y-2">
              {optionalDocs.map((d: string) => (
                <div
                  key={d}
                  className={`flex items-center justify-between rounded-lg border px-4 py-3 ${docs[d] ? "border-green-300 bg-green-50" : "border-dashed border-gray-200 bg-gray-50"}`}
                >
                  <div>
                    <p className="text-sm text-gray-600">{d}</p>
                    {docs[d] && (
                      <p className="text-xs text-green-600 mt-0.5 flex items-center gap-2">
                        <span>{docLabel(docs[d])}</span>
                        {docIsUrl(docs[d]) && (
                          <button
                            type="button"
                            onClick={() => triggerDownload(docs[d])}
                            className="underline text-blue-600"
                          >
                            View ↗
                          </button>
                        )}
                      </p>
                    )}
                  </div>
                  {editableDocs && (
                    <div className="flex items-center gap-2">
                      {docs[d] && (
                        <button
                          type="button"
                          disabled={!!uploading}
                          onClick={async () => {
                            if (!confirm(`Remove "${d}"?`)) return;
                            const previous = { ...docs };
                            const updated = { ...docs };
                            delete updated[d];
                            setDocs(updated);
                            try {
                              const { api } = await import("./api-client");
                              const updatedSupplier = await api.updateSupplier(
                                supplier.id,
                                { docs: updated },
                              );
                              setSuppliers((prev: Supplier[]) =>
                                prev.map((s: Supplier) =>
                                  s.id === supplier.id ? updatedSupplier : s,
                                ),
                              );
                              addNote(
                                `"${d}" removed from ${supplier.supplierName}.`,
                              );
                            } catch {
                              alert(
                                "Failed to remove document. Please try again.",
                              );
                              setDocs(previous);
                            }
                          }}
                          className="text-xs px-3 py-1.5 rounded-lg bg-red-100 text-red-700 hover:bg-red-200 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                        >
                          Remove
                        </button>
                      )}
                      <label
                        className={`cursor-pointer text-xs px-3 py-1.5 rounded-lg transition whitespace-nowrap ${uploading === d ? "bg-gray-100 text-gray-400 pointer-events-none" : "bg-gray-200 text-gray-600 hover:bg-gray-300"}`}
                      >
                        {uploading === d ? "Uploading..." : "Upload PDF"}
                        <input
                          type="file"
                          accept="application/pdf"
                          className="hidden"
                          disabled={!!uploading}
                          onClick={(e) => {
                            (e.target as HTMLInputElement).value = "";
                          }}
                          onChange={(e) => uploadDoc(d, e.target.files?.[0])}
                        />
                      </label>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {editable && (
            <div className="space-y-4">
              <div
                className={`border-l-4 rounded-lg p-4 ${supplier.status === "For Compliance" ? "bg-amber-50 border-amber-500" : "bg-purple-50 border-purple-500"}`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <h3
                      className={`font-bold mb-1 ${supplier.status === "For Compliance" ? "text-amber-900" : "text-purple-900"}`}
                    >
                      {supplier.status === "For Compliance"
                        ? "Step 6: Re-Certification & Re-Submission"
                        : "Step 4: Certification & Submission"}
                    </h3>
                    <p
                      className={`text-sm mb-2 ${supplier.status === "For Compliance" ? "text-amber-800" : "text-purple-800"}`}
                    >
                      {supplier.status === "For Compliance"
                        ? "After making all required corrections and updating your documents, you must re-certify the accuracy of your information and re-submit your application for Compliance Officer review."
                        : "Before submitting your application, you must formally certify that all information provided is accurate and complete. After clicking Submit, a Data Privacy Consent Form will appear for your review and acceptance."}
                    </p>
                    <ul
                      className={`text-xs space-y-1 ml-4 list-disc ${supplier.status === "For Compliance" ? "text-amber-700" : "text-purple-700"}`}
                    >
                      <li>
                        <strong>Certification Required:</strong> Tick the
                        certification box below to confirm the accuracy of your{" "}
                        {supplier.status === "For Compliance"
                          ? "corrected "
                          : ""}
                        information
                      </li>
                      <li>
                        <strong>Complete All Uploads:</strong> All 6 mandatory
                        documents must be uploaded before{" "}
                        {supplier.status === "For Compliance" ? "re-" : ""}
                        submission
                      </li>
                      <li>
                        <strong>Data Privacy Consent:</strong> You will be asked
                        to review and accept the Data Privacy Consent Form
                      </li>
                      <li>
                        <strong>Final Review:</strong> Once{" "}
                        {supplier.status === "For Compliance" ? "re-" : ""}
                        submitted, your application will be{" "}
                        {supplier.status === "For Compliance"
                          ? "returned to the "
                          : "queued for "}
                        Compliance Officer{" "}
                        {supplier.status === "For Compliance"
                          ? "for re-evaluation"
                          : "review"}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow border p-5 space-y-4">
                <h3 className="font-semibold text-gray-800 text-lg">
                  {supplier.status === "For Compliance"
                    ? "Re-Certification & Re-Submission"
                    : "Certification & Formal Submission"}
                </h3>
                {!allMandatory && (
                  <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 flex items-start gap-2">
                    <span className="text-yellow-600 text-lg"></span>
                    <div className="text-sm text-yellow-800">
                      <strong>Action Required:</strong> You must upload all 6
                      mandatory documents before you can submit your
                      application. Currently uploaded:{" "}
                      <strong>
                        {mandatoryDocs.filter((d: string) => docs[d]).length}/
                        {mandatoryDocs.length}
                      </strong>
                    </div>
                  </div>
                )}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <label className="flex items-start gap-3 cursor-pointer select-none group">
                    <input
                      type="checkbox"
                      className="mt-1 w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      checked={certified}
                      onChange={(e) => setCertified(e.target.checked)}
                    />
                    <div>
                      <span className="text-sm font-semibold text-gray-800 group-hover:text-blue-700">
                        I hereby certify and declare:
                      </span>
                      <p className="text-sm text-gray-700 mt-1">
                        That all information provided in this Supplier
                        Information Form and all documents submitted are{" "}
                        <strong>true, correct, and complete</strong> to the best
                        of my knowledge and belief. I understand that any false
                        information or misrepresentation may result in
                        disqualification or termination of accreditation.
                      </p>
                    </div>
                  </label>
                </div>
                <div className="flex gap-3 flex-wrap items-center pt-2">
                  <button
                    onClick={submitApp}
                    disabled={!allMandatory}
                    className={`px-8 py-3 rounded-lg font-semibold text-sm transition flex items-center gap-2 ${allMandatory ? "bg-green-600 text-white hover:bg-green-700 shadow-md hover:shadow-lg" : "bg-gray-300 text-gray-500 cursor-not-allowed"}`}
                  >
                    <span>
                      {allMandatory
                        ? supplier.status === "For Compliance"
                          ? "Re-Submit Application"
                          : "Submit Application"
                        : "Complete Upload to Submit"}
                    </span>
                  </button>
                </div>
                {!certified && allMandatory && (
                  <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
                    <strong>Reminder:</strong> Please tick the certification
                    checkbox above before submitting your application.
                  </div>
                )}
              </div>
            </div>
          )}

          {!editable && (
            <div className="space-y-3">
              <div className="bg-green-50 border-2 border-green-400 rounded-xl p-5">
                <div className="flex-1 mb-3">
                  <h3 className="font-bold text-green-900 text-lg">
                    {wasResubmission
                      ? "Application Successfully Re-Submitted!"
                      : "Application Successfully Submitted!"}
                  </h3>
                  <p className="text-sm text-green-700 mt-1">
                    {wasResubmission
                      ? "Your corrected application has been re-submitted and is now back in the Compliance Officer's review queue."
                      : "Your supplier accreditation application has been formally submitted and is now queued for review."}
                  </p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-green-200 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Current Status:</span>
                    <Badge status={supplier.status} />
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Supplier ID:</span>
                    <span className="font-mono font-semibold text-blue-700">
                      {supplier.id}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      {wasResubmission ? "Re-Submitted On" : "Submitted On"}:
                    </span>
                    <span className="font-semibold text-gray-800">
                      {fmtDate(supplier.enrollDate)}
                    </span>
                  </div>
                </div>
                <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-blue-800">
                    <strong>Next Steps:</strong>
                  </p>
                  <ul className="text-xs text-blue-700 space-y-1 ml-4 list-disc mt-1">
                    <li>
                      {wasResubmission
                        ? "The Compliance Officer will review your corrections and updated documents"
                        : "Your application will be reviewed by the Compliance Officer"}
                    </li>
                    <li>
                      You will be notified of any updates or required actions
                      via email
                    </li>
                    <li>
                      You can track your status by logging into the Supplier
                      Portal
                    </li>
                    <li>
                      Estimated processing time:{" "}
                      {wasResubmission
                        ? "5 working days for re-review"
                        : "10 working days (SLA target)"}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Consent Modal */}
      {showConsent && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full p-6 space-y-4">
            <div className="flex items-center gap-3 border-b pb-3">
              <h3 className="font-bold text-xl text-gray-800">
                Data Privacy Consent Form
              </h3>
            </div>

            {/* Scroll-gated privacy text */}
            <div
              className="h-72 overflow-y-auto border border-gray-300 rounded-xl p-4 space-y-3 text-sm"
              onScroll={(e) => {
                const el = e.currentTarget;
                if (el.scrollTop + el.clientHeight >= el.scrollHeight - 8) {
                  setHasScrolledPolicy(true);
                }
              }}
            >
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-2">
                  Purpose of Data Collection
                </h4>
                <p className="text-sm text-blue-800">
                  By submitting this application, you consent to the collection,
                  processing, and storage of your personal and business
                  information in accordance with the{" "}
                  <strong>
                    Data Privacy Act of 2012 (Republic Act No. 10173)
                  </strong>
                  . Your information will be used solely for supplier
                  accreditation purposes.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h4 className="font-semibold text-gray-800 mb-2">
                  What We Collect
                </h4>
                <ul className="text-sm text-gray-700 space-y-1 ml-4 list-disc">
                  <li>
                    Company information (name, address, registration details)
                  </li>
                  <li>Authorized representative's contact information</li>
                  <li>
                    Business documents (permits, registrations, certifications)
                  </li>
                  <li>Banking and financial information</li>
                  <li>Transaction and performance records</li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h4 className="font-semibold text-gray-800 mb-2">
                  How We Use Your Data
                </h4>
                <ul className="text-sm text-gray-700 space-y-1 ml-4 list-disc">
                  <li>
                    Evaluate and process your supplier accreditation application
                  </li>
                  <li>
                    Verify business legitimacy and compliance requirements
                  </li>
                  <li>Maintain records for audit and regulatory purposes</li>
                  <li>Communicate regarding your accreditation status</li>
                  <li>Process transactions and business activities</li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h4 className="font-semibold text-gray-800 mb-2">
                  Data Sharing
                </h4>
                <p className="text-sm text-gray-700">
                  Your data will not be shared with third parties except as
                  required by law or with your explicit consent. Internal access
                  is limited to authorized personnel involved in the
                  accreditation process.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h4 className="font-semibold text-gray-800 mb-2">
                  Data Retention
                </h4>
                <p className="text-sm text-gray-700">
                  Your data will be retained for the duration required by
                  applicable laws and regulations. After the retention period,
                  data will be securely disposed of in accordance with ECPG's
                  data management policies.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h4 className="font-semibold text-gray-800 mb-2">
                  Your Rights Under RA 10173
                </h4>
                <ul className="text-sm text-gray-700 space-y-1 ml-4 list-disc">
                  <li>
                    Right to be informed about data collection and processing
                  </li>
                  <li>Right to access your personal data held by ECPG</li>
                  <li>Right to correct inaccurate or outdated information</li>
                  <li>
                    Right to object to data processing in certain circumstances
                  </li>
                  <li>
                    Right to erasure or blocking of data (subject to legal
                    retention requirements)
                  </li>
                  <li>Right to data portability</li>
                  <li>Right to lodge a complaint with the NPC</li>
                </ul>
              </div>

              <div className="bg-green-50 rounded-lg p-3 border border-green-300">
                <p className="text-xs text-green-800">
                  <strong>Data Protection Officer:</strong> For privacy concerns
                  or to exercise your rights, contact our DPO at{" "}
                  <strong>dpo@ecpg.com</strong> or call{" "}
                  <strong>(02) 8888-ECPG</strong>.
                </p>
              </div>
            </div>

            {/* Scroll prompt */}
            {!hasScrolledPolicy && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-center">
                Please scroll to the bottom of the Data Privacy text above to
                enable the agreement.
              </p>
            )}

            {/* Checkbox — only active after scrolling */}
            <div
              className={`border-t pt-4 ${!hasScrolledPolicy ? "opacity-40 pointer-events-none select-none" : ""}`}
            >
              <label
                className={`flex items-start gap-3 rounded-lg p-3 border transition ${hasScrolledPolicy ? "cursor-pointer bg-yellow-50 border-yellow-300 hover:bg-yellow-100" : "bg-gray-50 border-gray-200"}`}
              >
                <input
                  type="checkbox"
                  className="mt-1 w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                  checked={privacyOk}
                  onChange={(e) => setPrivacyOk(e.target.checked)}
                  disabled={!hasScrolledPolicy}
                />
                <span className="text-sm text-gray-800">
                  <strong>I have read, understood, and agree</strong> to the
                  Data Privacy Consent Form and authorize ECPG to collect,
                  process, and store my personal and business information for
                  supplier accreditation purposes in accordance with RA 10173.
                </span>
              </label>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={confirmConsent}
                disabled={!privacyOk || !hasScrolledPolicy}
                className={`flex-1 py-3 rounded-lg font-semibold text-sm transition ${
                  privacyOk && hasScrolledPolicy
                    ? "bg-green-600 text-white hover:bg-green-700"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
              >
                Confirm & Submit Application
              </button>
              <button
                onClick={() => {
                  setShowConsent(false);
                  setPrivacyOk(false);
                  setHasScrolledPolicy(false);
                }}
                className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-lg text-sm font-semibold hover:bg-gray-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Review Panel ──────────────────────────────────────────────────────────────
function ReviewPanel({
  suppliers,
  setSuppliers,
  role,
  addNote,
  initialId,
  actorName,
  refreshSuppliers,
}: {
  suppliers: Supplier[];
  setSuppliers: React.Dispatch<React.SetStateAction<Supplier[]>>;
  role: Role;
  addNote: (msg: string) => void;
  initialId: string | null;
  actorName: string;
  refreshSuppliers?: () => Promise<void>;
}) {
  const [selId, setSelId] = useState<string | null>(initialId || null);
  const [remarks, setRemarks] = useState<string>("");
  const [modReason, setModReason] = useState<string>("");
  const [rdModScope, setRdModScope] = useState<"form" | "documents" | "both">(
    "both",
  );
  const [filter, setFilter] = useState<string>("All");
  const [search, setSearch] = useState<string>("");
  const [acting, setActing] = useState<boolean>(false);
  const [txHistory, setTxHistory] = useState<AuditEntry[]>([]);
  const [txLoading, setTxLoading] = useState<boolean>(false);
  const [txExpanded, setTxExpanded] = useState<boolean>(false);
  const [formViewExpanded, setFormViewExpanded] = useState<boolean>(false);
  const [evalForms, setEvalForms] = useState<Record<string, string>>({});
  const [evalUploading, setEvalUploading] = useState<boolean>(false);

  const sel = suppliers.find((s: Supplier) => s.id === selId);

  // Load per-supplier transaction history whenever the selected supplier changes
  useEffect(() => {
    if (!selId) {
      setTxHistory([]);
      setTxExpanded(false);
      setFormViewExpanded(false);
      return;
    }
    setTxExpanded(false);
    setFormViewExpanded(false);
    setTxLoading(true);
    import("./api-client").then(({ api }) =>
      api
        .getAuditTrail(selId)
        .then((entries) => setTxHistory(entries))
        .catch(() => setTxHistory([]))
        .finally(() => setTxLoading(false)),
    );
  }, [selId]);
  useEffect(() => {
    setEvalForms(sel?.evaluation_forms ?? {});
  }, [selId]);

  // Only these roles can download forms, documents, and certificates
  const canDownload = [
    "Compliance Officer",
    "Compliance Head",
    "Requesting Department",
  ].includes(role);

  const act = async (
    id: string,
    status: SupplierStatus,
    note: string,
    extra: Partial<Supplier> = {},
  ) => {
    setActing(true);
    try {
      const { api } = await import("./api-client");
      const sel = suppliers.find((s) => s.id === id);
      const payload: any = {
        status,
        remarks: remarks || sel?.remarks || "",
        ...extra,
      };
      const reviewStartDate = payload.reviewStartDate ?? sel?.reviewStartDate;
      const daysInProcess =
        payload.daysInProcess ??
        (reviewStartDate
          ? countWorkingDays(reviewStartDate)
          : (sel?.daysInProcess ?? 0));
      payload.daysInProcess = daysInProcess;
      payload.slaStatus = calculateSLA(daysInProcess);
      payload.actorName = actorName;
      payload.actorRole = role as string;
      payload.actorNote = note;
      const updated = await api.updateSupplier(id, payload);
      setSuppliers((prev) => prev.map((s) => (s.id === id ? updated : s)));
      addNote(note);
      setRemarks("");
      await refreshSuppliers?.();
    } catch (error) {
      console.error("Failed to update supplier:", error);
      addNote("Failed to update supplier status");
    } finally {
      setActing(false);
    }
  };
  const dl = (label: string) => addNote(`${label} downloaded for ${sel?.id}`);

  const uploadEvalForm = async (file: File | null | undefined) => {
    if (!file || !sel) return;
    const allowed = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];
    if (!allowed.includes(file.type))
      return alert(
        "Invalid file format!\n\nOnly PDF, DOC, DOCX, XLS, XLSX files are accepted.",
      );
    if (file.size > 25 * 1024 * 1024)
      return alert(
        `File size too large!\n\nMaximum 25MB. Your file is ${(file.size / (1024 * 1024)).toFixed(2)}MB.`,
      );
    setEvalUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("supplierId", sel.id);
      const uploadRes = await fetch("/api/upload", {
        method: "POST",
        headers: getAuthHeaders(),
        body: formData,
      });
      if (!uploadRes.ok) {
        const err = await uploadRes.json();
        throw new Error(err.error || "Upload failed");
      }
      const { url } = await uploadRes.json();
      if (!url || typeof url !== "string" || !url.startsWith("https://")) {
        throw new Error(
          "Upload succeeded but returned an invalid URL. Please try again.",
        );
      }
      const key = `${Date.now()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
      const updated = { ...evalForms, [key]: url };
      const { api } = await import("./api-client");
      const updatedSupplier = await api.updateSupplier(sel.id, {
        evaluation_forms: updated,
      });
      setSuppliers((prev) =>
        prev.map((s) => (s.id === sel.id ? updatedSupplier : s)),
      );
      setEvalForms(updated);
      addNote(
        `Evaluation form "${file.name}" uploaded for ${sel.supplierName} by ${actorName}.`,
      );
    } catch (e: any) {
      alert(
        `Failed to upload evaluation form: ${e.message || "Please try again."}`,
      );
    } finally {
      setEvalUploading(false);
    }
  };

  const deleteEvalForm = async (key: string) => {
    if (!sel || !confirm(`Delete "${evalForms[key]}"?`)) return;
    const updated = { ...evalForms };
    delete updated[key];
    try {
      const { api } = await import("./api-client");
      const updatedSupplier = await api.updateSupplier(sel.id, {
        evaluation_forms: updated,
      });
      setSuppliers((prev) =>
        prev.map((s) => (s.id === sel.id ? updatedSupplier : s)),
      );
      setEvalForms(updated);
      addNote(
        `Evaluation form "${evalForms[key]}" deleted from ${sel.supplierName} by ${actorName}.`,
      );
    } catch {
      alert("Failed to delete evaluation form. Please try again.");
    }
  };

  const allStatuses = [...new Set(suppliers.map((s: Supplier) => s.status))];
  const statusDisplayName: Record<string, string> = STATUS_DISPLAY_NAMES;
  const queue = suppliers.filter((s: Supplier) => {
    const matchStatus =
      filter === "All"
        ? true
        : filter === "CO_PENDING"
          ? ["Under Review", "Modification Requested"].includes(s.status)
          : filter === "CH_PENDING"
            ? [
                "Recommended for Approval",
                "Modification Pre-Approved",
              ].includes(s.status)
            : s.status === filter;
    const q = search.trim().toLowerCase();
    const matchSearch =
      !q ||
      s.id.toLowerCase().includes(q) ||
      s.supplierName.toLowerCase().includes(q) ||
      s.rep.toLowerCase().includes(q) ||
      s.email.toLowerCase().includes(q);
    return matchStatus && matchSearch;
  });
  const pendingReview = suppliers.filter(
    (s: Supplier) =>
      s.status === "Under Review" || s.status === "Modification Requested",
  ).length;
  const pendingFinalReview = suppliers.filter(
    (s: Supplier) =>
      s.status === "Recommended for Approval" ||
      s.status === "Modification Pre-Approved",
  ).length;

  const isComplianceHeadStage =
    !!sel?.chStartDate &&
    ["Recommended for Approval", "Modification Pre-Approved"].includes(
      sel.status,
    );
  const chStageDays = isComplianceHeadStage
    ? countWorkingDays(sel!.chStartDate!)
    : 0;
  const chStageSla = isComplianceHeadStage
    ? calculateStageSLA(chStageDays)
    : "on-time";
  const chDaysLeft = isComplianceHeadStage ? 5 - chStageDays : 0;

  if (suppliers.length === 0)
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center text-gray-400 space-y-4">
        <h3 className="text-xl font-semibold text-gray-500">
          No suppliers to review yet
        </h3>
        <p className="text-sm max-w-xs">
          Once a supplier is enrolled and submits their application, it will
          appear here for review.
        </p>
      </div>
    );

  return (
    <div className="space-y-4">
      {isComplianceHeadStage &&
        (role === "Requesting Department" || role === "Compliance Officer") && (
          <div
            className={`flex items-center justify-between rounded-lg px-4 py-3 border ${chStageSla === "delayed" ? "bg-red-50 border-red-200" : chStageSla === "warning" ? "bg-yellow-50 border-yellow-200" : "bg-purple-50 border-purple-200"}`}
          >
            <div className="flex items-center gap-2">
              <span
                className={`w-2 h-2 rounded-full ${chStageSla === "delayed" ? "bg-red-500" : chStageSla === "warning" ? "bg-yellow-500 animate-pulse" : "bg-purple-500"}`}
              />
              <div>
                <p className="text-sm font-semibold text-gray-800">
                  Compliance Head Review: Day {chStageDays} of 5
                </p>
                <p className="text-xs text-gray-600">
                  This supplier is currently in the Compliance Head review
                  stage.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <SlaBadge sla={chStageSla} />
              <span
                className={`text-xs font-medium ${chDaysLeft <= 0 ? "text-red-700" : chDaysLeft <= 1 ? "text-yellow-700" : "text-gray-600"}`}
              >
                {chDaysLeft > 0
                  ? `${chDaysLeft} day${chDaysLeft !== 1 ? "s" : ""} left`
                  : `${Math.abs(chDaysLeft)} day${Math.abs(chDaysLeft) !== 1 ? "s" : ""} overdue`}
              </span>
            </div>
          </div>
        )}
      {['Compliance Officer', 'Compliance Head', 'Requesting Department'].includes(role) && pendingReview > 0 && (
        <div className="bg-indigo-50 border-l-4 border-indigo-500 rounded-lg p-4 flex items-center gap-3">
          <span className="text-2xl"></span>
          <div className="flex-1">
            <h3 className="font-bold text-indigo-900">
              New Application{pendingReview > 1 ? "s" : ""} Submitted
            </h3>
            <p className="text-sm text-indigo-800">
              {role === "Compliance Officer" ? (
                <>
                  You have <strong>{pendingReview}</strong> supplier application{pendingReview > 1 ? "s" : ""} awaiting review. Please evaluate within <strong>5 working days</strong> from submission date.
                </>
              ) : (
                <>
                  There {pendingReview > 1 ? "are" : "is"} <strong>{pendingReview}</strong> supplier application{pendingReview > 1 ? "s" : ""} awaiting review from the <strong>Compliance Officer</strong>.
                </>
              )}
            </p>
          </div>
          <button
            onClick={() => setFilter("CO_PENDING")}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-indigo-700 whitespace-nowrap"
          >
            View {pendingReview} Pending
          </button>
        </div>
      )}

      {[
        "Compliance Head",
        "Compliance Officer",
        "Requesting Department",
      ].includes(role) &&
        pendingFinalReview > 0 && (
          <div className="bg-cyan-50 border-l-4 border-cyan-600 rounded-lg p-4 flex items-center gap-3">
            <span className="text-2xl"></span>
            <div className="flex-1">
              <h3 className="font-bold text-cyan-900">
                Application{pendingFinalReview > 1 ? "s" : ""} Awaiting Final
                Review
              </h3>
              <p className="text-sm text-cyan-800">
                {role === "Compliance Head" ? (
                  <> 
                    You have <strong>{pendingFinalReview}</strong> application
                    {pendingFinalReview > 1 ? "s" : ""} requiring final
                    certification review. Please complete within{" "}
                    <strong>5 working days</strong>.
                  </>
                ) : (
                  <> 
                    There {pendingFinalReview > 1 ? "are" : "is"} <strong>{pendingFinalReview}</strong> application
                    {pendingFinalReview > 1 ? "s" : ""} requiring the <strong>Compliance Head</strong> final certification review.
                  </>
                )}
              </p>
            </div>
            <button
              onClick={() => setFilter("CH_PENDING")}
              className="bg-cyan-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-cyan-700 whitespace-nowrap"
            >
              View {pendingFinalReview} Pending
            </button>
          </div>
        )}
      <div className="grid md:grid-cols-5 gap-4">
        <div className="md:col-span-2 space-y-3">
          <div>
            <div className="flex justify-between items-center mb-2">
              <h2 className="font-bold text-gray-800">Supplier Queue</h2>
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                {queue.length}
                {queue.length !== suppliers.length
                  ? ` of ${suppliers.length}`
                  : ""}{" "}
                supplier{queue.length !== 1 ? "s" : ""}
              </span>
            </div>
            <input
              type="text"
              placeholder="Search by ID, name, rep…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            <select
              className="w-full border rounded-lg px-3 py-2 text-sm"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <option value="All">All Statuses</option>
              {filter === "CO_PENDING" && (
                <option value="CO_PENDING">Pending Review</option>
              )}
              {filter === "CH_PENDING" && (
                <option value="CH_PENDING">Pending Final Review</option>
              )}
              {allStatuses.map((s: string) => (
                <option key={s} value={s}>
                  {statusDisplayName[s] ?? s} (
                  {suppliers.filter((sup: Supplier) => sup.status === s).length}
                  )
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2 max-h-[calc(100vh-260px)] overflow-y-auto pr-1">
            {queue.length === 0 && (
              <p className="text-sm text-gray-400 text-center py-6">
                No suppliers in this filter.
              </p>
            )}
            {queue.map((s: Supplier) => (
              <div
                key={s.id}
                onClick={() => setSelId(s.id)}
                className={`bg-white border rounded-xl p-3 cursor-pointer hover:shadow-md transition ${selId === s.id ? "border-blue-500 shadow-md" : ""} ${s.status === "Under Review" ? "border-l-4 border-l-indigo-500" : ""}`}
              >
                <div className="flex justify-between items-start mb-1">
                  <p className="font-semibold text-sm text-gray-800">
                    {s.supplierName}
                  </p>
                  <div className="flex flex-col items-end gap-1 shrink-0 ml-2">
                    {s.status === "Under Review" &&
                      role === "Compliance Officer" && (
                        <span className="bg-indigo-600 text-white text-xs font-bold px-2 py-0.5 rounded-full animate-pulse">
                          NEW
                        </span>
                      )}
                    <SlaBadge sla={stageSlaOf(s)} />
                  </div>
                </div>
                <p className="text-xs font-mono text-blue-600">{s.id}</p>
                <div className="flex justify-between items-center mt-2">
                  <Badge status={s.status} />
                  <span
                    className={`text-xs font-bold ${stageSlaOf(s) === "delayed" ? "text-red-600" : stageSlaOf(s) === "warning" ? "text-yellow-600" : "text-gray-400"}`}
                  >
                    {liveDays(s)} days
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="md:col-span-3">
          {sel ? (
            <div className="bg-white rounded-xl shadow border p-5 space-y-4">
              <div className="flex justify-between items-start border-b pb-3">
                <div>
                  <h3 className="font-bold text-lg text-gray-800">
                    {sel.supplierName}
                  </h3>
                  <p className="text-xs font-mono text-blue-700">{sel.id}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {sel.rep} • {sel.email}
                  </p>
                </div>
                <div className="text-right space-y-1">
                  <Badge status={sel.status} />
                  <div className="mt-1">
                    <SlaBadge sla={stageSlaOf(sel)} />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3 text-center">
                {[
                  ["Enrolled", fmtDate(sel.enrollDate)],
                  ["Days in Process", `${liveDays(sel)} days`],
                  ["Company", sel.company],
                ].map(([l, v]) => (
                  <div key={l} className="bg-gray-50 rounded-lg p-2">
                    <p className="text-xs text-gray-500">{l}</p>
                    <p className="font-semibold text-gray-800 text-sm mt-0.5">
                      {v}
                    </p>
                  </div>
                ))}
              </div>

              {(() => {
                const rdStage =
                  sel.status === "Modification Endorsement Pending";
                const coStage = [
                  "Under Review",
                  "For Compliance",
                  "Returned with Remarks",
                  "Modification Requested",
                ].includes(sel.status);
                const chStage = [
                  "Recommended for Approval",
                  "Modification Pre-Approved",
                ].includes(sel.status);
                if (!rdStage && !coStage && !chStage) return null;

                const stageStart =
                  rdStage || coStage ? sel.reviewStartDate : sel.chStartDate;
                const stageDays = stageStart ? countWorkingDays(stageStart) : 0;
                const stageSla = calculateStageSLA(stageDays);
                const daysLeft = 5 - stageDays;
                const stageLabel = rdStage
                  ? "Modification Endorsement Review"
                  : coStage
                    ? "Compliance Officer Review"
                    : "Compliance Head Review";
                const stageColors = rdStage
                  ? {
                      bg: "bg-amber-50",
                      border: "border-amber-200",
                      text: "text-amber-800",
                      dot: "bg-amber-500",
                    }
                  : coStage
                    ? {
                        bg: "bg-orange-50",
                        border: "border-orange-200",
                        text: "text-orange-800",
                        dot: "bg-orange-500",
                      }
                    : {
                        bg: "bg-purple-50",
                        border: "border-purple-200",
                        text: "text-purple-800",
                        dot: "bg-purple-500",
                      };

                return (
                  <div
                    className={`flex items-center justify-between rounded-lg px-4 py-2.5 border ${stageColors.bg} ${stageColors.border}`}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={`w-2 h-2 rounded-full ${stageColors.dot}`}
                      />
                      <p className="text-sm font-semibold text-gray-800">
                        {stageLabel}: Day {Math.max(stageDays, 1)} of 5
                      </p>
                    </div>
                    <SlaBadge sla={stageSla} />
                  </div>
                );
              })()}

              {sel.formData?.gen?.companyName &&
                (() => {
                  // Prefer preModSnapshot (server-authoritative original saved at "Modification Approved")
                  // over snapshot (client-captured at submit time). This ensures the diff always compares
                  // against the true approved baseline, not a potentially-stale first-submission snapshot.
                  const snap =
                    sel.formData.preModSnapshot ?? sel.formData.snapshot;
                  const hasSnap = !!snap;
                  const genFields: [string, keyof GeneralFormData][] = [
                    ["Company", "companyName"],
                    ["Business Type", "bizType"],
                    ["TIN", "tin"],
                    ["Category", "mainCat"],
                    ["Sub-Category", "subCat"],
                    ["SEC", "sec"],
                    ["Address", "address"],
                    ["Telephone", "tel"],
                    ["Capital (₱)", "capital"],
                    ["Payment Terms", "payTerms"],
                    ["Years Operating", "yearsOp"],
                    ["Date Established", "dateEst"],
                  ];
                  const changedGenCount = hasSnap
                    ? genFields.filter(([, k]) => {
                        const cur = sel.formData.gen![k];
                        return cur && cur !== (snap!.gen?.[k] ?? "__na__");
                      }).length
                    : 0;
                  const changedDocKeys = hasSnap
                    ? Object.keys(sel.docs || {}).filter(
                        (k) => sel.docs[k] !== (snap!.docs?.[k] ?? "__na__"),
                      )
                    : [];
                  // Compare array sections (banks, signatories, major clients)
                  const snapBanks = snap?.banks ?? [];
                  const snapSigs = snap?.sigs ?? [];
                  const snapClients = snap?.clients ?? [];
                  const curBanks = sel.formData.banks ?? [];
                  const curSigs = sel.formData.sigs ?? [];
                  const curClients = sel.formData.clients ?? [];
                  const banksChanged =
                    hasSnap &&
                    JSON.stringify(curBanks) !== JSON.stringify(snapBanks);
                  const sigsChanged =
                    hasSnap &&
                    JSON.stringify(curSigs) !== JSON.stringify(snapSigs);
                  const clientsChanged =
                    hasSnap &&
                    JSON.stringify(curClients) !== JSON.stringify(snapClients);
                  const changedSections = [
                    banksChanged && "Bank References",
                    sigsChanged && "Signatories",
                    clientsChanged && "Major Clients",
                  ].filter(Boolean) as string[];
                  const totalChanges =
                    changedGenCount +
                    changedDocKeys.length +
                    changedSections.length;
                  return (
                    <div className="border rounded-lg overflow-hidden text-xs">
                      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b">
                        <p className="font-semibold text-gray-700 text-sm">
                          Submitted Form Summary
                        </p>
                        {hasSnap && totalChanges > 0 && (
                          <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold border border-amber-300">
                            ⚠ {totalChanges} change{totalChanges > 1 ? "s" : ""}{" "}
                            detected
                          </span>
                        )}
                        {hasSnap && totalChanges === 0 && (
                          <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium border border-green-200 text-xs">
                            Resubmission — no field changes
                          </span>
                        )}
                      </div>
                      <div className="p-3 space-y-1 text-gray-600">
                        <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                          {genFields.map(([label, key]) => {
                            const cur = sel.formData.gen![key];
                            if (!cur) return null;
                            const prev = snap?.gen?.[key];
                            const changed =
                              hasSnap && prev !== undefined && cur !== prev;
                            return (
                              <div
                                key={key}
                                className={`rounded px-1.5 py-0.5 ${changed ? "bg-amber-50 border border-amber-200" : ""}`}
                              >
                                <span className="text-gray-400">{label}:</span>{" "}
                                <span
                                  className={
                                    changed
                                      ? "font-semibold text-amber-900"
                                      : ""
                                  }
                                >
                                  {key === "dateEst"
                                    ? fmtDate(cur)
                                    : key === "capital"
                                      ? fmtCurrency(cur)
                                      : cur}
                                </span>
                                {changed && prev && (
                                  <div className="text-amber-600 line-through text-xs mt-0.5">
                                    was:{" "}
                                    {key === "dateEst"
                                      ? fmtDate(prev)
                                      : key === "capital"
                                        ? fmtCurrency(prev)
                                        : prev}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        {changedSections.length > 0 && (
                          <div className="mt-2 pt-2 border-t">
                            <span className="font-semibold text-gray-700">
                              Modified Sections:
                            </span>
                            <div className="mt-1 flex flex-wrap gap-2">
                              {changedSections.map((section) => (
                                <span
                                  key={section}
                                  className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border bg-amber-50 border-amber-300 text-amber-800"
                                >
                                  <span className="font-bold">UPDATED</span>{" "}
                                  {section}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {sel.docs && Object.keys(sel.docs).length > 0 && (
                          <div className="mt-2 pt-2 border-t">
                            <span className="font-semibold text-gray-700">
                              Uploaded Documents:
                            </span>
                            <div className="mt-1 flex flex-wrap gap-2">
                              {Object.entries(
                                sel.docs as Record<string, string>,
                              ).map(([docName, val]) => {
                                const prevVal = snap?.docs?.[docName];
                                const docChanged = hasSnap && val !== prevVal;
                                const isNew = hasSnap && !prevVal;
                                return (
                                  <span
                                    key={docName}
                                    className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md border ${
                                      isNew
                                        ? "bg-green-50 border-green-300 text-green-800"
                                        : docChanged
                                          ? "bg-amber-50 border-amber-300 text-amber-800"
                                          : "bg-gray-100 border-gray-200 text-gray-700"
                                    }`}
                                  >
                                    {isNew && (
                                      <span className="font-bold">NEW</span>
                                    )}
                                    {docChanged && !isNew && (
                                      <span className="font-bold">UPDATED</span>
                                    )}
                                    {docName}
                                    {typeof val === "string" &&
                                      val.startsWith("https://") && (
                                        <button
                                          type="button"
                                          onClick={() => triggerDownload(val)}
                                          className="text-blue-600 underline font-medium"
                                        >
                                          View ↗
                                        </button>
                                      )}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

              {/* ── View Locked Accreditation Form & Requirements ──────────── */}
              {sel.formData?.gen?.companyName && (
                <div className="border border-blue-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setFormViewExpanded((p) => !p)}
                    className="w-full flex items-center justify-between px-3 py-2.5 bg-blue-50 hover:bg-blue-100 text-sm font-semibold text-blue-800 transition"
                  >
                    <span className="flex items-center gap-2">
                      <span>📋</span>
                      <span>
                        View Locked Accreditation Form &amp; Requirements
                      </span>
                      <span className="text-xs font-normal text-blue-500 bg-blue-100 px-2 py-0.5 rounded-full border border-blue-200">
                        Read-only
                      </span>
                    </span>
                    <span className="text-blue-400 text-xs">
                      {formViewExpanded ? "▲ Collapse" : "▼ Expand"}
                    </span>
                  </button>

                  {formViewExpanded &&
                    (() => {
                      // Use preModSnapshot (saved server-side at Modification Approved time) as the
                      // authoritative original. Fall back to client-captured snapshot for older records.
                      const snap =
                        sel.formData?.preModSnapshot ?? sel.formData?.snapshot;
                      const isModPending = !!snap;
                      const srcForm = isModPending ? snap : sel.formData;
                      const srcDocs = isModPending
                        ? (snap?.docs ?? sel.docs)
                        : sel.docs;
                      const g = srcForm?.gen as typeof sel.formData.gen;
                      const b = (srcForm?.banks || []) as {
                        name: string;
                        acctName: string;
                        acctNo: string;
                      }[];
                      const s = (srcForm?.sigs || []) as {
                        name: string;
                        position: string;
                        contact: string;
                        email: string;
                      }[];
                      const cl = (srcForm?.clients || []) as {
                        company: string;
                        contact: string;
                        email: string;
                      }[];
                      const fmtField = (
                        label: string,
                        value: string | undefined,
                      ) =>
                        value ? (
                          <div key={label}>
                            <span className="text-gray-400 text-xs">
                              {label}:
                            </span>{" "}
                            <span className="font-medium text-gray-800 text-xs">
                              {value}
                            </span>
                          </div>
                        ) : null;
                      return (
                        <div className="p-3 space-y-4 text-xs bg-white">
                          {isModPending && (
                            <div className="bg-amber-50 border border-amber-300 rounded-lg px-3 py-2 text-xs text-amber-800">
                              <strong>Note:</strong> Showing the{" "}
                              <strong>original approved data</strong>. The
                              supplier's pending modification is displayed in
                              the "Submitted Form Summary" above.
                            </div>
                          )}
                          {/* I. General Info */}
                          <div>
                            <p className="font-bold text-gray-700 mb-2 border-b pb-1">
                              I. General Information
                            </p>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                              {g && fmtField("Company Name", g.companyName)}
                              {g && fmtField("Business Type", g.bizType)}
                              {g && fmtField("TIN", g.tin)}
                              {g && fmtField("SEC", g.sec)}
                              {g && fmtField("Category", g.mainCat)}
                              {g &&
                                fmtField(
                                  "Sub-Category",
                                  g.subCat !== "Other"
                                    ? g.subCat
                                    : g.subCatOther || g.subCat,
                                )}
                              {g && fmtField("Address", g.address)}
                              {g && fmtField("Telephone", g.tel)}
                              {g &&
                                fmtField("Capital (₱)", fmtCurrency(g.capital))}
                              {g && fmtField("Payment Terms", g.payTerms)}
                              {g && fmtField("Years Operating", g.yearsOp)}
                              {g &&
                                fmtField(
                                  "Date Established",
                                  fmtDate(g.dateEst),
                                )}
                            </div>
                          </div>

                          {/* II. Bank References */}
                          {b.some(
                            (bk) => bk.name || bk.acctName || bk.acctNo,
                          ) && (
                            <div>
                              <p className="font-bold text-gray-700 mb-2 border-b pb-1">
                                II. Bank References
                              </p>
                              <div className="space-y-2">
                                {b.map((bk, i) =>
                                  bk.name || bk.acctName || bk.acctNo ? (
                                    <div
                                      key={i}
                                      className="border rounded p-2 bg-gray-50"
                                    >
                                      <p className="font-semibold text-gray-600 mb-1">
                                        Bank Account {i + 1}
                                      </p>
                                      <div className="grid grid-cols-3 gap-2">
                                        {fmtField("Bank Name", bk.name)}
                                        {fmtField("Account Name", bk.acctName)}
                                        {fmtField("Account No.", bk.acctNo)}
                                      </div>
                                    </div>
                                  ) : null,
                                )}
                              </div>
                            </div>
                          )}

                          {/* III. Signatories */}
                          {s.some((sg) => sg.name || sg.position) && (
                            <div>
                              <p className="font-bold text-gray-700 mb-2 border-b pb-1">
                                III. Authorized Signatories
                              </p>
                              <div className="space-y-2">
                                {s.map((sg, i) =>
                                  sg.name || sg.position ? (
                                    <div
                                      key={i}
                                      className="border rounded p-2 bg-gray-50"
                                    >
                                      <p className="font-semibold text-gray-600 mb-1">
                                        Signatory {i + 1}
                                      </p>
                                      <div className="grid grid-cols-2 gap-2">
                                        {fmtField("Full Name", sg.name)}
                                        {fmtField("Position", sg.position)}
                                        {fmtField("Contact", sg.contact)}
                                        {fmtField("Email", sg.email)}
                                      </div>
                                    </div>
                                  ) : null,
                                )}
                              </div>
                            </div>
                          )}

                          {/* IV. Top Clients */}
                          {cl.some((c) => c.company || c.contact) && (
                            <div>
                              <p className="font-bold text-gray-700 mb-2 border-b pb-1">
                                IV. Major Clients – Top 3
                              </p>
                              <div className="space-y-2">
                                {cl.map((c, i) =>
                                  c.company || c.contact ? (
                                    <div
                                      key={i}
                                      className="border rounded p-2 bg-gray-50"
                                    >
                                      <p className="font-semibold text-gray-600 mb-1">
                                        Client {i + 1}
                                      </p>
                                      <div className="grid grid-cols-3 gap-2">
                                        {fmtField("Company", c.company)}
                                        {fmtField("Contact", c.contact)}
                                        {fmtField("Email", c.email)}
                                      </div>
                                    </div>
                                  ) : null,
                                )}
                              </div>
                            </div>
                          )}

                          {/* V. Documents */}
                          {srcDocs && Object.keys(srcDocs).length > 0 && (
                            <div>
                              <p className="font-bold text-gray-700 mb-2 border-b pb-1">
                                V. Uploaded Documents
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {Object.entries(
                                  srcDocs as Record<string, string>,
                                ).map(([docName, val]) => (
                                  <span
                                    key={docName}
                                    className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border bg-gray-50 border-gray-200 text-gray-700"
                                  >
                                    {docName}
                                    {typeof val === "string" &&
                                      val.startsWith("https://") && (
                                        <button
                                          type="button"
                                          onClick={() => triggerDownload(val)}
                                          className="text-blue-600 underline"
                                        >
                                          View ↗
                                        </button>
                                      )}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                </div>
              )}

              {/* Evaluation Forms — CO can upload during CO review and when returned with remarks; CH can upload only in final review */}
              <div className="bg-white rounded-xl shadow border overflow-hidden">
                <div className="flex items-center justify-between px-5 py-3 border-b bg-gray-50">
                  <div>
                    <h3 className="font-bold text-gray-800 text-sm">
                      Evaluation Forms
                    </h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {((role === "Compliance Officer" &&
                        sel &&
                        ["Under Review", "Modification Requested", "Returned with Remarks"].includes(sel.status)) ||
                        (role === "Compliance Head" &&
                          sel &&
                          ["Recommended for Approval", "Modification Pre-Approved"].includes(sel.status)))
                        ? "Upload evaluation and assessment forms for this supplier."
                        : "Evaluation forms uploaded by compliance staff."}
                    </p>
                    {((role === "Compliance Officer" &&
                        sel &&
                        ["Under Review", "Modification Requested", "Returned with Remarks"].includes(sel.status)) ||
                      (role === "Compliance Head" &&
                        sel &&
                        ["Recommended for Approval", "Modification Pre-Approved"].includes(sel.status))) && (
                      <p className="text-xs text-gray-400 mt-0.5">
                        PDF, DOC, DOCX, XLS, XLSX • Max 25MB per file
                      </p>
                    )}
                  </div>
                  {((role === "Compliance Officer" &&
                      sel &&
                      ["Under Review", "Modification Requested", "Returned with Remarks"].includes(sel.status)) ||
                    (role === "Compliance Head" &&
                      sel &&
                      ["Recommended for Approval", "Modification Pre-Approved"].includes(sel.status))) && (
                    <>
                      <input
                        type="file"
                        id="rp-eval-upload"
                        className="hidden"
                        accept=".pdf,.doc,.docx,.xls,.xlsx"
                        disabled={evalUploading}
                        onChange={(e) => {
                          uploadEvalForm(e.target.files?.[0]);
                          e.target.value = "";
                        }}
                      />
                      <label
                        htmlFor="rp-eval-upload"
                        className={`inline-flex items-center gap-1.5 bg-indigo-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-indigo-700 cursor-pointer${evalUploading ? " opacity-50 pointer-events-none" : ""}`}
                      >
                        {evalUploading ? "Uploading…" : "+ Upload Form"}
                      </label>
                    </>
                  )}
                </div>
                {Object.keys(evalForms).length === 0 ? (
                  <div className="px-5 py-6 text-center text-xs text-gray-400">
                    {((role === "Compliance Officer" &&
                        sel &&
                        ["Under Review", "Modification Requested", "Returned with Remarks"].includes(sel.status)) ||
                      (role === "Compliance Head" &&
                        sel &&
                        ["Recommended for Approval", "Modification Pre-Approved"].includes(sel.status)))
                      ? "No evaluation forms uploaded yet. Use the button above to add one."
                      : "No evaluation forms have been uploaded yet."}
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {Object.entries(evalForms).map(([key, val]) => {
                      // Support both storage formats:
                      //   Current: { "timestamp-filename": "https://..." }  (val is the URL)
                      //   Legacy:  { "https://...": "filename" }           (key is the URL)
                      const valIsUrl =
                        typeof val === "string" && val.startsWith("https://");
                      const keyIsUrl =
                        !valIsUrl &&
                        typeof key === "string" &&
                        key.startsWith("https://");
                      const downloadUrl = valIsUrl
                        ? val
                        : keyIsUrl
                          ? key
                          : null;
                      const displayName = valIsUrl
                        ? decodeURIComponent(
                            key.replace(/^\d+-/, "").replace(/_/g, " "),
                          )
                        : keyIsUrl
                          ? decodeURIComponent(
                              (key.split("/").pop() ?? key)
                                .replace(/^\d+-/, "")
                                .replace(/_/g, " "),
                            )
                          : typeof val === "string" && val
                            ? val
                            : key;
                      return (
                        <div
                          key={key}
                          className="flex items-center justify-between px-5 py-3"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-indigo-50 border border-indigo-200 flex items-center justify-center text-indigo-600 text-xs font-bold flex-shrink-0">
                              📄
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-gray-800">
                                {displayName}
                              </p>
                              <p className="text-xs text-gray-400">
                                Evaluation Document
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            {downloadUrl ? (
                              <button
                                onClick={() => triggerDownload(downloadUrl)}
                                className="bg-blue-100 text-blue-700 px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-blue-200"
                              >
                                Download
                              </button>
                            ) : (
                              <span className="text-xs text-gray-400 italic px-3 py-1.5">
                                Unavailable
                              </span>
                            )}
                            {((role === "Compliance Officer" &&
                                sel &&
                                ["Under Review", "Modification Requested", "Returned with Remarks"].includes(sel.status)) ||
                              (role === "Compliance Head" &&
                                sel &&
                                ["Recommended for Approval", "Modification Pre-Approved"].includes(sel.status))) && (
                              <button
                                onClick={() => deleteEvalForm(key)}
                                className="bg-red-100 text-red-700 px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-red-200"
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* View-only notice for General Manager */}
              {role === "General Manager" && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                  <strong>General Manager View:</strong> You have oversight
                  access to view all supplier information, forms, and documents.
                  Actions are performed by Compliance Officers and Heads.
                </div>
              )}

              {/* Actions based on role + status */}
              {(role === "Compliance Officer" ||
                role === "Compliance Head") && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Remarks / Reasons
                  </label>
                  <textarea
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    rows={3}
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Enter remarks, reasons, or specific compliance requirements..."
                  />
                </div>
              )}

              {role === "Compliance Officer" &&
                sel.status === "Under Review" && (
                  <div className="space-y-4">
                    <div className="bg-indigo-50 border-l-4 border-indigo-500 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <div className="flex-1">
                          <h3 className="font-bold text-indigo-900 mb-1">
                            {!!sel.approvalDate ||
                            !!sel.formData?.isModificationResubmission
                              ? "Step 9: Modification Resubmission Review"
                              : "Step 5: Compliance Officer Review"}
                          </h3>
                          {!!sel.approvalDate ||
                          !!sel.formData?.isModificationResubmission ? (
                            <p className="text-sm text-indigo-800 mb-2">
                              The supplier has updated their Supplier
                              Information Form and/or documents as allowed by
                              the approved modification request. Review the
                              changes and decide the appropriate action.
                            </p>
                          ) : (
                            <>
                              <p className="text-sm text-indigo-800 mb-2">
                                As the Compliance Officer, you are responsible
                                for evaluating the Supplier Information Form and
                                all uploaded documents. Review period:{" "}
                                <strong>Five (5) working days</strong> from
                                submission date.
                              </p>
                              <div className="text-xs text-indigo-700 space-y-1">
                                <p>
                                  <strong>Review Checklist:</strong>
                                </p>
                                <ul className="ml-6 list-disc space-y-0.5">
                                  <li>
                                    Verify completeness of submitted information
                                  </li>
                                  <li>
                                    Check authenticity and validity of uploaded
                                    documents
                                  </li>
                                  <li>
                                    Assess compliance with accreditation
                                    requirements
                                  </li>
                                  <li>
                                    Determine appropriate action (approve,
                                    disapprove, or request compliance)
                                  </li>
                                </ul>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="bg-white border-2 border-gray-200 rounded-xl p-4">
                      <p className="text-sm font-bold text-gray-700 mb-3">
                        {!!sel.approvalDate ||
                        !!sel.formData?.isModificationResubmission
                          ? "Select Action:"
                          : "Select Accreditation Status:"}
                      </p>
                      <div className="space-y-2">
                        <button
                          onClick={() => {
                            const isModResub =
                              !!sel.approvalDate ||
                              !!sel.formData?.isModificationResubmission;
                            if (!remarks.trim())
                              return alert(
                                isModResub
                                  ? "Please provide your review notes before forwarding."
                                  : "Remarks Required\n\nPlease provide your evaluation remarks before recommending for approval. Include key findings from your review.",
                              );
                            if (
                              !confirm(
                                isModResub
                                  ? `Forward modification update for "${sel.supplierName}" to the Compliance Head?\n\nThe CH will review the supplier's updated information and documents.`
                                  : `Recommend "${sel.supplierName}" for approval?\n\nThis will forward the application to the Compliance Head for final certification.`,
                              )
                            )
                              return;
                            act(
                              sel.id,
                              "Recommended for Approval",
                              isModResub
                                ? `${sel.id} (${sel.supplierName}) modification update forwarded to Compliance Head for final review by Compliance Officer.`
                                : `${sel.id} (${sel.supplierName}) recommended for approval by Compliance Officer. Forwarded to Compliance Head for final certification.`,
                              {
                                daysInProcess: liveDays(sel),
                                chStartDate: TODAY,
                                ...(isModResub
                                  ? {
                                      formData: {
                                        ...sel.formData,
                                        isModificationResubmission: true,
                                      },
                                    }
                                  : {}),
                              },
                            );
                          }}
                          className="w-full bg-green-600 text-white px-4 py-3 rounded-lg text-sm font-semibold hover:bg-green-700 transition text-center"
                        >
                          {!!sel.approvalDate ||
                          !!sel.formData?.isModificationResubmission
                            ? "Forward to Compliance Head"
                            : "Recommend for Approval"}
                        </button>
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            onClick={() => {
                              if (!remarks.trim())
                                return alert(
                                  "Disapproval Reason Required\n\nPlease provide clear, detailed reasons for disapproval in the Remarks field. This will be communicated to the supplier.",
                                );
                              const isModResub =
                                !!sel.approvalDate ||
                                !!sel.formData?.isModificationResubmission;
                              if (
                                !confirm(
                                  isModResub
                                    ? `Disapprove modification update for "${sel.supplierName}"?\n\nThe supplier's updated information will be rejected. They will revert to their prior status and be notified.`
                                    : `Disapprove "${sel.supplierName}"?\n\nThis action will reject the accreditation application. The supplier will be notified with your stated reasons.`,
                                )
                              )
                                return;
                              if (isModResub) {
                                const snap =
                                  sel.formData?.preModSnapshot ??
                                  sel.formData?.snapshot;
                                act(
                                  sel.id,
                                  sel.formData?.previousStatus || "Approved",
                                  `${sel.id} (${sel.supplierName}) modification resubmission denied by Compliance Officer. Supplier reverted to prior status. Reason: ${remarks.substring(0, 50)}...`,
                                  {
                                    daysInProcess: 0,
                                    reviewStartDate: undefined,
                                    formData: {
                                      ...(snap
                                        ? {
                                            gen: snap.gen,
                                            banks: snap.banks,
                                            sigs: snap.sigs,
                                            clients: snap.clients,
                                          }
                                        : sel.formData),
                                      modResubDenied: true,
                                      modScope: undefined,
                                      snapshot: undefined,
                                      preModSnapshot: undefined,
                                      isModificationResubmission: undefined,
                                    },
                                    docs: snap?.docs ?? sel.docs,
                                  },
                                );
                              } else {
                                act(
                                  sel.id,
                                  "Disapproved",
                                  `${sel.id} (${sel.supplierName}) disapproved by Compliance Officer. Reason: ${remarks.substring(0, 50)}...`,
                                  { daysInProcess: 0, reviewStartDate: TODAY },
                                );
                              }
                            }}
                            className="bg-red-600 text-white px-4 py-3 rounded-lg text-sm font-semibold hover:bg-red-700 transition text-center"
                          >
                            {!!sel.approvalDate ||
                            !!sel.formData?.isModificationResubmission
                              ? "Disapprove Modification Update"
                              : "Disapprove Application"}
                          </button>
                          <button
                            onClick={() => {
                              if (!remarks.trim())
                                return alert(
                                  "Compliance Requirements Missing\n\nPlease specify what documents, corrections, or additional information the supplier needs to provide in the Remarks field.",
                                );
                              if (
                                !confirm(
                                  `Mark "${sel.supplierName}" as For Compliance?\n\nThe supplier will be notified of required corrections/submissions and can resubmit.`,
                                )
                              )
                                return;
                              act(
                                sel.id,
                                "For Compliance",
                                `${sel.id} (${sel.supplierName}) marked For Compliance. Supplier notified of required actions.`,
                                { daysInProcess: 0, reviewStartDate: TODAY },
                              );
                            }}
                            className="bg-yellow-500 text-white px-4 py-3 rounded-lg text-sm font-semibold hover:bg-yellow-600 transition text-center"
                          >
                            For Compliance with Remarks
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-600">
                      {!!sel.approvalDate ||
                      !!sel.formData?.isModificationResubmission ? (
                        <>
                          <strong>Modification Review Guidelines:</strong>
                          <ul className="ml-4 list-disc mt-1 space-y-0.5">
                            <li>
                              <strong>Forward to Compliance Head:</strong>{" "}
                              Updated information is complete and acceptable;
                              changes are within the approved modification scope
                            </li>
                            <li>
                              <strong>For Compliance:</strong> Updated info has
                              gaps or missing documents that need supplier
                              correction before forwarding
                            </li>
                            <li>
                              <strong>Disapprove Modification Update:</strong>{" "}
                              Updated information has critical issues or the
                              changes are unacceptable
                            </li>
                          </ul>
                        </>
                      ) : (
                        <>
                          <strong>Review Guidelines:</strong>
                          <ul className="ml-4 list-disc mt-1 space-y-0.5">
                            <li>
                              <strong>Recommended for Approval:</strong> All
                              documents valid, information complete, meets all
                              requirements
                            </li>
                            <li>
                              <strong>For Compliance:</strong> Missing
                              documents, expired permits, incomplete info, or
                              minor corrections needed
                            </li>
                            <li>
                              <strong>Disapprove:</strong> Fraudulent documents,
                              major violations, fails to meet minimum
                              requirements, or duplicate registration
                            </li>
                          </ul>
                        </>
                      )}
                    </div>
                  </div>
                )}

              {role === "Compliance Officer" &&
                sel.status === "Returned with Remarks" && (
                  <div className="space-y-4">
                    <div className="bg-rose-50 border-l-4 border-rose-500 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <div className="flex-1">
                          <h3 className="font-bold text-rose-900 mb-1">
                            Returned by Compliance Head
                          </h3>
                          <p className="text-sm text-rose-800 mb-2">
                            The Compliance Head has returned this application
                            with remarks. Review the remarks below and decide
                            the next course of action.
                          </p>
                          {sel.remarks && (
                            <div className="bg-white border border-rose-200 rounded-lg p-3 mt-3">
                              <p className="text-xs font-semibold text-rose-700 mb-1">
                                Compliance Head's Remarks:
                              </p>
                              <p className="text-sm text-rose-900 italic">
                                {sel.remarks}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="bg-white border-2 border-gray-200 rounded-xl p-4">
                      <p className="text-sm font-bold text-gray-700 mb-3">
                        Select Next Action:
                      </p>
                      <div className="space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            onClick={() => {
                              if (!remarks.trim())
                                return alert(
                                  "Please provide compliance requirements in the Remarks field before forwarding to the supplier.",
                                );
                              if (
                                !confirm(
                                  `Forward \"${sel.supplierName}\" to the Supplier Company for corrections?\n\nThe supplier will be notified and asked to address the remarks, then resubmit.`,
                                )
                              )
                                return;
                              act(
                                sel.id,
                                "For Compliance",
                                `${sel.id} (${sel.supplierName}) forwarded to Supplier Company for corrections by Compliance Officer (after Compliance Head return).\n\nRemarks: ${remarks}`,
                                { daysInProcess: 0, reviewStartDate: TODAY },
                              );
                            }}
                            className="bg-yellow-500 text-white px-4 py-3 rounded-lg text-sm font-semibold hover:bg-yellow-600 transition text-center"
                          >
                            Forward to Supplier for Corrections
                          </button>
                          <button
                            onClick={() => {
                              const isModResub =
                                !!sel.approvalDate ||
                                !!sel.formData?.isModificationResubmission;
                              if (!remarks.trim())
                                return alert(
                                  isModResub
                                    ? "Please provide your updated review notes before re-forwarding."
                                    : "Please provide updated evaluation remarks before re-recommending.",
                                );
                              if (
                                !confirm(
                                  isModResub
                                    ? `Re-forward modification update for "${sel.supplierName}" to the Compliance Head?\n\nThe CH will review the supplier's updated information again.`
                                    : `Re-recommend "${sel.supplierName}" for approval?\n\nThis will forward the application back to the Compliance Head for final certification.`,
                                )
                              )
                                return;
                              act(
                                sel.id,
                                "Recommended for Approval",
                                isModResub
                                  ? `${sel.id} (${sel.supplierName}) modification update re-forwarded to Compliance Head for review by Compliance Officer (after Compliance Head return).\n\nUpdated Remarks: ${remarks}`
                                  : `${sel.id} (${sel.supplierName}) re-recommended for approval by Compliance Officer (after Compliance Head return).\n\nUpdated Remarks: ${remarks}`,
                                {
                                  daysInProcess: liveDays(sel),
                                  chStartDate: TODAY,
                                  ...(isModResub
                                    ? {
                                        formData: {
                                          ...sel.formData,
                                          isModificationResubmission: true,
                                        },
                                      }
                                    : {}),
                                },
                              );
                            }}
                            className="bg-green-600 text-white px-4 py-3 rounded-lg text-sm font-semibold hover:bg-green-700 transition text-center"
                          >
                            {!!sel.approvalDate ||
                            !!sel.formData?.isModificationResubmission
                              ? "Re-Forward to Compliance Head"
                              : "Re-Recommend for Approval"}
                          </button>
                        </div>
                        <button
                          onClick={() => {
                            const isModResub =
                              !!sel.approvalDate ||
                              !!sel.formData?.isModificationResubmission;
                            if (!remarks.trim())
                              return alert(
                                "Please provide disapproval reasons in the Remarks field.",
                              );
                            if (
                              !confirm(
                                isModResub
                                  ? `Disapprove modification update for "${sel.supplierName}"?\n\nThe supplier's updated information will be rejected. They will revert to their prior status.`
                                  : `Disapprove "${sel.supplierName}"?\n\nThis will reject the accreditation application. The supplier will be notified.`,
                              )
                            )
                              return;
                            if (isModResub) {
                              const snap =
                                sel.formData?.preModSnapshot ??
                                sel.formData?.snapshot;
                              act(
                                sel.id,
                                sel.formData?.previousStatus || "Approved",
                                `${sel.id} (${sel.supplierName}) modification resubmission denied by Compliance Officer (after Compliance Head return). Supplier reverted to prior status. Reason: ${remarks.substring(0, 50)}...`,
                                {
                                  daysInProcess: 0,
                                  reviewStartDate: undefined,
                                  formData: {
                                    ...(snap
                                      ? {
                                          gen: snap.gen,
                                          banks: snap.banks,
                                          sigs: snap.sigs,
                                          clients: snap.clients,
                                        }
                                      : sel.formData),
                                    modResubDenied: true,
                                    modScope: undefined,
                                    snapshot: undefined,
                                    preModSnapshot: undefined,
                                    isModificationResubmission: undefined,
                                  },
                                  docs: snap?.docs ?? sel.docs,
                                },
                              );
                            } else {
                              act(
                                sel.id,
                                "Disapproved",
                                `${sel.id} (${sel.supplierName}) disapproved by Compliance Officer (after Compliance Head return). Reason: ${remarks.substring(0, 50)}...`,
                                { daysInProcess: 0, reviewStartDate: TODAY },
                              );
                            }
                          }}
                          className="w-full bg-red-600 text-white px-4 py-3 rounded-lg text-sm font-semibold hover:bg-red-700 transition text-center"
                        >
                          {!!sel.approvalDate ||
                          !!sel.formData?.isModificationResubmission
                            ? "Disapprove Modification Update"
                            : "Disapprove Application"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

              {role === "Compliance Officer" &&
                sel.status === "Modification Requested" &&
                (sel.status === "Modification Requested" ? (
                  <div className="space-y-3">
                    <div className="bg-teal-50 border-l-4 border-teal-600 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <div className="flex-1">
                          <h3 className="font-bold text-teal-900 text-sm">
                            Step 9: Modification Pre-Approval
                          </h3>
                          <p className="text-xs text-teal-800 mt-1">
                            The Requesting Department has endorsed a
                            modification request. Review the reason and decide
                            whether to pre-approve.
                          </p>
                          {sel.formData?.modScope && (
                            <div className="bg-teal-100 border border-teal-300 rounded-lg px-3 py-2 mt-2">
                              <p className="text-xs font-semibold text-teal-800">
                                Scope:{" "}
                                <span className="font-bold">
                                  {sel.formData.modScope === "form"
                                    ? "Form / Profile Only"
                                    : sel.formData.modScope === "documents"
                                      ? "Documents Only"
                                      : "Both Form & Documents"}
                                </span>
                              </p>
                            </div>
                          )}
                          {sel.remarks && (
                            <div className="bg-white border border-teal-200 rounded-lg p-3 mt-3">
                              <p className="text-xs font-semibold text-teal-700 mb-1">
                                Modification Request Reason:
                              </p>
                              <p className="text-xs text-teal-900 italic">
                                {sel.remarks}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => {
                          if (!remarks.trim()) {
                            alert(
                              "Please provide a reason in the Remarks field before denying.",
                            );
                            return;
                          }
                          if (
                            !confirm(
                              `Deny modification request for "${sel.supplierName}"?\n\nThe supplier will remain in its prior status and cannot modify their information.`,
                            )
                          )
                            return;
                          act(
                            sel.id,
                            sel.formData?.previousStatus || "Approved",
                            `STEP 9: MODIFICATION REQUEST DENIED\n\n${sel.id} (${sel.supplierName}) - Modification request denied by Compliance Officer.\n\nNotifications sent to:\n  - Requesting Department\n  - Supplier Company (${sel.supplierName})\n  - ECPG Compliance Officer`,
                            {
                              remarks: "",
                              reviewStartDate: TODAY,
                              daysInProcess: 0,
                              formData: {
                                ...sel.formData,
                                modScope: undefined,
                                modDenied: true,
                              },
                            },
                          );
                        }}
                        className="bg-red-600 text-white px-4 py-3 rounded-lg text-sm font-semibold hover:bg-red-700 transition text-center"
                      >
                        Deny Modification Request
                      </button>
                      <button
                        onClick={() => {
                          if (
                            !confirm(
                              `Pre-approve modification request for "${sel.supplierName}"?\n\nThis will forward the request to the Compliance Head for final approval.`,
                            )
                          )
                            return;
                          act(
                            sel.id,
                            "Modification Pre-Approved",
                            `STEP 9: PRE-APPROVAL GRANTED\n\n${sel.id} (${sel.supplierName}) - Modification request pre-approved by Compliance Officer.\n\nForwarded to Compliance Head for final approval.`,
                            { chStartDate: TODAY },
                          );
                        }}
                        className="bg-teal-600 text-white px-4 py-3 rounded-lg text-sm font-semibold hover:bg-teal-700 transition text-center"
                      >
                        Pre-Approve Modification
                      </button>
                    </div>
                  </div>
                ) : null)}

              {role === "Compliance Officer" &&
                ![
                  "Under Review",
                  "Returned with Remarks",
                  "Modification Requested",
                ].includes(sel.status) && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-600">
                    {sel.status === "Enrolled" ||
                    sel.status === "Form Completion" ? (
                      <>
                        <span className="font-semibold text-gray-700">
                          Awaiting Supplier Submission
                        </span>
                        <p className="mt-1 text-xs">
                          The supplier has not yet submitted their application.
                          Actions will be available once the supplier completes
                          and submits their form.
                        </p>
                      </>
                    ) : sel.status === "Recommended for Approval" ? (
                      <>
                        <span className="font-semibold text-gray-700">
                          Forwarded to Compliance Head
                        </span>
                        <p className="mt-1 text-xs">
                          You have already recommended this application for
                          approval. It is now awaiting final certification by
                          the Compliance Head.
                        </p>
                      </>
                    ) : sel.status === "For Compliance" ? (
                      <>
                        <span className="font-semibold text-gray-700">
                          Awaiting Supplier Re-submission
                        </span>
                        <p className="mt-1 text-xs">
                          You have requested compliance corrections. Actions
                          will be available again once the supplier re-submits
                          their application.
                        </p>
                      </>
                    ) : sel.status === "Approved" ? (
                      <>
                        <span className="font-semibold text-gray-700">
                          Application Approved
                        </span>
                        <p className="mt-1 text-xs">
                          This supplier has been fully accredited. No further
                          actions are required.
                        </p>
                      </>
                    ) : sel.status === "Disapproved" ? (
                      <>
                        <span className="font-semibold text-gray-700">
                          Application Disapproved
                        </span>
                        <p className="mt-1 text-xs">
                          This application was disapproved. The Requesting
                          Department may enroll a new application if needed.
                        </p>
                      </>
                    ) : sel.status === "Modification Pre-Approved" ? (
                      <>
                        <span className="font-semibold text-gray-700">
                          Modification Awaiting Compliance Head
                        </span>
                        <p className="mt-1 text-xs">
                          You have pre-approved the modification request. It is
                          now awaiting final approval from the Compliance Head.
                        </p>
                      </>
                    ) : sel.status === "Modification Endorsement Pending" ? (
                      <>
                        <span className="font-semibold text-gray-700">
                          Awaiting Requesting Department Endorsement
                        </span>
                        <p className="mt-1 text-xs">
                          The supplier has submitted a modification request. The
                          Requesting Department must first endorse it before you
                          can take action.
                        </p>
                      </>
                    ) : sel.status === "Modification Approved" ? null : (
                      <>
                        <span className="font-semibold text-gray-700">
                          No Actions Available
                        </span>
                        <p className="mt-1 text-xs">
                          No actions are available for the current status:{" "}
                          <strong>{sel.status}</strong>.
                        </p>
                      </>
                    )}
                  </div>
                )}

              {role === "Compliance Head" &&
                sel.status === "Recommended for Approval" && (
                  <div className="space-y-3">
                    <div className="bg-cyan-50 border-l-4 border-cyan-600 rounded-lg p-4 space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="flex-1">
                          <h3 className="font-bold text-cyan-900 text-sm">
                            {!!sel.approvalDate ||
                            !!sel.formData?.isModificationResubmission
                              ? "Step 9: Modified Application Review"
                              : "Step 7: Compliance Head Final Review"}
                          </h3>
                          {!!sel.approvalDate ||
                          !!sel.formData?.isModificationResubmission ? (
                            <p className="text-xs text-cyan-800 mt-1">
                              Review the supplier's updated information and
                              documents submitted after the modification request
                              was approved. Approve to confirm the changes.
                            </p>
                          ) : (
                            <>
                              <p className="text-xs text-cyan-800 mt-1">
                                As the Compliance Head, you provide final
                                certification review. Review period:{" "}
                                <strong>Five (5) working days</strong> from
                                Compliance Officer recommendation.
                              </p>
                              <ul className="text-xs text-cyan-800 mt-2 space-y-1 ml-4 list-disc">
                                <li>
                                  Verify Compliance Officer's evaluation and
                                  recommendation
                                </li>
                                <li>
                                  Review complete supplier information and
                                  documentation
                                </li>
                                <li>Determine final accreditation decision</li>
                              </ul>
                            </>
                          )}
                        </div>
                      </div>

                      {acting ? (
                        <div className="flex items-center justify-center gap-3 py-6 text-blue-700">
                          <div className="w-5 h-5 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
                          <span className="text-sm font-medium">
                            Processing action, please wait…
                          </span>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            <button
                              onClick={() => {
                                if (!remarks.trim()) {
                                  alert(
                                    "Please provide disapproval reasons in the Remarks field before rejecting.",
                                  );
                                  return;
                                }
                                const isModResub =
                                  !!sel.approvalDate ||
                                  !!sel.formData?.isModificationResubmission;
                                if (
                                  !confirm(
                                    isModResub
                                      ? `Disapprove modification update for "${sel.supplierName}"?\n\nThe supplier's updated information will be rejected. They will revert to their prior status and be notified.`
                                      : `Disapprove "${sel.supplierName}"?\n\nThis action is the final rejection of the accreditation application. The supplier will be notified with your stated reasons.`,
                                  )
                                )
                                  return;
                                if (isModResub) {
                                  const snap =
                                    sel.formData?.preModSnapshot ??
                                    sel.formData?.snapshot;
                                  act(
                                    sel.id,
                                    sel.formData?.previousStatus || "Approved",
                                    `STEP 8: MODIFICATION RESUBMISSION DENIED\n\n${sel.id} (${sel.supplierName}) - Modification resubmission denied by Compliance Head. Supplier reverted to prior status.\n\nReason: ${remarks.substring(0, 80)}${remarks.length > 80 ? "..." : ""}\n\nNotifications sent to:\n  - Requesting Department\n  - Supplier Company (${sel.supplierName})\n  - ECPG Compliance Officer`,
                                    {
                                      daysInProcess: 0,
                                      reviewStartDate: undefined,
                                      formData: {
                                        ...(snap
                                          ? {
                                              gen: snap.gen,
                                              banks: snap.banks,
                                              sigs: snap.sigs,
                                              clients: snap.clients,
                                            }
                                          : sel.formData),
                                        modResubDenied: true,
                                        modScope: undefined,
                                        snapshot: undefined,
                                        preModSnapshot: undefined,
                                        isModificationResubmission: undefined,
                                      },
                                      docs: snap?.docs ?? sel.docs,
                                    },
                                  );
                                } else {
                                  act(
                                    sel.id,
                                    "Disapproved",
                                    `STEP 8: FINAL NOTIFICATION\n\n${sel.id} (${sel.supplierName}) disapproved by Compliance Head.\n\nFinal Decision: Application rejected.\nReason: ${remarks.substring(0, 80)}${remarks.length > 80 ? "..." : ""}\n\nNotifications sent to:\n  - Requesting Department\n  - Supplier Company (${sel.supplierName})\n  - ECPG Compliance Officer`,
                                    {
                                      daysInProcess: 0,
                                      reviewStartDate: TODAY,
                                    },
                                  );
                                }
                              }}
                              className="bg-red-600 text-white px-4 py-3 rounded-lg text-sm font-semibold hover:bg-red-700 transition text-center"
                            >
                              {!!sel.approvalDate ||
                              !!sel.formData?.isModificationResubmission
                                ? "Disapprove Modification Update"
                                : "Disapprove Application"}
                            </button>
                            <button
                              onClick={() => {
                                const isModResub =
                                  !!sel.approvalDate ||
                                  !!sel.formData?.isModificationResubmission;
                                if (!remarks.trim()) {
                                  alert(
                                    isModResub
                                      ? "Please provide review notes in the Remarks field before approving."
                                      : "Please provide certification notes in the Remarks field before approving.",
                                  );
                                  return;
                                }
                                if (
                                  !confirm(
                                    isModResub
                                      ? `Approve modified application for "${sel.supplierName}"?\n\nThe supplier's updated information will be officially accepted and the accreditation record updated. All relevant parties will be notified.`
                                      : `Certify and Approve "${sel.supplierName}"?\n\nThis action grants full accreditation certification. The supplier will be officially accredited and notified of approval.`,
                                  )
                                )
                                  return;
                                act(
                                  sel.id,
                                  "Approved",
                                  isModResub
                                    ? `STEP 9: MODIFIED APPLICATION APPROVED\n\n${sel.id} (${sel.supplierName}) - Updated information approved by Compliance Head on ${TODAY}.\n\nNotifications sent to:\n  - Requesting Department\n  - Supplier Company (${sel.supplierName})\n  - ECPG Compliance Officer`
                                    : `STEP 8: FINAL NOTIFICATION & CERTIFICATION\n\n${sel.id} (${sel.supplierName}) certified and approved by Compliance Head. Accreditation granted on ${TODAY}.\n\nNotifications sent to:\n  - Requesting Department\n  - Supplier Company (${sel.supplierName})\n  - ECPG Compliance Officer\n  - ECPG Compliance Head\n\nAccreditation Certificate generated and available for download.`,
                                  {
                                    slaStatus: "on-time",
                                    ...(!isModResub
                                      ? { approvalDate: TODAY }
                                      : {}),
                                    reviewStartDate: TODAY,
                                    daysInProcess: 0,
                                    ...(isModResub &&
                                    sel.formData?.gen?.companyName
                                      ? {
                                          supplierName:
                                            sel.formData.gen.companyName,
                                        }
                                      : {}),
                                    formData: {
                                      ...sel.formData,
                                      modScope: undefined,
                                      snapshot: undefined,
                                      preModSnapshot: undefined,
                                      ...(isModResub
                                        ? {}
                                        : {
                                            isModificationResubmission:
                                              undefined,
                                          }),
                                    },
                                  },
                                );
                              }}
                              className="bg-green-700 text-white px-4 py-3 rounded-lg text-sm font-semibold hover:bg-green-800 transition text-center"
                            >
                              {!!sel.approvalDate ||
                              !!sel.formData?.isModificationResubmission
                                ? "Approve Modified Application"
                                : "Certify & Approve Application"}
                            </button>
                          </div>
                          <button
                            onClick={() => {
                              if (!remarks.trim()) {
                                alert(
                                  "Please specify reasons for returning to Compliance Officer in the Remarks field.",
                                );
                                return;
                              }
                              const isModResub =
                                !!sel.approvalDate ||
                                !!sel.formData?.isModificationResubmission;
                              if (
                                !confirm(
                                  isModResub
                                    ? `Return "${sel.supplierName}" to Compliance Officer?\n\nThe modification update will be sent back to the CO for further review. The CO can request corrections from the supplier or re-forward the update.`
                                    : `Return "${sel.supplierName}" to Compliance Officer?\n\nThis application will be sent back to the Compliance Officer for further review. The CO can then forward it to the Supplier Company for corrections or re-recommend it.`,
                                )
                              )
                                return;
                              act(
                                sel.id,
                                "Returned with Remarks",
                                isModResub
                                  ? `STEP 9: RETURNED WITH REMARKS\n\n${sel.id} (${sel.supplierName}) modification update returned by Compliance Head to Compliance Officer.\n\nAction Required: CO to review remarks and decide next action.\n\nRemarks: ${remarks}`
                                  : `STEP 8: RETURNED WITH REMARKS\n\n${sel.id} (${sel.supplierName}) returned by Compliance Head to Compliance Officer.\n\nAction Required: Compliance Officer to review remarks and decide next action (forward to supplier for corrections or re-recommend).\n\nRemarks: ${remarks}`,
                                {
                                  daysInProcess: 0,
                                  reviewStartDate: TODAY,
                                  ...(isModResub
                                    ? {
                                        formData: {
                                          ...sel.formData,
                                          isModificationResubmission: true,
                                        },
                                      }
                                    : {}),
                                },
                              );
                            }}
                            className="w-full bg-gray-600 text-white px-4 py-3 rounded-lg text-sm font-semibold hover:bg-gray-700 transition text-center"
                          >
                            Return to Compliance Officer
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-600">
                      {!!sel.approvalDate ||
                      !!sel.formData?.isModificationResubmission ? (
                        <>
                          <strong>Modification Review Guidelines:</strong>
                          <ul className="ml-4 list-disc mt-1 space-y-0.5">
                            <li>
                              <strong>Approve Modified Application:</strong>{" "}
                              Updated information is accurate, complete, and
                              changes are acceptable
                            </li>
                            <li>
                              <strong>Disapprove Modification Update:</strong>{" "}
                              Updated information has critical issues or changes
                              are unacceptable; supplier reverts to Approved
                            </li>
                            <li>
                              <strong>Return to CO:</strong> Need further
                              verification or clarification on the supplier's
                              updates before deciding
                            </li>
                          </ul>
                        </>
                      ) : (
                        <>
                          <strong>Final Review Guidelines:</strong>
                          <ul className="ml-4 list-disc mt-1 space-y-0.5">
                            <li>
                              <strong>Certify & Approve:</strong> CO evaluation
                              is thorough, all requirements met, documentation
                              complete and valid, supplier qualifies for
                              accreditation
                            </li>
                            <li>
                              <strong>Disapprove:</strong> Material concerns
                              found in final review, does not meet certification
                              standards, or critical issues identified
                            </li>
                            <li>
                              <strong>Return to CO:</strong> Need additional
                              verification, clarification on CO's assessment, or
                              further investigation required
                            </li>
                          </ul>
                        </>
                      )}
                    </div>
                  </div>
                )}

              {role === "Compliance Head" &&
                ["Disapproved", "Approved"].includes(sel.status) &&
                !acting && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-600">
                    {sel.status === "Disapproved" ? (
                      <>
                        <span className="font-semibold text-red-700">
                          Application Disapproved
                        </span>
                        <p className="mt-1 text-xs">
                          Your decision has been recorded. The supplier has been
                          notified of the rejection.
                        </p>
                      </>
                    ) : sel.formData?.modResubDenied ? (
                      <>
                        <span className="font-semibold text-orange-700">
                          Modification Resubmission Denied
                        </span>
                        <p className="mt-1 text-xs">
                          The supplier has been reverted to their prior status.
                          They may submit a new modification request if needed.
                        </p>
                      </>
                    ) : sel.formData?.isModificationResubmission ? (
                      <>
                        <span className="font-semibold text-green-700">
                          Modification Update Approved
                        </span>
                        <p className="mt-1 text-xs">
                          The supplier's updated information has been approved.
                          The accreditation record has been updated and all
                          parties notified.
                        </p>
                      </>
                    ) : (
                      <>
                        <span className="font-semibold text-green-700">
                          Application Approved
                        </span>
                        <p className="mt-1 text-xs">
                          The supplier has been certified and accredited.
                          Notifications have been sent to all relevant parties.
                        </p>
                      </>
                    )}
                  </div>
                )}

              {role === "Compliance Head" &&
                sel.status === "Modification Pre-Approved" && (
                  <div className="space-y-3">
                    <div className="bg-emerald-50 border-l-4 border-emerald-600 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <div className="flex-1">
                          <h3 className="font-bold text-emerald-900 text-sm">
                            Step 9: Final Modification Approval
                          </h3>
                          <p className="text-xs text-emerald-800 mt-1">
                            This modification request has been pre-approved by
                            the Compliance Officer. Grant final approval to
                            allow the supplier to edit their information.
                          </p>
                          {sel.formData?.modScope && (
                            <div className="bg-emerald-100 border border-emerald-300 rounded-lg px-3 py-2 mt-2">
                              <p className="text-xs font-semibold text-emerald-800">
                                Scope:{" "}
                                <span className="font-bold">
                                  {sel.formData.modScope === "form"
                                    ? "Form / Profile Only"
                                    : sel.formData.modScope === "documents"
                                      ? "Documents Only"
                                      : "Both Form & Documents"}
                                </span>
                              </p>
                            </div>
                          )}
                          {sel.remarks && (
                            <div className="bg-white border border-emerald-200 rounded-lg p-3 mt-3">
                              <p className="text-xs font-semibold text-emerald-700 mb-1">
                                Modification Request Reason:
                              </p>
                              <p className="text-xs text-emerald-900 italic">
                                {sel.remarks}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => {
                          if (!remarks.trim()) {
                            alert(
                              "Please provide a reason in the Remarks field before denying.",
                            );
                            return;
                          }
                          if (
                            !confirm(
                              `Deny modification request for "${sel.supplierName}"?\n\nThe supplier will remain in its prior status and cannot modify their information.`,
                            )
                          )
                            return;
                          act(
                            sel.id,
                            sel.formData?.previousStatus || "Approved",
                            `STEP 9: MODIFICATION REQUEST DENIED BY COMPLIANCE HEAD\n\n${sel.id} (${sel.supplierName}) - Modification request denied by Compliance Head.\n\nReason: ${remarks.substring(0, 80)}${remarks.length > 80 ? "..." : ""}\n\nThe supplier remains in its prior status.\n\nNotifications sent to:\n  - Requesting Department\n  - Supplier Company (${sel.supplierName})\n  - ECPG Compliance Officer`,
                            {
                              remarks: "",
                              reviewStartDate: TODAY,
                              daysInProcess: 0,
                              formData: {
                                ...sel.formData,
                                modScope: undefined,
                                modDenied: true,
                              },
                            },
                          );
                        }}
                        className="bg-red-600 text-white px-4 py-3 rounded-lg text-sm font-semibold hover:bg-red-700 transition text-center"
                      >
                        Deny Modification Request
                      </button>
                      <button
                        onClick={() => {
                          const scope = sel.formData?.modScope;
                          const scopeLabel =
                            scope === "form"
                              ? "Form / Profile Only"
                              : scope === "documents"
                                ? "Documents Only"
                                : "Both Form & Documents";
                          const scopeDesc =
                            scope === "form"
                              ? "update their Supplier Information Form"
                              : scope === "documents"
                                ? "upload new documents"
                                : "update their Supplier Information Form and upload new documents";
                          if (
                            !confirm(
                              `Approve modification for "${sel.supplierName}"?\n\nScope: ${scopeLabel}\n\nThe supplier will be allowed to ${scopeDesc}. After re-submission, the process will follow Steps 5-8 again.`,
                            )
                          )
                            return;
                          act(
                            sel.id,
                            "Modification Approved",
                            `STEP 9: MODIFICATION APPROVED\n\n${sel.id} (${sel.supplierName}) - Modification request approved by Compliance Head.\n\nScope: ${scopeLabel}\n\nNotifications sent to:\n  - Supplier Company (${sel.supplierName}) - You may now ${scopeDesc}\n  - Requesting Department\n  - ECPG Compliance Officer\n\nAfter re-submission, the application will undergo Steps 5-8 review process again.`,
                            {
                              remarks: "",
                              reviewStartDate: TODAY,
                              daysInProcess: 0,
                            },
                          );
                        }}
                        className="bg-green-700 text-white px-4 py-3 rounded-lg text-sm font-semibold hover:bg-green-800 transition text-center"
                      >
                        Approve Modification Request
                      </button>
                    </div>
                  </div>
                )}

              {role === "Requesting Department" &&
                sel.status === "Approved" &&
                sel.formData?.modDenied !== true && (
                  <div className="space-y-3 pt-4 border-t">
                    <div className="bg-indigo-50 border-l-4 border-indigo-500 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <div className="flex-1">
                          <h3 className="font-bold text-indigo-900 text-sm">
                            Step 9: Request Modification
                          </h3>
                          <p className="text-xs text-indigo-800 mt-1">
                            The supplier's forms and documents are now{" "}
                            <strong>locked</strong>. To allow modifications, you
                            must submit a request with the reason.
                          </p>
                          <ul className="text-xs text-indigo-800 mt-2 space-y-1 ml-4 list-disc">
                            <li>
                              Your request will be reviewed by the Compliance
                              Officer
                            </li>
                            <li>
                              If pre-approved by CO, it will then go to
                              Compliance Head for final approval
                            </li>
                            <li>
                              Once approved, the supplier can modify their
                              information and documents
                            </li>
                            <li>
                              After re-submission, the process follows Steps 5-8
                              again
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    <textarea
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                      rows={3}
                      value={modReason}
                      onChange={(e) => setModReason(e.target.value)}
                      placeholder="Describe what needs to be modified and the reason for this request..."
                    />
                    <div className="mb-2">
                      <label className="block text-xs font-semibold text-indigo-900 mb-1.5">
                        Modification Scope
                      </label>
                      <div className="flex gap-2 flex-wrap">
                        {(
                          [
                            ["form", "Form / Profile Only"],
                            ["documents", "Documents Only"],
                            ["both", "Both Form & Documents"],
                          ] as const
                        ).map(([v, l]) => (
                          <button
                            key={v}
                            type="button"
                            onClick={() => setRdModScope(v)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${rdModScope === v ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-gray-600 border-gray-300 hover:border-indigo-400"}`}
                          >
                            {l}
                          </button>
                        ))}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        if (!modReason.trim())
                          return alert(
                            "Please provide a detailed reason for the modification request.",
                          );
                        const sl =
                          rdModScope === "form"
                            ? "Form Only"
                            : rdModScope === "documents"
                              ? "Documents Only"
                              : "Both Form & Documents";
                        act(
                          sel.id,
                          "Modification Endorsement Pending",
                          `STEP 9: MODIFICATION REQUEST SUBMITTED\n\n${sel.id} (${sel.supplierName}) - Modification request submitted by Requesting Department.\n\nScope: ${sl}\nReason: ${modReason}\n\nNext Step: Requesting Department must endorse this request for Compliance Officer review.`,
                          {
                            remarks: modReason,
                            formData: {
                              ...sel.formData,
                              modScope: rdModScope,
                              modDenied: false,
                              previousStatus: sel.status,
                            },
                            reviewStartDate: TODAY,
                            daysInProcess: countWorkingDays(TODAY),
                          },
                        );
                        setModReason("");
                        setRdModScope("both");
                      }}
                      className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-indigo-700"
                    >
                      Submit Modification Request
                    </button>
                  </div>
                )}

              {role === "Requesting Department" &&
                sel.status === "Modification Endorsement Pending" && (
                  <div className="space-y-3 pt-4 border-t">
                    <div className="bg-amber-50 border-l-4 border-amber-500 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <div className="flex-1">
                          <h3 className="font-bold text-amber-900 text-sm">
                            Step 9: Endorse Modification Request
                          </h3>
                          <p className="text-xs text-amber-800 mt-1">
                            The supplier has submitted a modification request.
                            Review the reason below and endorse it to the
                            Compliance Officer for pre-approval, or reject it.
                          </p>
                          {sel.formData?.modScope && (
                            <div className="bg-amber-100 border border-amber-300 rounded-lg px-3 py-2 mt-2">
                              <p className="text-xs font-semibold text-amber-800">
                                Requested Scope:{" "}
                                <span className="font-bold">
                                  {sel.formData.modScope === "form"
                                    ? "Form / Profile Only"
                                    : sel.formData.modScope === "documents"
                                      ? "Documents Only"
                                      : "Both Form & Documents"}
                                </span>
                              </p>
                            </div>
                          )}
                          {sel.remarks && (
                            <div className="bg-white border border-amber-200 rounded-lg p-3 mt-3">
                              <p className="text-xs font-semibold text-amber-900 mb-1">
                                Supplier's Reason for Modification:
                              </p>
                              <p className="text-xs text-amber-900 italic">
                                {sel.remarks}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Remarks / Reason for Action
                      </label>
                      <textarea
                        className="w-full border rounded-lg px-3 py-2 text-sm mb-3"
                        rows={3}
                        value={remarks}
                        onChange={(e) => setRemarks(e.target.value)}
                        placeholder="Enter your reason for endorsing or rejecting this modification request..."
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => {
                          if (!remarks.trim()) {
                            alert(
                              "Please provide a reason in the Remarks field before rejecting.",
                            );
                            return;
                          }
                          if (
                            !confirm(
                              `Reject this modification request for "${sel.supplierName}"?\n\nThe supplier will remain in its prior status.`,
                            )
                          )
                            return;
                          act(
                            sel.id,
                            sel.formData?.previousStatus || "Approved",
                            `STEP 9: MODIFICATION REQUEST REJECTED\n\n${sel.id} (${sel.supplierName}) - Modification request rejected by Requesting Department.\n\nReason: ${remarks}\n\nThe supplier remains in its prior status.\n\nNotifications sent to:\n  - Supplier Company (${sel.supplierName})\n  - Requesting Department\n  - ECPG Compliance Officer`,
                            {
                              remarks: "",
                              reviewStartDate: TODAY,
                              daysInProcess: 0,
                              formData: {
                                ...sel.formData,
                                modScope: undefined,
                                modDenied: true,
                              },
                            },
                          );
                        }}
                        className="bg-red-600 text-white px-4 py-3 rounded-lg text-sm font-semibold hover:bg-red-700 transition text-center"
                      >
                        Reject Modification Request
                      </button>
                      <button
                        onClick={() => {
                          if (
                            !confirm(
                              `Endorse modification request for "${sel.supplierName}" to the Compliance Officer?\n\nThe CO will review this request for pre-approval.`,
                            )
                          )
                            return;
                          act(
                            sel.id,
                            "Modification Requested",
                            `STEP 9: MODIFICATION REQUEST ENDORSED\n\n${sel.id} (${sel.supplierName}) - Modification request endorsed by Requesting Department.\n\nReason: ${sel.remarks || "(no reason provided)"}\n\nNext Step: Compliance Officer will review for pre-approval.`,
                            {
                              remarks: sel.remarks || "",
                              reviewStartDate: TODAY,
                              daysInProcess: 0,
                            },
                          );
                        }}
                        className="bg-amber-600 text-white px-4 py-3 rounded-lg text-sm font-semibold hover:bg-amber-700 transition text-center"
                      >
                        Endorse to Compliance Officer
                      </button>
                    </div>
                  </div>
                )}

              {role === "Requesting Department" &&
                sel.status === "Modification Pre-Approved" && (
                  <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-1">
                        <h3 className="font-bold text-blue-900 text-sm">
                          Step 9: Modification Request Status
                        </h3>
                        <p className="text-xs text-blue-800 mt-1">
                          The Compliance Officer has pre-approved this request.
                          Awaiting final approval from the Compliance Head.
                        </p>
                        {sel.remarks && (
                          <div className="bg-white border border-blue-200 rounded-lg p-3 mt-3">
                            <p className="text-xs font-semibold text-blue-700 mb-1">
                              Endorsed Request Reason:
                            </p>
                            <p className="text-xs text-blue-900 italic">
                              {sel.remarks}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

              {sel.status === "Disapproved" && (
                <div className="bg-gradient-to-r from-red-50 to-red-50 border-l-4 border-red-500 rounded-lg p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="flex-1">
                      <h3 className="font-bold text-red-900 text-sm">
                        Step 8: Application Disapproved
                      </h3>
                      <p className="text-xs text-red-800 mt-1">
                        This application has been <strong>Disapproved</strong>{" "}
                        by the Compliance Head and will not proceed to
                        accreditation.
                      </p>
                      {sel.remarks && (
                        <div className="bg-white border border-red-200 rounded-lg p-3 mt-3">
                          <p className="text-xs font-semibold text-red-700 mb-1">
                            Disapproval Reason:
                          </p>
                          <p className="text-xs text-red-800 italic">
                            {sel.remarks}
                          </p>
                        </div>
                      )}
                      <div className="bg-white border border-red-200 rounded-lg p-3 mt-3 space-y-1.5">
                        <p className="text-xs font-semibold text-red-700">
                          Notifications Sent To:
                        </p>
                        <ul className="text-xs text-red-700 space-y-1 ml-4">
                          <li className="flex items-center gap-2">
                            <span>Requesting Department</span>
                          </li>
                          <li className="flex items-center gap-2">
                            <span>Supplier Company ({sel.supplierName})</span>
                          </li>
                          <li className="flex items-center gap-2">
                            <span>ECPG Compliance Officer</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {sel.status === "Approved" && (
                <>
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500 rounded-lg p-4 space-y-3 mb-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-1">
                        <h3 className="font-bold text-green-900 text-sm">
                          Step 8: Accreditation Completed Successfully
                        </h3>
                        <p className="text-xs text-green-800 mt-1">
                          This supplier has been{" "}
                          <strong>Certified and Approved</strong> by the
                          Compliance Head. The accreditation certificate has
                          been generated and is now available.
                        </p>
                        <div className="bg-white border border-green-200 rounded-lg p-3 mt-3 space-y-1.5">
                          <p className="text-xs font-semibold text-green-700">
                            Notifications Sent To:
                          </p>
                          <ul className="text-xs text-green-700 space-y-1 ml-4">
                            <li className="flex items-center gap-2">
                              <span>Requesting Department</span>
                            </li>
                            <li className="flex items-center gap-2">
                              <span>Supplier Company ({sel.supplierName})</span>
                            </li>
                            <li className="flex items-center gap-2">
                              <span>ECPG Compliance Officer</span>
                            </li>
                            <li className="flex items-center gap-2">
                              <span>ECPG Compliance Head</span>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-5 text-center space-y-2">
                    <p className="font-bold text-green-800 text-lg uppercase tracking-wide">
                      Accreditation Certificate
                    </p>
                    <p className="text-xs text-green-600 mb-2">
                      ECPG Compliance Management System
                    </p>
                    <div className="border-t border-b border-green-200 py-3 my-3">
                      <p className="text-xs text-green-600 mb-1">
                        This certifies that
                      </p>
                      <p className="font-bold text-xl text-green-800">
                        {sel.supplierName}
                      </p>
                      <p className="text-xs text-green-600 mt-1">
                        has successfully completed the supplier accreditation
                        process
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-green-600">Supplier ID</p>
                      <p className="font-mono text-green-700 font-bold text-base">
                        {sel.id}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mt-3 text-xs">
                      <div className="bg-white rounded-lg p-2 border border-green-200">
                        <p className="text-green-600">Enrolled</p>
                        <p className="font-semibold text-green-800">
                          {fmtDate(sel.enrollDate)}
                        </p>
                      </div>
                      <div className="bg-white rounded-lg p-2 border border-green-200">
                        <p className="text-green-600">Approved</p>
                        <p className="font-semibold text-green-800">
                          {fmtDate(sel.approvalDate)}
                        </p>
                      </div>
                    </div>
                    <p className="text-xs text-green-500 mt-3">
                      Valid for transactions with ECPG Companies
                    </p>
                    {canDownload && (
                      <div className="flex gap-2 justify-center mt-3">
                        <button
                          onClick={() => {
                            // Use preModSnapshot (authoritative original) when a mod resubmission is in progress
                            const srcGen =
                              sel.formData?.preModSnapshot?.gen ??
                              sel.formData?.snapshot?.gen ??
                              sel.formData?.gen;
                            const srcBanks =
                              sel.formData?.preModSnapshot?.banks ??
                              sel.formData?.snapshot?.banks ??
                              sel.formData?.banks;
                            const srcSigs =
                              sel.formData?.preModSnapshot?.sigs ??
                              sel.formData?.snapshot?.sigs ??
                              sel.formData?.sigs;
                            const srcClients =
                              sel.formData?.preModSnapshot?.clients ??
                              sel.formData?.snapshot?.clients ??
                              sel.formData?.clients;
                            const srcDocs =
                              sel.formData?.preModSnapshot?.docs ??
                              sel.formData?.snapshot?.docs ??
                              sel.docs;
                            printHtml(
                              `Supplier Information Form — ${sel.supplierName}`,
                              buildFormHtml({
                                ...sel,
                                docs: srcDocs,
                                formData: {
                                  ...sel.formData,
                                  banks: srcBanks,
                                  sigs: srcSigs,
                                  clients: srcClients,
                                  gen: {
                                    bizType: srcGen?.bizType ?? "",
                                    companyName: srcGen?.companyName ?? "",
                                    sec: srcGen?.sec ?? "",
                                    address: srcGen?.address ?? "",
                                    mainCat: srcGen?.mainCat ?? "",
                                    subCat: srcGen?.subCat ?? "",
                                    subCatOther: srcGen?.subCatOther ?? "",
                                    dateEst: fmtDate(srcGen?.dateEst ?? ""),
                                    tel: srcGen?.tel ?? "",
                                    capital: fmtCurrency(srcGen?.capital ?? ""),
                                    payTerms: srcGen?.payTerms ?? "",
                                    tin: srcGen?.tin ?? "",
                                    yearsOp: srcGen?.yearsOp ?? "",
                                  },
                                },
                              }),
                            );
                          }}
                          className="bg-blue-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-blue-700"
                        >
                          Download Form
                        </button>
                        <button
                          onClick={() =>
                            printHtml(
                              `Accreditation Certificate — ${sel.supplierName}`,
                              buildCertHtml(sel),
                            )
                          }
                          className="bg-purple-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-purple-700"
                        >
                          Download Certificate
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* ── Transaction History (US#15) ─────────────────────────────── */}
              {[
                "Compliance Officer",
                "Compliance Head",
                "Requesting Department",
                "General Manager",
              ].includes(role) && (
                <div className="pt-4 border-t mt-2">
                  {/* Key Milestones strip */}
                  {txHistory.length > 0 &&
                    (() => {
                      const fmtDT = (iso: string | undefined) =>
                        iso ? fmtDateTime(iso) : "—";
                      const find = (status: string) =>
                        txHistory.find((e) => e.newStatus === status)
                          ?.changedAt;
                      const milestones: [string, string | undefined, string][] =
                        [
                          [
                            "Submitted",
                            find("Under Review"),
                            "bg-indigo-50 border-indigo-200 text-indigo-700",
                          ],
                          [
                            "Recommended for Approval",
                            find("Recommended for Approval"),
                            "bg-green-50 border-green-200 text-green-700",
                          ],
                          [
                            "Approved",
                            find("Approved"),
                            "bg-emerald-50 border-emerald-300 text-emerald-800",
                          ],
                        ];
                      return (
                        <div className="mb-3 grid grid-cols-3 gap-2 text-xs">
                          {milestones.map(([label, ts, cls]) => (
                            <div
                              key={label}
                              className={`border rounded-lg px-2 py-2 ${cls}`}
                            >
                              <p className="font-semibold mb-0.5">{label}</p>
                              <p className="font-mono leading-tight">
                                {fmtDT(ts)}
                              </p>
                            </div>
                          ))}
                        </div>
                      );
                    })()}

                  <button
                    onClick={() => setTxExpanded((p) => !p)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg text-sm font-semibold text-gray-700 transition"
                  >
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-blue-500 inline-block"></span>
                      Transaction History
                      {txHistory.length > 0 && (
                        <span className="bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                          {txHistory.length}
                        </span>
                      )}
                    </span>
                    <span className="text-gray-400 text-xs">
                      {txExpanded ? "▲ Collapse" : "▼ Expand"}
                    </span>
                  </button>

                  {txExpanded && (
                    <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden">
                      {txLoading ? (
                        <div className="px-4 py-6 text-center text-sm text-gray-400">
                          Loading transaction history…
                        </div>
                      ) : txHistory.length === 0 ? (
                        <div className="px-4 py-6 text-center text-sm text-gray-400">
                          No transactions recorded yet.
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
                          {txHistory.map((entry, idx) => {
                            const isStatusChange = !!(
                              entry.oldStatus || entry.newStatus
                            );
                            const roleBadgeColor: Record<string, string> = {
                              "Compliance Officer":
                                "bg-orange-100 text-orange-700",
                              "Compliance Head":
                                "bg-purple-100 text-purple-700",
                              "Requesting Department":
                                "bg-indigo-100 text-indigo-700",
                              "General Manager":
                                "bg-emerald-100 text-emerald-700",
                              Administrator: "bg-gray-100 text-gray-600",
                              "Supplier Company": "bg-blue-100 text-blue-700",
                            };
                            const roleCls =
                              (entry.actorRole
                                ? roleBadgeColor[entry.actorRole]
                                : null) || "bg-gray-100 text-gray-600";
                            return (
                              <div
                                key={entry.id ?? idx}
                                className="px-4 py-3 flex gap-3 items-start text-xs hover:bg-gray-50 transition"
                              >
                                {/* Timeline dot */}
                                <div className="flex flex-col items-center gap-1 pt-0.5 shrink-0">
                                  <div
                                    className={`w-2.5 h-2.5 rounded-full border-2 ${isStatusChange ? "bg-blue-500 border-blue-300" : "bg-gray-300 border-gray-200"}`}
                                  ></div>
                                  {idx < txHistory.length - 1 && (
                                    <div className="w-px h-full bg-gray-200 min-h-[1rem]"></div>
                                  )}
                                </div>
                                <div className="flex-1 space-y-1">
                                  {/* Timestamp + actor */}
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-gray-400 font-mono">
                                      {fmtDateTime(entry.changedAt)}
                                    </span>
                                    <span className="font-semibold text-gray-800">
                                      {entry.actorName || "—"}
                                    </span>
                                    <span
                                      className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${roleCls}`}
                                    >
                                      {entry.actorRole}
                                    </span>
                                  </div>
                                  {/* Event type */}
                                  <div className="font-semibold text-gray-700">
                                    {entry.eventType}
                                  </div>
                                  {/* Status transition */}
                                  {isStatusChange && (
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                      {entry.oldStatus && (
                                        <Badge
                                          status={
                                            entry.oldStatus as SupplierStatus
                                          }
                                        />
                                      )}
                                      {entry.oldStatus && entry.newStatus && (
                                        <span className="text-gray-400">→</span>
                                      )}
                                      {entry.newStatus && (
                                        <Badge
                                          status={
                                            entry.newStatus as SupplierStatus
                                          }
                                        />
                                      )}
                                    </div>
                                  )}
                                  {/* Note */}
                                  {entry.note && (
                                    <p className="text-gray-500 leading-relaxed whitespace-pre-line">
                                      {entry.note}
                                    </p>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow border p-10 text-center text-gray-400">
              <p className="font-medium">
                Select a supplier from the list to view details and take action.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Supplier Portal (logged in as Supplier Company) ───────────────────────────
function SupplierPortal({
  suppliers,
  setSuppliers,
  addNote,
  actorName,
  actorRole,
  currentUserEmail,
  suppliersLoading,
}: {
  suppliers: Supplier[];
  setSuppliers: React.Dispatch<React.SetStateAction<Supplier[]>>;
  addNote: (msg: string) => void;
  actorName: string;
  actorRole: string;
  currentUserEmail: string;
  suppliersLoading?: boolean;
}) {
  const [activeTab, setActiveTab] = useState<string>("form");
  const [modRequest, setModRequest] = useState<string>("");
  const [rdModScope, setRdModScope] = useState<"form" | "documents" | "both">(
    "both",
  );
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const { api } = await import("./api-client");
      const data = await api.getAllSuppliers();
      setSuppliers(data);
    } catch {
      addNote("Failed to refresh. Please reload the page.");
    } finally {
      setRefreshing(false);
    }
  };

  // Match supplier by the logged-in user's email; fall back to most-recent for legacy/demo accounts
  const normalizedCurrentUserEmail = currentUserEmail?.trim().toLowerCase() || "";

  const mySupplier =
    suppliers.find(
      (s: Supplier) =>
        s.email?.trim().toLowerCase() === normalizedCurrentUserEmail,
    ) ??
    [...suppliers]
      .reverse()
      .find((s: Supplier) =>
        [
          "Enrolled",
          "Form Completion",
          "For Compliance",
          "Modification Approved",
          "Under Review",
          "Recommended for Approval",
          "Approved",
          "Disapproved",
        ].includes(s.status),
      );

  const [evalForms, setEvalForms] = useState<{ [key: string]: string }>({});

  // Sync evalForms with mySupplier's evaluation_forms
  useEffect(() => {
    if (mySupplier?.evaluation_forms) {
      setEvalForms(mySupplier.evaluation_forms);
    }
  }, [mySupplier?.id]);

  // These must all be computed BEFORE the early-return below so that the useEffect
  // that follows is always called on every render (React Rules of Hooks).
  const modScope = mySupplier?.formData?.modScope;
  const isModApprovedCtx = mySupplier?.status === "Modification Approved";
  const isModForComplianceCtx =
    mySupplier?.status === "For Compliance" &&
    !!mySupplier?.formData?.isModificationResubmission;
  const isModCtx = isModApprovedCtx || isModForComplianceCtx;
  const isApproved = mySupplier?.status === "Approved";
  const tabs = [
    {
      id: "form",
      label: "Application Form",
      show: !(isModCtx && modScope === "documents"),
    },
    {
      id: "documents",
      label: "My Documents",
      show: !(isModCtx && modScope === "form"),
    },
    { id: "certificate", label: "Certificate", show: !!isApproved },
    { id: "evaluation", label: "Evaluation Forms", show: true },
  ].filter((t) => t.show);

  // If the active tab is hidden by scope restriction, reset to the first visible tab
  useEffect(() => {
    if (!tabs.find((t) => t.id === activeTab)) {
      setActiveTab(tabs[0]?.id ?? "form");
    }
  }, [modScope, mySupplier?.status]);

  if (!mySupplier)
    return suppliersLoading ? (
      <div className="flex flex-col items-center justify-center py-24 text-center text-gray-400 space-y-3">
        <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
        <p className="text-sm text-gray-500">Loading your application…</p>
      </div>
    ) : suppliers.length === 0 ? (
      <div className="flex flex-col items-center justify-center py-24 text-center text-gray-400 space-y-3">
        <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
        <p className="text-sm text-gray-500">Retrieving your portal access information…</p>
      </div>
    ) : (
      <div className="flex flex-col items-center justify-center py-24 text-center text-gray-400 space-y-5">
        <h3 className="text-xl font-semibold text-gray-700">
          Portal Access Link Required
        </h3>
        <div className="max-w-md bg-blue-50 border border-blue-200 rounded-lg p-4 text-left space-y-2">
          <p className="text-sm text-blue-900 font-semibold">
            How to access your Supplier Portal:
          </p>
          <ol className="text-xs text-blue-800 space-y-1 ml-4 list-decimal">
            <li>
              Your company must be enrolled by an authorized staff member from
              the Requesting Department (Step 1)
            </li>
            <li>
              A system-generated email with your portal access link will be
              automatically sent to your Authorized Representative's registered
              email address
            </li>
            <li>
              Click the link in the email to access your dedicated Supplier
              Portal
            </li>
            <li>
              Complete the Supplier Information Form and upload required
              documents
            </li>
          </ol>
        </div>
        <p className="text-xs text-gray-500 max-w-md">
          If you believe your company has been enrolled but you haven't received
          the access link, please check your email spam/junk folder or contact
          the Requesting Department.
        </p>
      </div>
    );

  const canEdit = [
    "Enrolled",
    "Form Completion",
    "For Compliance",
    "Modification Approved",
  ].includes(mySupplier.status);
  // isApproved, modScope, isModApprovedCtx, isModForComplianceCtx, isModCtx, tabs are all declared before the early return above.

  const scopeLabel = (s: string) =>
    s === "form"
      ? "Form Only"
      : s === "documents"
        ? "Documents Only"
        : "Both Form & Documents";
  const requestMod = async () => {
    if (!modRequest.trim())
      return alert(
        "Please provide a detailed reason for the modification request.",
      );

    try {
      const { api } = await import("./api-client");
      const updated = await api.updateSupplier(mySupplier.id, {
        status: "Modification Endorsement Pending",
        remarks: modRequest,
        formData: {
          ...(mySupplier.formData ?? {}),
          modScope: rdModScope,
          modDenied: false,
          modResubDenied: false,
          previousStatus: mySupplier.status,
        },
        reviewStartDate: TODAY,
        daysInProcess: countWorkingDays(TODAY),
        actorName,
        actorRole,
        actorNote: `Modification request (${scopeLabel(rdModScope)}): ${modRequest}`,
      });
      setSuppliers((prev) =>
        prev.map((s) => (s.id === mySupplier.id ? updated : s)),
      );
      addNote(
        `STEP 9: MODIFICATION REQUEST SUBMITTED\n\n${mySupplier.id} (${mySupplier.supplierName}) - Modification request submitted by Supplier Company.\n\nScope: ${scopeLabel(rdModScope)}\nReason: ${modRequest}\n\nNext Step: Awaiting endorsement by the Requesting Department.`,
      );
      setModRequest("");
      setRdModScope("both");
    } catch (error: any) {
      console.error("Failed to submit modification request:", error);
      const message =
        error?.message ||
        "Failed to submit modification request. Please try again.";
      if (
        message.includes("Authentication required") ||
        message.includes("Invalid or expired token")
      ) {
        sessionStorage.removeItem("currentUser");
        setCurrentUser(null);
        setView("login");
        setAuthView("login");
        return alert("Your session has expired. Please log in again.");
      }
      alert(message);
    }
  };

  return (
    <div className="space-y-4">
      {mySupplier.status === "Modification Approved" && (
        <div className="bg-emerald-50 border-l-4 border-emerald-500 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <h3 className="font-bold text-emerald-900 mb-1">
                Step 9: Modification Approved - You Can Now Edit
              </h3>
              {mySupplier.formData?.modScope &&
                mySupplier.formData.modScope !== "both" && (
                  <div className="bg-emerald-200 border border-emerald-400 rounded-lg px-3 py-2 mb-2">
                    <p className="text-xs font-bold text-emerald-900">
                      Approved Scope:{" "}
                      {mySupplier.formData.modScope === "form"
                        ? "Form / Profile Only"
                        : "Documents Only"}
                    </p>
                    <p className="text-xs text-emerald-800 mt-0.5">
                      {mySupplier.formData.modScope === "form"
                        ? "You can edit your form fields but documents remain locked."
                        : "You can upload/replace documents but form fields remain locked."}
                    </p>
                  </div>
                )}
              <p className="text-sm text-emerald-800 mb-2">
                Your modification request has been approved by the Compliance
                Head.
                {(!mySupplier.formData?.modScope ||
                  mySupplier.formData.modScope === "both") &&
                  " You can now edit your Supplier Information Form and upload new documents."}
                {mySupplier.formData?.modScope === "form" &&
                  " You can now edit your Supplier Information Form."}
                {mySupplier.formData?.modScope === "documents" &&
                  " You can now upload or replace your documents."}
              </p>
              <div className="bg-emerald-100 rounded-lg p-3 border border-emerald-300">
                <p className="text-xs font-semibold text-emerald-900 mb-2">
                  What You Need To Do:
                </p>
                <ul className="text-xs text-emerald-800 space-y-1 ml-4 list-decimal">
                  {(!mySupplier.formData?.modScope ||
                    mySupplier.formData.modScope !== "documents") && (
                    <li>
                      Navigate to the Application Form tab to make your changes
                    </li>
                  )}
                  {(!mySupplier.formData?.modScope ||
                    mySupplier.formData.modScope !== "documents") && (
                    <li>Update any information that needs to be modified</li>
                  )}
                  {(!mySupplier.formData?.modScope ||
                    mySupplier.formData.modScope !== "form") && (
                    <li>
                      Upload new or replacement documents as needed in the
                      Documents tab
                    </li>
                  )}
                  <li>
                    Re-submit your application when all changes are complete
                  </li>
                </ul>
              </div>
              <p className="text-xs text-emerald-700 mt-2 italic">
                Important: After re-submission, your application will undergo
                the full review process (Steps 5-8) again.
              </p>
            </div>
          </div>
        </div>
      )}

      {isApproved &&
        mySupplier.formData?.modDenied &&
        !mySupplier.formData?.modResubDenied && (
          <div className="bg-red-50 border-l-4 border-red-400 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <h3 className="font-bold text-red-900 mb-1">
                  Modification Request Denied
                </h3>
                <p className="text-sm text-red-800 mb-2">
                  Your modification request was reviewed but was{" "}
                  <strong>not approved</strong> by the Compliance Team. Your
                  current accreditation remains active.
                </p>
                {mySupplier.remarks && (
                  <div className="bg-white rounded-lg border border-red-300 p-3 mb-3">
                    <p className="text-xs font-semibold text-red-900 mb-1">
                      Reason:
                    </p>
                    <p className="text-sm text-gray-800">
                      {mySupplier.remarks}
                    </p>
                  </div>
                )}
                <p className="text-xs text-red-700 italic">
                  You may submit a new modification request below if you still
                  wish to make changes.
                </p>
              </div>
            </div>
          </div>
        )}

      {isApproved && mySupplier.formData?.modResubDenied && (
        <div className="bg-orange-50 border-l-4 border-orange-500 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <h3 className="font-bold text-orange-900 mb-1">
                Modification Resubmission Denied
              </h3>
              <p className="text-sm text-orange-800 mb-2">
                Your updated application was reviewed but was{" "}
                <strong>not approved</strong> by the Compliance Team. Your
                original accreditation remains valid and active.
              </p>
              <p className="text-xs text-orange-700 italic">
                You may submit a new modification request below if you wish to
                make further changes.
              </p>
            </div>
          </div>
        </div>
      )}

      {(mySupplier.status === "Modification Endorsement Pending" ||
        mySupplier.status === "Modification Requested" ||
        mySupplier.status === "Modification Pre-Approved") && (
        <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <h3 className="font-bold text-blue-900 mb-1">
                {mySupplier.status === "Modification Endorsement Pending"
                  ? "Step 9: Modification Request Submitted"
                  : mySupplier.status === "Modification Requested"
                    ? "Step 9: Modification Request Endorsed"
                    : "Step 9: Modification Pre-Approved"}
              </h3>
              <p className="text-sm text-blue-800 mb-2">
                {mySupplier.status === "Modification Endorsement Pending"
                  ? "Your modification request has been submitted and is awaiting endorsement by the Requesting Department."
                  : mySupplier.status === "Modification Requested"
                    ? "Your modification request has been endorsed by the Requesting Department and is currently under review by the Compliance Officer."
                    : "Your modification request has been pre-approved by the Compliance Officer and is awaiting final approval from the Compliance Head."}
              </p>
              {mySupplier.remarks && (
                <div className="bg-gray-100 rounded-lg border border-blue-300 p-3 mb-3">
                  <p className="text-xs font-semibold text-blue-900 mb-1">
                    Your Request Reason:
                  </p>
                  <p className="text-sm text-gray-500">{mySupplier.remarks}</p>
                </div>
              )}
              <p className="text-xs text-blue-700 italic">
                You will be notified once your modification request is approved.
                Your forms remain locked until approval is granted.
              </p>
            </div>
          </div>
        </div>
      )}

      {mySupplier.status === "For Compliance" && (
        <div className="bg-yellow-50 border-l-4 border-yellow-500 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <h3 className="font-bold text-yellow-900 mb-1">
                Step 6: Compliance Requirements
              </h3>
              <p className="text-sm text-yellow-800 mb-2">
                Your application has been reviewed by the Compliance Officer.
                Additional information or corrections are required before your
                application can be approved.
              </p>
              {mySupplier.remarks && (
                <div className="bg-white rounded-lg border border-yellow-300 p-3 mb-3">
                  <p className="text-xs font-semibold text-yellow-900 mb-1">
                    Compliance Officer's Remarks:
                  </p>
                  <p className="text-sm text-gray-800">{mySupplier.remarks}</p>
                </div>
              )}
              <div className="bg-yellow-100 rounded-lg p-3 border border-yellow-300">
                <p className="text-xs font-semibold text-yellow-900 mb-2">
                  What You Need To Do:
                </p>
                <ul className="text-xs text-yellow-800 space-y-1 ml-4 list-decimal">
                  <li>Review the Compliance Officer's remarks carefully</li>
                  <li>
                    Edit your Supplier Information Form with the required
                    corrections
                  </li>
                  <li>
                    Re-upload any missing or incorrect documents (new uploads
                    will replace old ones)
                  </li>
                  <li>Submit additional supporting documents if requested</li>
                  <li>
                    Once all corrections are made, re-submit your application
                    using the Documents tab
                  </li>
                </ul>
              </div>
              <p className="text-xs text-yellow-700 mt-2 italic">
                Tip: Navigate through the form tabs below to make your
                corrections, then go to the Documents & Submit tab to re-submit
                your application.
              </p>
            </div>
          </div>
        </div>
      )}

      {mySupplier.status === "Disapproved" && (
        <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <h3 className="font-bold text-red-900 mb-1">
                Application Disapproved
              </h3>
              <p className="text-sm text-red-800 mb-2">
                Your accreditation application has been{" "}
                <strong>disapproved</strong> by the Compliance Head. The form is
                locked. Please contact the Requesting Department if you have
                questions or wish to re-apply.
              </p>
              {mySupplier.remarks && (
                <div className="bg-white rounded-lg border border-red-300 p-3 mb-3">
                  <p className="text-xs font-semibold text-red-900 mb-1">
                    Reason:
                  </p>
                  <p className="text-sm text-gray-800">{mySupplier.remarks}</p>
                </div>
              )}
              <p className="text-xs text-red-700 italic">
                If you have questions regarding this decision, please contact
                the Requesting Department or the Compliance team.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow border p-4">
        <div className="flex justify-between items-start mb-3">
          <div>
            <h2 className="text-xl font-bold text-gray-800">
              {mySupplier.supplierName}
            </h2>
            <p className="text-xs font-mono text-blue-700">{mySupplier.id}</p>
          </div>
          <div className="text-right space-y-1">
            <Badge status={mySupplier.status} />
            <div>
              <SlaBadge sla={stageSlaOf(mySupplier)} />
            </div>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="text-xs text-blue-600 hover:text-blue-800 underline disabled:opacity-50"
              title="Refresh your application status"
            >
              {refreshing ? "Refreshing…" : "↺ Refresh status"}
            </button>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div className="bg-gray-50 rounded p-2 text-center">
            <p className="text-gray-500">Enrolled</p>
            <p className="font-semibold text-gray-800">
              {fmtDate(mySupplier.enrollDate)}
            </p>
          </div>
          <div className="bg-gray-50 rounded p-2 text-center">
            <p className="text-gray-500">Days in Process</p>
            <p className="font-semibold text-gray-800">
              {liveDays(mySupplier)} days
            </p>
          </div>
          <div className="bg-gray-50 rounded p-2 text-center">
            <p className="text-gray-500">Company</p>
            <p className="font-semibold text-gray-800">{mySupplier.company}</p>
          </div>
        </div>
      </div>

      <div className="bg-white border-b shadow-sm">
        <div className="flex gap-0 overflow-x-auto">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition whitespace-nowrap ${activeTab === t.id ? "border-blue-600 text-blue-700" : "border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-300"}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === "form" && canEdit && (
        <SupplierInfoForm
          supplier={mySupplier}
          setSuppliers={setSuppliers}
          addNote={addNote}
        />
      )}

      {activeTab === "form" && !canEdit && (
        <div className="bg-white rounded-xl shadow border p-6">
          {mySupplier.status === "Disapproved" ? (
            <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <h3 className="font-bold text-red-900 text-sm">
                    Application Disapproved
                  </h3>
                  <p className="text-xs text-red-800 mt-1">
                    Your accreditation application has been{" "}
                    <strong>disapproved</strong>. Please contact the Requesting
                    Department if you have questions or wish to re-apply.
                  </p>
                  {mySupplier.remarks && (
                    <div className="bg-white border border-red-200 rounded-lg p-3 mt-3">
                      <p className="text-xs font-semibold text-red-700 mb-1">
                        Reason:
                      </p>
                      <p className="text-xs text-red-900 italic">
                        {mySupplier.remarks}
                      </p>
                    </div>
                  )}
                  <p className="text-xs text-red-700 mt-2 italic">
                    If you have questions regarding this decision, please
                    contact the Requesting Department or the Compliance team.
                  </p>
                </div>
              </div>
            </div>
          ) : isApproved ? (
            <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <h3 className="font-bold text-green-900 text-sm">
                    Form Locked - Accreditation Approved
                  </h3>
                  <p className="text-xs text-green-800 mt-1">
                    Your application has been approved and your form is now{" "}
                    <strong>locked</strong> to preserve the certified
                    information.
                  </p>
                  <p className="text-xs text-green-700 mt-2 italic">
                    If you need to modify your information, scroll down to
                    submit a modification request.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <h3 className="font-bold text-blue-900 text-sm">
                    Form Locked - Under Review
                  </h3>
                  <p className="text-xs text-blue-800 mt-1">
                    Your application is currently under review. The form is{" "}
                    <strong>locked</strong> during the evaluation process.
                  </p>
                  <p className="text-xs text-blue-700 mt-2 italic">
                    You will be notified if any changes are needed.
                  </p>
                </div>
              </div>
            </div>
          )}
          <div className="flex items-center gap-3 mb-4">
            <div>
              <h3 className="font-bold text-gray-800">Application Form</h3>
              <p className="text-xs text-gray-500">
                {mySupplier.status === "Disapproved"
                  ? "Application disapproved"
                  : isApproved
                    ? "Approved and certified"
                    : "Your form is currently under review"}
              </p>
            </div>
          </div>
          {mySupplier.formData?.gen && (
            <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
              {(() => {
                // Only use snapshot when a modification is actively in progress (not after a completed revert)
                const modInProgress =
                  mySupplier.formData?.isModificationResubmission;
                const origGen = modInProgress
                  ? (mySupplier.formData?.preModSnapshot?.gen ??
                    mySupplier.formData?.snapshot?.gen)
                  : undefined;
                const displayGen = origGen ?? mySupplier.formData.gen;
                return (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <span className="text-gray-500">Company:</span>{" "}
                      <span className="font-semibold">
                        {displayGen.companyName}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Type:</span>{" "}
                      <span className="font-semibold">
                        {displayGen.bizType}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">TIN:</span>{" "}
                      <span className="font-semibold">{displayGen.tin}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Category:</span>{" "}
                      <span className="font-semibold">
                        {displayGen.mainCat}
                      </span>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
          {isApproved && (
            <div className="mt-4 flex gap-2">
              <button
                onClick={() => {
                  // Only use preModSnapshot when a modification edit is actively in progress
                  // (supplier has been granted Modification Approved and is editing/under review).
                  // After a completed revert (modResubDenied), formData is already the original.
                  const modInProgress =
                    mySupplier.formData?.isModificationResubmission;
                  const pdfSnap = modInProgress
                    ? (mySupplier.formData?.preModSnapshot ??
                      mySupplier.formData?.snapshot)
                    : undefined;
                  const pdfSupplier = pdfSnap
                    ? {
                        ...mySupplier,
                        docs: pdfSnap.docs ?? mySupplier.docs,
                        formData: {
                          ...mySupplier.formData,
                          gen: pdfSnap.gen ?? mySupplier.formData?.gen,
                          banks: pdfSnap.banks ?? mySupplier.formData?.banks,
                          sigs: pdfSnap.sigs ?? mySupplier.formData?.sigs,
                          clients:
                            pdfSnap.clients ?? mySupplier.formData?.clients,
                        },
                      }
                    : mySupplier;
                  printHtml(
                    `Supplier Information Form — ${mySupplier.supplierName}`,
                    buildFormHtml(pdfSupplier),
                  );
                }}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700"
              >
                Download Form PDF
              </button>
            </div>
          )}
        </div>
      )}

      {activeTab === "documents" &&
        canEdit &&
        isModCtx &&
        modScope === "documents" && (
          <SupplierInfoForm
            supplier={mySupplier}
            setSuppliers={setSuppliers}
            addNote={addNote}
          />
        )}

      {activeTab === "documents" &&
        !(canEdit && isModCtx && modScope === "documents") && (
          <div className="bg-white rounded-xl shadow border p-6">
            <div className="flex items-center gap-3 mb-4">
              <div>
                <h3 className="font-bold text-gray-800">My Documents</h3>
                <p className="text-xs text-gray-500">
                  Uploaded requirements and attachments
                </p>
              </div>
            </div>
            {mySupplier.docs && Object.keys(mySupplier.docs).length > 0 ? (
              <div className="space-y-2">
                {Object.entries(mySupplier.docs).map(([key, val]) => (
                  <div
                    key={key}
                    className="flex items-center justify-between bg-gray-50 rounded-lg p-3"
                  >
                    <div className="flex items-center gap-3">
                      <div>
                        <p className="text-sm font-semibold text-gray-800">
                          {key}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => triggerDownload(val)}
                      className="bg-blue-100 text-blue-700 px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-blue-200"
                    >
                      Download
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 text-center py-6">
                No documents uploaded yet. Go to Application Form tab to upload.
              </p>
            )}
          </div>
        )}

      {activeTab === "certificate" && isApproved && (
        <div className="bg-white rounded-xl shadow border p-6">
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-8 text-center space-y-3">
            <div className="text-6xl"></div>
            <p className="font-bold text-green-800 text-2xl uppercase tracking-wide">
              Accreditation Certificate
            </p>
            <p className="text-xs text-green-600 mb-2">
              ECPG Compliance Management System
            </p>
            <div className="border-t border-b border-green-200 py-4 my-4">
              <p className="text-sm text-green-600 mb-2">This certifies that</p>
              <p className="font-bold text-2xl text-green-800">
                {mySupplier.supplierName}
              </p>
              <p className="text-sm text-green-600 mt-2">
                has successfully completed the supplier accreditation process
                and is authorized to transact with ECPG companies
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-green-600">Supplier ID</p>
              <p className="font-mono text-green-700 font-bold text-lg">
                {mySupplier.id}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="bg-white rounded-lg p-3 border border-green-200">
                <p className="text-xs text-green-600">Date Enrolled</p>
                <p className="font-semibold text-green-800 mt-1">
                  {fmtDate(mySupplier.enrollDate)}
                </p>
              </div>
              <div className="bg-white rounded-lg p-3 border border-green-200">
                <p className="text-xs text-green-600">Date Approved</p>
                <p className="font-semibold text-green-800 mt-1">
                  {fmtDate(mySupplier.approvalDate)}
                </p>
              </div>
            </div>
            <p className="text-xs text-green-500 mt-4">
              This certificate is valid for all transactions with Easycall
              Communications Philippines, Inc. and Transnational E-Business
              Solutions Inc.
            </p>
            <div className="flex gap-3 justify-center mt-4">
              <button
                onClick={() =>
                  printHtml(
                    `Accreditation Certificate — ${mySupplier.supplierName}`,
                    buildCertHtml(mySupplier),
                  )
                }
                className="bg-green-600 text-white px-6 py-3 rounded-lg text-sm font-semibold hover:bg-green-700 flex items-center gap-2"
              >
                <span>Download Certificate</span>
              </button>
              <button
                onClick={() => {
                  const modInProgress =
                    mySupplier.formData?.isModificationResubmission;
                  const pdfSnap = modInProgress
                    ? (mySupplier.formData?.preModSnapshot ??
                      mySupplier.formData?.snapshot)
                    : undefined;
                  const pdfSupplier = pdfSnap
                    ? {
                        ...mySupplier,
                        docs: pdfSnap.docs ?? mySupplier.docs,
                        formData: {
                          ...mySupplier.formData,
                          gen: pdfSnap.gen ?? mySupplier.formData?.gen,
                          banks: pdfSnap.banks ?? mySupplier.formData?.banks,
                          sigs: pdfSnap.sigs ?? mySupplier.formData?.sigs,
                          clients:
                            pdfSnap.clients ?? mySupplier.formData?.clients,
                        },
                      }
                    : mySupplier;
                  printHtml(
                    `Supplier Information Form — ${mySupplier.supplierName}`,
                    buildFormHtml(pdfSupplier),
                  );
                }}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg text-sm font-semibold hover:bg-blue-700 flex items-center gap-2"
              >
                <span>Download Form</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === "evaluation" && (
        <div className="bg-white rounded-xl shadow border overflow-hidden">
          <div className="px-5 py-3 border-b bg-gray-50">
            <h3 className="font-bold text-gray-800 text-sm">
              Evaluation Forms
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Evaluation forms are uploaded and managed by Compliance Officers
              and the Compliance Head.
            </p>
          </div>
          {Object.keys(evalForms).length === 0 ? (
            <div className="px-5 py-10 text-center text-xs text-gray-400">
              No evaluation forms have been uploaded for your application yet.
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {Object.entries(evalForms).map(([key, val]) => {
                const valIsUrl =
                  typeof val === "string" && val.startsWith("https://");
                const keyIsUrl =
                  !valIsUrl &&
                  typeof key === "string" &&
                  key.startsWith("https://");
                const downloadUrl = valIsUrl ? val : keyIsUrl ? key : null;
                const displayName = valIsUrl
                  ? decodeURIComponent(
                      key.replace(/^\d+-/, "").replace(/_/g, " "),
                    )
                  : keyIsUrl
                    ? decodeURIComponent(
                        (key.split("/").pop() ?? key)
                          .replace(/^\d+-/, "")
                          .replace(/_/g, " "),
                      )
                    : typeof val === "string" && val
                      ? val
                      : key;
                return (
                  <div
                    key={key}
                    className="flex items-center justify-between px-5 py-3"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-indigo-50 border border-indigo-200 flex items-center justify-center text-indigo-600 text-xs font-bold flex-shrink-0">
                        📄
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-800">
                          {displayName}
                        </p>
                        <p className="text-xs text-gray-400">
                          Evaluation Document
                        </p>
                      </div>
                    </div>
                    {downloadUrl ? (
                      <button
                        onClick={() => triggerDownload(downloadUrl)}
                        className="bg-blue-100 text-blue-700 px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-blue-200"
                      >
                        Download
                      </button>
                    ) : (
                      <span className="text-xs text-gray-400 italic px-3 py-1.5">
                        Unavailable
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {isApproved && (activeTab === "form" || activeTab === "documents") && (
        <div className="bg-white rounded-xl shadow border p-4">
          <div className="bg-indigo-50 border-l-4 border-indigo-500 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <h4 className="font-semibold text-indigo-900 text-sm mb-1">
                  Request Form Modification (Step 9)
                </h4>
                <p className="text-xs text-indigo-800 mb-2">
                  Your form is currently <strong>locked</strong> because your
                  accreditation is approved. If you need to update your
                  information or documents, submit a modification request.
                </p>
                <div className="bg-white border border-indigo-200 rounded-lg p-3 mt-2">
                  <p className="text-xs font-semibold text-indigo-900 mb-2">
                    Approval Process:
                  </p>
                  <ol className="text-xs text-indigo-800 space-y-1 ml-4 list-decimal">
                    <li>
                      Your request will be endorsed by the Requesting Department
                    </li>
                    <li>Compliance Officer will review for pre-approval</li>
                    <li>Compliance Head will grant final approval</li>
                    <li>
                      Once approved, you can edit your form and upload new
                      documents
                    </li>
                    <li>
                      After re-submission, your application will undergo Steps
                      5-8 review again
                    </li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
          <div className="mb-3">
            <label className="block text-xs font-semibold text-indigo-900 mb-2">
              What do you need to modify?
            </label>
            <div className="flex gap-2 flex-wrap">
              {(
                [
                  ["form", "Form / Profile Only"],
                  ["documents", "Documents Only"],
                  ["both", "Both Form & Documents"],
                ] as const
              ).map(([v, l]) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setRdModScope(v)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${rdModScope === v ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-gray-600 border-gray-300 hover:border-indigo-400"}`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
          <textarea
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-3 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            rows={3}
            value={modRequest}
            onChange={(e) => setModRequest(e.target.value)}
            placeholder="Describe in detail what needs to be modified and the reason for this request..."
          />
          <button
            onClick={requestMod}
            className="w-full bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-indigo-700 transition"
          >
            Submit Modification Request to Requesting Department
          </button>
        </div>
      )}
    </div>
  );
}

// ── Sample Test Data for Demo ─────────────────────────────────────────────────
// SLA Status calculated automatically based on daysInProcess:
// - Days 0-7: On-time
// - Days 8-10: Warning
// - Days 11+: Delayed
/* Commented out - now using database
function createSampleSupplier(id: string, company: Company, supplierName: string, rep: string, email: string, status: SupplierStatus, enrollDate: string, daysInProcess: number, remarks: string, approvalDate?: string): Supplier {
  return { id, company, supplierName, rep, email, status, enrollDate, approvalDate, daysInProcess, slaStatus: calculateSLA(daysInProcess), remarks, formData: {}, docs: {}, evaluation_forms: {} };
}
*/

// SAMPLE_SUPPLIERS removed - now using database
/* const SAMPLE_SUPPLIERS: Supplier[] = [
  createSampleSupplier("ECP-2026-001", "ECP", "Tech Solutions Inc", "John Smith", "john@techsolutions.com", "Approved", "2026-02-01", 8, "All documents verified - Day 8: Compliance Officer approved", "2026-02-09"),
  createSampleSupplier("TESI-2026-001", "TESI", "Global Supplies Ltd", "Jane Doe", "jane@globalsupplies.com", "Under Review", "2026-02-10", 9, "Day 9: Final review by Head of Compliance pending"),
  createSampleSupplier("ECP-2026-002", "ECP", "Quality Parts Co", "Bob Wilson", "bob@qualityparts.com", "Approved", "2026-01-25", 7, "Fast tracked - Completed within SLA", "2026-02-01"),
  createSampleSupplier("TESI-2026-002", "TESI", "Premium Materials", "Alice Brown", "alice@premium.com", "Recommended for Approval", "2026-02-05", 12, "Day 12: EXCEEDED SLA - Delayed document submission"),
  createSampleSupplier("ECP-2026-003", "ECP", "Fast Logistics", "Charlie Green", "charlie@fastlog.com", "Form Completion", "2026-02-15", 5, "Day 5: Within Compliance Officer review period"),
  createSampleSupplier("TESI-2026-003", "TESI", "Industrial Equipment", "Diana Lee", "diana@industrial.com", "Under Review", "2026-02-08", 11, "Day 11: EXCEEDED SLA - Document verification delayed"),
  createSampleSupplier("ECP-2026-004", "ECP", "Safety First Ltd", "Edward Kim", "edward@safetyfirst.com", "Approved", "2026-01-30", 6, "Day 6: Excellent compliance - Completed in Head approval phase", "2026-02-05"),
  createSampleSupplier("ECP-2026-005", "ECP", "Metro Services", "Fiona Chen", "fiona@metro.com", "Enrolled", "2026-02-17", 3, "Day 3: Recently enrolled - On track"),
  createSampleSupplier("TESI-2026-004", "TESI", "Omega Trading", "George Park", "george@omega.com", "Recommended for Approval", "2026-02-07", 10, "Day 10: At SLA deadline - Head of Compliance final decision needed"),
  createSampleSupplier("ECP-2026-006", "ECP", "Delta Manufacturing", "Helen Tan", "helen@delta.com", "Under Review", "2026-02-12", 8, "Day 8: Approaching deadline - Compliance Officer reviewing"),
]; */

// ── Admin Panel ──────────────────────────────────────────────────────────────
const ALL_ROLES: Role[] = [
  "Requesting Department",
  "Supplier Company",
  "Compliance Officer",
  "Compliance Head",
  "General Manager",
  "Administrator",
];
const ROLE_ICONS: Record<Role, string> = {
  "Requesting Department": "",
  "Supplier Company": "",
  "Compliance Officer": "",
  "Compliance Head": "",
  "General Manager": "",
  Administrator: "",
};

function AdminPanel({
  addNote,
  currentUserId,
  currentUserRole,
}: {
  addNote: (msg: string) => void;
  currentUserId: string;
  currentUserRole: string;
}) {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<"manage" | "create">("manage");
  // create-user form
  const emptyForm = {
    firstName: "",
    lastName: "",
    email: "",
    position: "",
    role: "" as Role | "",
    password: "",
  };
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState("");
  const [formLoading, setFormLoading] = useState(false);
  const [createdCreds, setCreatedCreds] = useState<{
    name: string;
    username: string;
    email: string;
    password: string;
    role: string;
  } | null>(null);
  // Supplier Company accounts are created via supplier enrollment flow.
  const CREATABLE_ROLES = ALL_ROLES.filter((r) => r !== "Supplier Company");
  // All roles available when editing — admin may need to reassign an existing account to Supplier Company
  const EDITABLE_ROLES = ALL_ROLES;
  // inline editing
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editRole, setEditRole] = useState<Role>("Requesting Department");
  const [editPosition, setEditPosition] = useState("");
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editPassword, setEditPassword] = useState("");
  const [editUsername, setEditUsername] = useState("");
  const [showCreatePassword, setShowCreatePassword] = useState(false);
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [userPage, setUserPage] = useState(1);
  const [userSearch, setUserSearch] = useState("");
  const [userPositionFilter, setUserPositionFilter] = useState("");
  const [userRoleFilter, setUserRoleFilter] = useState<Role | "">("");

  const handleUserRoleFilterChange = (value: string) => {
    setUserRoleFilter(value === "" ? "" : (value as Role));
  };

  const handleEditRoleChange = (value: string) => {
    setEditRole(value as Role);
  };

  const handleCreateRoleChange = (value: string) => {
    setForm({ ...form, role: value as Role });
  };

  useEffect(() => {
    setUserPage(1);
  }, [userSearch, userPositionFilter, userRoleFilter]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const { api } = await import("./api-client");
      setUsers(await api.getAllUsers());
    } catch (e: any) {
      addNote(`Failed to load users: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");
    if (
      !form.firstName.trim() ||
      !form.lastName.trim() ||
      !form.email.trim() ||
      !form.password ||
      !form.position.trim() ||
      !form.role
    )
      return setFormError("All fields are required.");
    if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(form.email))
      return setFormError(
        "Please enter a valid email address (e.g. name@company.com).",
      );
    const strongPw = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
    if (!strongPw.test(form.password))
      return setFormError(
        "Password must be at least 8 characters and include uppercase, lowercase, number, and special character.",
      );
    setFormLoading(true);
    try {
      const { api } = await import("./api-client");
      const created = await api.createUser({
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        password: form.password,
        position: form.position,
        role: form.role,
      });
      setUsers((prev) => [created, ...prev]);
      setCreatedCreds({
        name: created.name,
        username: created.username,
        email: created.email,
        password: form.password,
        role: created.role,
      });
      setForm(emptyForm);
      addNote(`User "${created.name}" (${created.role}) created successfully.`);
    } catch (e: any) {
      setFormError(e.message || "Failed to create user.");
    } finally {
      setFormLoading(false);
    }
  };

  const startEdit = (u: User) => {
    setEditingId(u.id);
    setEditRole(u.role);
    setEditPosition(u.position);
    setEditName(u.name);
    setEditEmail(u.email);
    setEditUsername(u.username);
    setEditPassword("");
    setShowEditPassword(false);
  };

  const saveEdit = async (u: User) => {
    if (editPassword) {
      // Strong password: min 8 chars, 1 uppercase, 1 lowercase, 1 digit, 1 symbol
      const strongPw =
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
      if (!strongPw.test(editPassword))
        return addNote(
          "Failed to update user: New password must be at least 8 characters and include uppercase, lowercase, number, and symbol.",
        );
    }

    if (editEmail.trim() && editEmail.trim() !== u.email) {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(editEmail.trim())) {
        return addNote("Failed to update user: Please enter a valid email address.");
      }
    }

    const trimmedUsername = editUsername.trim();
    if (trimmedUsername !== u.username) {
      const usernamePattern = /^[a-z]+\.[a-z]+$/;
      if (!usernamePattern.test(trimmedUsername)) {
        return addNote("Failed to update user: Username must follow the 'firstname.lastname' format (lowercase letters only).");
      }
    }

    try {
      const { api } = await import("./api-client");
      const payload: {
        role?: Role;
        position?: string;
        name?: string;
        email?: string;
        username?: string;
        adminResetPassword?: string;
      } = {};
      if (editRole !== u.role) payload.role = editRole;
      if (editPosition !== u.position) payload.position = editPosition;
      if (editName !== u.name) payload.name = editName;
      if (editEmail.trim() && editEmail.trim() !== u.email) payload.email = editEmail.trim();
      if (trimmedUsername !== u.username) payload.username = trimmedUsername;
      if (editPassword.trim()) payload.adminResetPassword = editPassword.trim();
      const updated = await api.updateUser(u.id, payload);
      setUsers((prev) => prev.map((x) => (x.id === u.id ? updated : x)));
      addNote(
        `User "${updated.name}" updated — Role: ${updated.role}.${editPassword.trim() ? " Password reset by admin." : ""}`,
      );
    } catch (e: any) {
      addNote(`Failed to update user: ${e.message}`);
    } finally {
      setEditingId(null);
      setEditPassword("");
      setShowEditPassword(false);
    }
  };

  const handleDelete = async (u: User) => {
    if (u.id === currentUserId)
      return alert("You cannot delete your own account.");
    if (
      !confirm(
        `Delete user "${u.name}" (${u.email})?\n\nThis action cannot be undone.`,
      )
    )
      return;
    try {
      const { api } = await import("./api-client");
      await api.deleteUser(u.id);
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
      addNote(`User "${u.name}" deleted.`);
    } catch (e: any) {
      console.error("Delete user failed:", e);
      addNote("Unable to delete user with pending application");
    }
  };

  const filteredUsers = users.filter((u) => {
    const query = userSearch.trim().toLowerCase();
    const matchesSearch =
      !query ||
      u.name.toLowerCase().includes(query) ||
      u.username.toLowerCase().includes(query) ||
      u.email.toLowerCase().includes(query);
    const matchesPosition =
      !userPositionFilter || u.position === userPositionFilter;
    const matchesRole = !userRoleFilter || u.role === userRoleFilter;
    return matchesSearch && matchesPosition && matchesRole;
  });

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow border p-5">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-xl font-bold text-gray-800">User Management</h2>
          <span className="text-xs bg-purple-100 text-purple-800 px-2.5 py-1 rounded-full font-medium">
            {currentUserRole}
          </span>
        </div>
        <p className="text-sm text-gray-500">
          Create new portal users and manage existing accounts and roles.
        </p>
      </div>

      <div className="bg-white border-b shadow-sm">
        <div className="flex gap-0">
          {(["manage", "create"] as const).map((t) => {
            const buttonClass =
              tab === t
                ? "px-5 py-3 text-sm font-medium border-b-2 transition border-blue-600 text-blue-700"
                : "px-5 py-3 text-sm font-medium border-b-2 transition border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-300";
            return (
              <button key={t} onClick={() => setTab(t)} className={buttonClass}>
                {t === "manage"
                  ? `Manage Users (${users.length})`
                  : "Create New User"}
              </button>
            );
          })}
        </div>
      </div>

      {tab === "manage" && (
        <div className="bg-white rounded-xl shadow border overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-400">Loading users…</div>
          ) : filteredUsers.length === 0 ? (
            <div className="p-8 text-center text-gray-400">
              No users match the current search or filters.
            </div>
          ) : (
            <div>
              <div className="border-b border-gray-200 bg-gray-50 px-4 py-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex-1 min-w-0">
                    <input
                      type="text"
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      placeholder="Search by name, username, or email"
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                  </div>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <select
                      value={userPositionFilter}
                      onChange={(e) => setUserPositionFilter(e.target.value)}
                      className="border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    >
                      <option value="">All positions</option>
                      {Array.from(
                        new Set(users.map((u) => u.position).filter(Boolean)),
                      )
                        .sort()
                        .map((position) => (
                          <option key={position} value={position}>
                            {position}
                          </option>
                        ))}
                    </select>
                    <select
                      value={userRoleFilter}
                      onChange={(e) =>
                        handleUserRoleFilterChange(e.target.value)
                      }
                      className="border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    >
                      <option value="">All roles</option>
                      {ALL_ROLES.map((role) => (
                        <option key={role} value={role}>
                          {role}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <p className="mt-3 text-xs text-gray-500">
                  Showing {filteredUsers.length} of {users.length} users.
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      {[
                        "Name",
                        "Username",
                        "Email",
                        "Position",
                        "Role",
                        "Created",
                        "Actions",
                      ].map((h) => (
                        <th
                          key={h}
                          className="px-4 py-3 text-left text-xs font-semibold text-gray-600"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredUsers
                      .slice(
                        (userPage - 1) * DASH_PAGE_SIZE,
                        userPage * DASH_PAGE_SIZE,
                      )
                      .map((u) => {
                        const rowClass =
                          u.id === currentUserId
                            ? "hover:bg-gray-50 bg-blue-50"
                            : "hover:bg-gray-50";

                        if (editingId === u.id) {
                          return (
                            <tr key={u.id} className={rowClass}>
                              <td className="px-4 py-2">
                                <input
                                  className="border rounded px-2 py-1 text-xs w-full"
                                  value={editName}
                                  onChange={(e) => setEditName(e.target.value)}
                                />
                              </td>
                              <td className="px-4 py-2">
                                <input
                                  className="border rounded px-2 py-1 text-xs w-full font-mono"
                                  value={editUsername}
                                  onChange={(e) => setEditUsername(e.target.value.toLowerCase())}
                                  placeholder="firstname.lastname"
                                />
                              </td>
                              <td className="px-4 py-2">
                                <input
                                  className="border rounded px-2 py-1 text-xs w-full"
                                  value={editEmail}
                                  onChange={(e) => setEditEmail(e.target.value)}
                                />
                              </td>
                              <td className="px-4 py-2">
                                <input
                                  className="border rounded px-2 py-1 text-xs w-full"
                                  value={editPosition}
                                  onChange={(e) =>
                                    setEditPosition(e.target.value)
                                  }
                                />
                              </td>
                              <td className="px-4 py-2">
                                {u.role === "Supplier Company" ? (
                                  <span className="text-xs text-gray-500">
                                    {u.role}
                                  </span>
                                ) : (
                                  <select
                                    value={editRole}
                                    onChange={(e) =>
                                      handleEditRoleChange(e.target.value)
                                    }
                                    className="border rounded px-2 py-1 text-xs"
                                  >
                                    {EDITABLE_ROLES.filter(
                                      (r) => r !== "Supplier Company",
                                    ).map((r) => (
                                      <option key={r} value={r}>
                                        {ROLE_ICONS[r]} {r}
                                      </option>
                                    ))}
                                  </select>
                                )}
                              </td>
                              <td className="px-4 py-2 text-gray-400 text-xs">
                                {u.createdAt ? fmtDate(u.createdAt) : "—"}
                              </td>
                              <td className="px-4 py-2">
                                <div className="flex flex-col gap-1.5">
                                  <div className="relative">
                                    <input
                                      type={
                                        showEditPassword ? "text" : "password"
                                      }
                                      placeholder="Password (optional)"
                                      value={editPassword}
                                      onChange={(e) =>
                                        setEditPassword(e.target.value)
                                      }
                                      className="border rounded px-2 py-1 pr-12 text-xs w-full"
                                    />
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setShowEditPassword((prev) => !prev)
                                      }
                                      className="absolute inset-y-0 right-2 text-[10px] text-blue-600 hover:text-blue-700 font-medium"
                                    >
                                      {showEditPassword ? "Hide" : "Show"}
                                    </button>
                                  </div>
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => saveEdit(u)}
                                      className="text-xs bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
                                    >
                                      Save
                                    </button>
                                    <button
                                      onClick={() => setEditingId(null)}
                                      className="text-xs bg-gray-200 text-gray-700 px-3 py-1 rounded hover:bg-gray-300"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          );
                        }

                        return (
                          <tr key={u.id} className={rowClass}>
                            <td className="px-4 py-3 font-medium text-gray-800">
                              {u.name}
                              {u.id === currentUserId && (
                                <span className="ml-2 text-xs text-blue-500">
                                  (you)
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-gray-600 font-mono text-xs">
                              {u.username}
                            </td>
                            <td className="px-4 py-3 text-gray-500">
                              {u.email}
                            </td>
                            <td className="px-4 py-3 text-gray-600">
                              {u.position}
                            </td>
                            <td className="px-4 py-3">
                              <span className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">
                                {ROLE_ICONS[u.role]} {u.role}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-gray-400 text-xs">
                              {u.createdAt ? fmtDate(u.createdAt) : "—"}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex gap-2">
                                <button
                                  onClick={() => startEdit(u)}
                                  className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDelete(u)}
                                  disabled={u.id === currentUserId}
                                  className="text-xs bg-red-100 text-red-700 px-3 py-1 rounded hover:bg-red-200 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
              <Pager
                page={userPage}
                total={Math.max(
                  1,
                  Math.ceil(filteredUsers.length / DASH_PAGE_SIZE),
                )}
                count={filteredUsers.length}
                onChange={setUserPage}
              />
            </div>
          )}
        </div>
      )}

      {tab === "create" && (
        <div className="bg-white rounded-xl shadow border p-6 max-w-lg">
          <h3 className="font-bold text-gray-800 mb-4">Create New User</h3>
          {createdCreds && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-5">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="font-bold text-green-800 text-sm">
                    User Created Successfully
                  </p>
                  <p className="text-xs text-green-600 mt-0.5">
                    Share the credentials below with the new user.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setCreatedCreds(null);
                    setTab("manage");
                  }}
                  className="text-xs text-green-700 hover:text-green-900 font-medium border border-green-300 px-2.5 py-1 rounded-lg hover:bg-green-100 transition"
                >
                  View Users
                </button>
              </div>
              <div className="bg-white rounded-lg border border-green-200 p-3.5 space-y-1.5 text-sm">
                <div className="flex gap-2">
                  <span className="text-gray-400 w-24 shrink-0 text-xs">
                    Name
                  </span>
                  <span className="font-medium text-gray-800">
                    {createdCreds.name}
                  </span>
                </div>
                <div className="flex gap-2">
                  <span className="text-gray-400 w-24 shrink-0 text-xs">
                    Username
                  </span>
                  <span className="font-mono font-medium text-gray-800">
                    {createdCreds.username}
                  </span>
                </div>
                <div className="flex gap-2">
                  <span className="text-gray-400 w-24 shrink-0 text-xs">
                    Email
                  </span>
                  <span className="text-gray-800">{createdCreds.email}</span>
                </div>
                <div className="flex gap-2">
                  <span className="text-gray-400 w-24 shrink-0 text-xs">
                    Role
                  </span>
                  <span className="text-gray-800">{createdCreds.role}</span>
                </div>
                <div className="flex gap-2 pt-1.5 border-t border-green-100">
                  <span className="text-gray-400 w-24 shrink-0 text-xs">
                    Password
                  </span>
                  <span className="font-mono font-bold text-blue-700">
                    {createdCreds.password}
                  </span>
                </div>
              </div>
              <p className="text-xs text-green-600 mt-2.5">
                The user can sign in with their username and change their
                password after first login.
              </p>
            </div>
          )}
          {formError && (
            <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
              {formError}
            </div>
          )}
          <form onSubmit={handleCreate} className="space-y-4">
            {(
              [
                ["firstName", "First Name", "text", "e.g. Juan"],
                ["lastName", "Last Name", "text", "e.g. dela Cruz"],
                ["email", "Email Address", "email", "user@example.com"],
                ["position", "Position", "text", "e.g. Procurement Manager"],
              ] as const
            ).map(([key, label, type, ph]) => (
              <div key={key}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {label} *
                </label>
                <input
                  type={type}
                  value={(form as any)[key]}
                  placeholder={ph}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
            ))}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role *
              </label>
              <select
                value={form.role}
                onChange={(e) => handleCreateRoleChange(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                <option value="">-- Select Role --</option>
                {CREATABLE_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_ICONS[r]} {r}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Temporary Password *
              </label>
              <div className="relative">
                <input
                  type={showCreatePassword ? "text" : "password"}
                  value={form.password}
                  placeholder="Password"
                  onChange={(e) =>
                    setForm({ ...form, password: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 pr-16 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
                <button
                  type="button"
                  onClick={() => setShowCreatePassword((prev) => !prev)}
                  className="absolute inset-y-0 right-3 text-xs text-blue-600 hover:text-blue-700 font-medium"
                >
                  {showCreatePassword ? "Hide" : "Show"}
                </button>
              </div>
              <p className="text-xs text-amber-700 mt-1.5 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                <strong>Password requirements:</strong> Min. 8 characters, must
                include uppercase, lowercase, number, and special character
                (e.g. !@#$%).
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Share this temporary password with the new user. They can update
                it later.
              </p>
            </div>
            <button
              type="submit"
              disabled={formLoading}
              className="w-full bg-blue-600 text-white py-2.5 rounded-lg font-semibold text-sm hover:bg-blue-700 transition disabled:opacity-50"
            >
              {formLoading ? "Creating..." : "Create User"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

// ── Forgot Password Form ─────────────────────────────────────────────────────
function ForgotPasswordForm({ onBack }: { onBack: () => void }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!email.trim()) {
      setError("Please enter your email address.");
      return;
    }
    setLoading(true);
    try {
      const { api } = await import("./api-client");
      await api.forgotPassword(email.trim());
      setSent(true);
    } catch (err: any) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-blue-800 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full">
        <div className="text-center mb-6">
          <img
            src={EasyCallLogo}
            alt="EasyCall Logo"
            className="h-16 mx-auto mb-4 object-contain"
          />
          <h1 className="text-2xl font-bold text-gray-800">Forgot Password</h1>
          <p className="text-gray-400 text-sm mt-1">
            Enter your email to receive a reset link
          </p>
        </div>
        {sent ? (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-300 text-green-800 px-4 py-4 rounded-lg text-sm">
              <p className="font-semibold mb-1">Check your email</p>
              <p>
                If <strong>{email}</strong> is registered, a password reset link
                has been sent. The link expires in 1 hour.
              </p>
            </div>
            <button
              onClick={onBack}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition text-sm"
            >
              Back to Sign In
            </button>
          </div>
        ) : (
          <>
            {error && (
              <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
                {error}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Registered Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                  placeholder="your.email@example.com"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Sending…" : "Send Reset Link"}
              </button>
            </form>
            <div className="mt-4 text-center">
              <button
                onClick={onBack}
                className="text-sm text-blue-600 hover:underline"
              >
                ← Back to Sign In
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Reset Password Form ───────────────────────────────────────────────────────
function ResetPasswordForm({
  token,
  onSuccess,
}: {
  token: string;
  onSuccess: () => void;
}) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    const strongPw =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/;
    if (!strongPw.test(password)) {
      setError(
        "Password must be at least 8 characters and include uppercase, lowercase, number, and special character.",
      );
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      const { api } = await import("./api-client");
      await api.resetPassword(token, password);
      setDone(true);
    } catch (err: any) {
      setError(
        err.message || "Failed to reset password. The link may have expired.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-blue-800 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full">
        <div className="text-center mb-6">
          <img
            src={EasyCallLogo}
            alt="EasyCall Logo"
            className="h-16 mx-auto mb-4 object-contain"
          />
          <h1 className="text-2xl font-bold text-gray-800">Reset Password</h1>
          <p className="text-gray-400 text-sm mt-1">
            Create a new password for your account
          </p>
        </div>
        {done ? (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-300 text-green-800 px-4 py-4 rounded-lg text-sm">
              <p className="font-semibold mb-1">Password reset successfully!</p>
              <p>You can now sign in with your new password.</p>
            </div>
            <button
              onClick={onSuccess}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition text-sm"
            >
              Sign In
            </button>
          </div>
        ) : (
          <>
            {error && (
              <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
                {error}
              </div>
            )}
            {!token && (
              <div className="bg-yellow-50 border border-yellow-300 text-yellow-800 px-4 py-3 rounded-lg mb-4 text-sm">
                Invalid or missing reset token. Please use the link from your
                email.
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={!token}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 pr-16 focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                    placeholder="Password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute inset-y-0 right-3 text-xs text-blue-600 hover:text-blue-700 font-medium disabled:text-gray-400"
                    disabled={!token}
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirm ? "text" : "password"}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    disabled={!token}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 pr-16 focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                    placeholder="Password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm((prev) => !prev)}
                    className="absolute inset-y-0 right-3 text-xs text-blue-600 hover:text-blue-700 font-medium disabled:text-gray-400"
                    disabled={!token}
                  >
                    {showConfirm ? "Hide" : "Show"}
                  </button>
                </div>
              </div>
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                <strong>Password requirements:</strong> Min. 8 characters, must
                include uppercase, lowercase, number, and special character
                (e.g. !@#$%).
              </p>
              <button
                type="submit"
                disabled={loading || !token}
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Resetting…" : "Reset Password"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

// ── Login Form ────────────────────────────────────────────────────────────────
function LoginForm({
  onLogin,
  onForgotPassword,
}: {
  onLogin: (user: User) => void;
  onForgotPassword: () => void;
}) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!username.trim() || !password) {
      setError("Username and password are required.");
      return;
    }

    setLoading(true);
    try {
      const { api } = await import("./api-client");
      const user = await api.login({ username, password });
      onLogin(user);
    } catch (err: any) {
      setError(err.message || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-blue-800 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full">
        <div className="text-center mb-6">
          <img
            src={EasyCallLogo}
            alt="EasyCall Logo"
            className="h-16 mx-auto mb-4 object-contain"
          />
          <h1 className="text-2xl font-bold text-gray-800">Sign In</h1>
          <p className="text-gray-400 text-sm mt-1">
            Supplier Accreditation Portal
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Username
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
              placeholder="Username"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 pr-16 focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                placeholder="Enter your password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute inset-y-0 right-3 text-xs text-blue-600 hover:text-blue-700 font-medium"
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
            <div className="text-right mt-1.5">
              <button
                type="button"
                onClick={onForgotPassword}
                className="text-xs text-blue-600 hover:underline"
              >
                Forgot Password?
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Signing In..." : "Sign In"}
          </button>
        </form>

        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-400 text-center">
            Accounts are created and role-assigned by Administrator or
            Compliance Head.
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Audit Trail Panel ────────────────────────────────────────────────────────
function AuditTrailPanel({ auditVersion }: { auditVersion: number }) {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [selSupplierId, setSelSupplierId] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  useEffect(() => {
    loadAudit();
    const interval = setInterval(() => {
      import("./api-client").then(({ api }) =>
        api
          .getAuditTrail()
          .then(setEntries)
          .catch(() => {}),
      );
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  // Refresh immediately when any action occurs elsewhere in the app
  useEffect(() => {
    if (auditVersion > 0) {
      import("./api-client").then(({ api }) =>
        api
          .getAuditTrail()
          .then(setEntries)
          .catch(() => {}),
      );
    }
  }, [auditVersion]);

  useEffect(() => {
    setPage(1);
  }, [selSupplierId, search]);

  const loadAudit = async () => {
    setLoading(true);
    setError("");
    try {
      const { api } = await import("./api-client");
      setEntries(await api.getAuditTrail());
    } catch (e: any) {
      setError(e.message || "Failed to load audit trail");
    } finally {
      setLoading(false);
    }
  };

  const systemCount = entries.filter((e) => !e.supplierId).length;
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  useEffect(() => {
    import("./api-client").then(({ api }) => {
      api
        .getAllSuppliers()
        .then(setSuppliers)
        .catch(() => {});
    });
  }, []);
  const uniqueSuppliers = [
    ...new Map(
      entries
        .filter((e) => e.supplierId)
        .map((e) => [e.supplierId, e.supplierName]),
    ).entries(),
  ]
    .map(([id, name]) => {
      const count = entries.filter((x) => x.supplierId === id).length;
      const createdAt = suppliers.find((s) => s.id === id)?.createdAt || "";
      return { id: id!, name: name!, count, createdAt };
    })
    .sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      // If counts are equal, sort by most recently added (createdAt desc)
      return (b.createdAt || "").localeCompare(a.createdAt || "");
    });

  const filtered = entries.filter((e) => {
    if (selSupplierId === "system" && e.supplierId) return false;
    if (
      selSupplierId !== "all" &&
      selSupplierId !== "system" &&
      e.supplierId !== selSupplierId
    )
      return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        (e.supplierName || "").toLowerCase().includes(q) ||
        (e.actorName || "").toLowerCase().includes(q) ||
        (e.newStatus || "").toLowerCase().includes(q) ||
        (e.note || "").toLowerCase().includes(q) ||
        (e.eventType || "").toLowerCase().includes(q)
      );
    }
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const mins = Math.floor(diffMs / 60000);
    const hours = Math.floor(mins / 60);
    const days = Math.floor(hours / 24);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return fmtDate(d.toISOString().split("T")[0]);
  };

  const formatFull = (iso: string) => {
    const d = new Date(iso);
    return (
      fmtDate(d.toISOString().split("T")[0]) +
      ", " +
      d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })
    );
  };

  const roleStyles: Record<string, string> = {
    "Requesting Department": "bg-blue-100 text-blue-800",
    "Supplier Company": "bg-emerald-100 text-emerald-800",
    "Compliance Officer": "bg-yellow-100 text-yellow-800",
    "Compliance Head": "bg-purple-100 text-purple-800",
    "General Manager": "bg-indigo-100 text-indigo-800",
    Administrator: "bg-red-100 text-red-800",
  };

  const statusDot: Record<string, string> = {
    Enrolled: "bg-blue-500",
    "Form Completion": "bg-indigo-500",
    "Under Review": "bg-yellow-500",
    "Recommended for Approval": "bg-purple-500",
    "For Compliance": "bg-orange-500",
    Approved: "bg-green-500",
    Disapproved: "bg-red-500",
    "Returned with Remarks": "bg-amber-500",
    "Modification Requested": "bg-sky-500",
    "Modification Pre-Approved": "bg-violet-500",
    "Modification Approved": "bg-teal-500",
  };

  const eventDot: Record<string, string> = {
    status_change: "bg-gray-400",
    enrollment: "bg-blue-500",
    login: "bg-green-500",
    logout: "bg-slate-400",
    password_change: "bg-amber-500",
    user_created: "bg-emerald-500",
    user_deleted: "bg-red-500",
    profile_update: "bg-cyan-500",
    document_upload: "bg-violet-500",
  };

  const eventLabel: Record<string, string> = {
    status_change: "Status Changed",
    enrollment: "Enrolled",
    login: "Signed In",
    logout: "Signed Out",
    password_change: "Password Changed",
    user_created: "Account Created",
    user_deleted: "Account Deleted",
    profile_update: "Profile Updated",
    document_upload: "Documents Updated",
  };

  const isSupplierEvent = (et: string) =>
    et === "status_change" || et === "enrollment";

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-xl shadow border p-5">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-800">Audit Trail</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Complete activity log — status changes, logins, password changes,
              document updates, and more.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400">
              {entries.length} event{entries.length !== 1 ? "s" : ""}
            </span>
            <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse inline-block" />
              Live
            </span>
            <button
              onClick={loadAudit}
              disabled={loading}
              className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-gray-200 transition disabled:opacity-40"
            >
              {loading ? "Loading…" : "Refresh"}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      <div className="flex gap-4 items-start">
        {/* Supplier sidebar */}
        <div className="w-52 shrink-0 bg-white rounded-xl shadow border overflow-hidden">
          <div className="px-3 py-2.5 bg-gray-50 border-b">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Suppliers
            </p>
          </div>
          <div className="overflow-y-auto" style={{ maxHeight: "600px" }}>
            <button
              onClick={() => {
                setSelSupplierId("all");
                setSearch("");
              }}
              className={`w-full text-left px-3 py-2.5 text-sm flex justify-between items-center border-b transition ${selSupplierId === "all" ? "bg-blue-50 text-blue-700 font-semibold" : "hover:bg-gray-50 text-gray-700"}`}
            >
              <span>All Activity</span>
              <span
                className={`text-xs px-1.5 py-0.5 rounded-full ${selSupplierId === "all" ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-500"}`}
              >
                {entries.length}
              </span>
            </button>
            {systemCount > 0 && (
              <button
                onClick={() => {
                  setSelSupplierId("system");
                  setSearch("");
                }}
                className={`w-full text-left px-3 py-2.5 text-sm flex justify-between items-center border-b transition ${selSupplierId === "system" ? "bg-slate-100 text-slate-700 font-semibold" : "hover:bg-gray-50 text-gray-700"}`}
              >
                <span>System Events</span>
                <span
                  className={`text-xs px-1.5 py-0.5 rounded-full ${selSupplierId === "system" ? "bg-slate-200 text-slate-600" : "bg-gray-100 text-gray-500"}`}
                >
                  {systemCount}
                </span>
              </button>
            )}
            {uniqueSuppliers.map((s) => (
              <button
                key={s.id}
                onClick={() => {
                  setSelSupplierId(s.id);
                  setSearch("");
                }}
                className={`w-full text-left px-3 py-2.5 flex items-center justify-between gap-2 border-b last:border-0 transition ${selSupplierId === s.id ? "bg-blue-50" : "hover:bg-gray-50"}`}
              >
                <div className="min-w-0 flex-1">
                  <p
                    className={`text-xs font-semibold truncate ${selSupplierId === s.id ? "text-blue-800" : "text-gray-800"}`}
                  >
                    {s.name}
                  </p>
                  <p className="text-xs font-mono text-gray-400 truncate">
                    {s.id}
                  </p>
                </div>
                <span
                  className={`text-xs px-1.5 py-0.5 rounded-full shrink-0 ${selSupplierId === s.id ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-500"}`}
                >
                  {s.count}
                </span>
              </button>
            ))}
            {uniqueSuppliers.length === 0 && !loading && (
              <p className="text-xs text-gray-400 text-center py-6 px-3">
                No activity yet.
              </p>
            )}
          </div>
        </div>

        {/* Timeline */}
        <div className="flex-1 bg-white rounded-xl shadow border overflow-hidden min-w-0">
          <div className="px-4 py-3 border-b bg-gray-50">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by supplier, actor, status, or note…"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          {loading ? (
            <div className="p-12 text-center text-gray-400 text-sm">
              Loading audit trail…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-gray-500 font-medium text-sm">
                {entries.length === 0
                  ? "No activity recorded yet."
                  : "No results match your search."}
              </p>
            </div>
          ) : (
            <div className="overflow-y-auto divide-y">
              {paginated.map((entry) => (
                <div
                  key={entry.id}
                  className="px-4 py-4 hover:bg-gray-50 transition"
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-1.5 shrink-0">
                      <div
                        className={`w-2.5 h-2.5 rounded-full ${
                          isSupplierEvent(entry.eventType)
                            ? statusDot[entry.newStatus || ""] || "bg-gray-400"
                            : eventDot[entry.eventType] || "bg-gray-400"
                        }`}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      {/* Supplier name or event type label + timestamp */}
                      <div className="flex items-start justify-between gap-2 mb-1.5 flex-wrap">
                        <div className="flex items-center gap-2 flex-wrap">
                          {entry.supplierId && selSupplierId === "all" && (
                            <button
                              onClick={() =>
                                setSelSupplierId(entry.supplierId!)
                              }
                              className="text-xs font-bold text-blue-700 hover:underline text-left"
                            >
                              {entry.supplierName}
                              <span className="font-mono font-normal text-blue-500 ml-1.5">
                                ({entry.supplierId})
                              </span>
                            </button>
                          )}
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                              isSupplierEvent(entry.eventType)
                                ? "bg-gray-100 text-gray-600"
                                : eventDot[entry.eventType]
                                  ? eventDot[entry.eventType]
                                      .replace("bg-", "bg-")
                                      .replace("-500", "-100") +
                                    " text-" +
                                    eventDot[entry.eventType]
                                      .replace("bg-", "")
                                      .replace("-500", "-800")
                                  : "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {eventLabel[entry.eventType] || entry.eventType}
                          </span>
                        </div>
                        <span
                          className="text-xs text-gray-400 ml-auto shrink-0"
                          title={formatFull(entry.changedAt)}
                        >
                          {formatTime(entry.changedAt)}
                        </span>
                      </div>
                      {/* Status transition — only for status/enrollment events */}
                      {isSupplierEvent(entry.eventType) && (
                        <div className="flex items-center gap-2 flex-wrap mb-1.5">
                          {entry.oldStatus ? (
                            <>
                              <Badge
                                status={entry.oldStatus as SupplierStatus}
                              />
                              <span className="text-gray-400 text-xs">→</span>
                            </>
                          ) : (
                            <span className="text-xs text-gray-400 italic">
                              Initial enrollment
                            </span>
                          )}
                          {entry.newStatus && (
                            <Badge status={entry.newStatus as SupplierStatus} />
                          )}
                        </div>
                      )}
                      {/* Actor */}
                      {(entry.actorName || entry.actorRole) && (
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-xs text-gray-600 font-medium">
                            {entry.actorName || "Unknown"}
                          </span>
                          {entry.actorRole && (
                            <span
                              className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${roleStyles[entry.actorRole] || "bg-gray-100 text-gray-600"}`}
                            >
                              {entry.actorRole}
                            </span>
                          )}
                        </div>
                      )}
                      {/* Note */}
                      {entry.note && (
                        <p className="text-xs text-gray-500 mt-1.5 italic line-clamp-2">
                          {entry.note.split("\n")[0]}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {!loading && (
            <Pager
              page={page}
              total={totalPages}
              count={filtered.length}
              onChange={setPage}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Change Password ─────────────────────────────────────────────────────────
function ChangePassword({
  userId,
  addNote,
}: {
  userId: string;
  addNote: (msg: string) => void;
}) {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess(false);
    if (!form.current) return setError("Please enter your current password.");
    // Strong password: min 8 chars, 1 uppercase, 1 lowercase, 1 digit, 1 symbol
    const strongPw =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/;
    if (!strongPw.test(form.next))
      return setError(
        "Password must be at least 8 characters and include uppercase, lowercase, number, and symbol.",
      );
    if (form.next !== form.confirm)
      return setError("New passwords do not match.");
    setLoading(true);
    try {
      const { api } = await import("./api-client");
      await api.changePassword(userId, form.current, form.next);
      setSuccess(true);
      setForm({ current: "", next: "", confirm: "" });
      addNote("Password changed successfully.");
    } catch (err: any) {
      setError(err.message || "Failed to change password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto">
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h2 className="text-lg font-bold text-gray-800 mb-1">
          Change Password
        </h2>
        <p className="text-sm text-gray-500 mb-5">
          Enter your current password, then choose a new one.
        </p>

        {success && (
          <div className="bg-green-50 border border-green-300 text-green-700 px-4 py-3 rounded-lg mb-4 text-sm">
            Password changed successfully!
          </div>
        )}
        {error && (
          <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Current Password
            </label>
            <div className="relative">
              <input
                type={showCurrent ? "text" : "password"}
                value={form.current}
                onChange={(e) => setForm({ ...form, current: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-16 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Password"
                required
              />
              <button
                type="button"
                onClick={() => setShowCurrent((prev) => !prev)}
                className="absolute inset-y-0 right-3 text-xs text-blue-600 hover:text-blue-700 font-medium"
              >
                {showCurrent ? "Hide" : "Show"}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              New Password
            </label>
            <div className="relative">
              <input
                type={showNext ? "text" : "password"}
                value={form.next}
                onChange={(e) => setForm({ ...form, next: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-16 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Password"
                required
              />
              <button
                type="button"
                onClick={() => setShowNext((prev) => !prev)}
                className="absolute inset-y-0 right-3 text-xs text-blue-600 hover:text-blue-700 font-medium"
              >
                {showNext ? "Hide" : "Show"}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirm New Password
            </label>
            <div className="relative">
              <input
                type={showConfirm ? "text" : "password"}
                value={form.confirm}
                onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-16 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Password"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirm((prev) => !prev)}
                className="absolute inset-y-0 right-3 text-xs text-blue-600 hover:text-blue-700 font-medium"
              >
                {showConfirm ? "Hide" : "Show"}
              </button>
            </div>
          </div>
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
            <strong>Password requirements:</strong> Min. 8 characters, must
            include uppercase, lowercase, number, and special character (e.g.
            !@#$%).
          </p>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition disabled:opacity-50"
          >
            {loading ? "Saving..." : "Change Password"}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Session Expired Modal ────────────────────────────────────────────────────
function SessionExpiredModal({ onDismiss }: { onDismiss: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
      <div className="bg-white rounded-xl shadow-2xl p-8 max-w-sm w-full mx-4 text-center">
        <div className="text-5xl mb-4">⏱</div>
        <h2 className="text-xl font-bold text-gray-800 mb-2">
          Session Expired
        </h2>
        <p className="text-gray-500 text-sm mb-6">
          You were automatically logged out after 10 minutes of inactivity.
          Please log in again to continue.
        </p>
        <button
          onClick={onDismiss}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 rounded-lg transition"
        >
          Log In Again
        </button>
      </div>
    </div>
  );
}

// ── Main App ──────────────────────────────────────────────────────────────────
export default function App() {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [suppliersLoading, setSuppliersLoading] = useState<boolean>(() => {
    // If a user is already persisted, suppliers will be loaded async — start in loading state
    try {
      return !!sessionStorage.getItem("currentUser");
    } catch {
      return false;
    }
  });
  const [view, setView] = useState<string>(() => {
    try {
      const saved = sessionStorage.getItem("currentUser");
      if (saved) {
        const user = JSON.parse(saved) as User;
        const ROLE_VIEWS: Record<string, string[]> = {
          "Supplier Company": ["form", "password"],
          Administrator: ["admin", "audit", "password"],
          "Requesting Department": [
            "dashboard",
            "review",
            "enroll",
            "password",
          ],
          "Compliance Officer": ["dashboard", "review", "password"],
          "Compliance Head": ["dashboard", "review", "admin", "password"],
        };
        const savedView = sessionStorage.getItem("activeView");
        const allowed = ROLE_VIEWS[user.role] ?? ["dashboard"];
        if (savedView && allowed.includes(savedView)) return savedView;
        if (user.role === "Supplier Company") return "form";
        if (user.role === "Administrator") return "admin";
        return "dashboard";
      }
    } catch {
      /* ignore */
    }
    return "dashboard";
  });
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [notes, setNotes] = useState<string[]>([]);
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(() => {
    if (typeof window === "undefined") return null;
    return parseStoredCurrentUser();
  });
  // Derive role directly from currentUser so they are always in sync.
  // Having role as separate state caused desync on reload (old role from a
  // previous session could persist while currentUser switched to a different user).
  const role: Role | null = currentUser?.role ?? null;

  useEffect(() => {
    if (!currentUser) return;
    if (!currentUser.token || typeof currentUser.token !== 'string' || !currentUser.token.trim()) {
      sessionStorage.removeItem('currentUser');
      setCurrentUser(null);
      setView('login');
      setAuthView('login');
    }
  }, [currentUser]);

  const [auditVersion, setAuditVersion] = useState<number>(0);
  const [authView, setAuthView] = useState<"login" | "forgot" | "reset">(
    "login",
  );
  const [resetToken, setResetToken] = useState<string>("");
  const [sessionExpired, setSessionExpired] = useState(false);

  // Detect password reset token in URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("reset_token");
    if (token) {
      setResetToken(token);
      setAuthView("reset");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const IDLE_TIMEOUT = 10 * 60 * 1000; // 10 minutes in milliseconds
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      // Auto-logout due to inactivity
      sessionStorage.removeItem("currentUser");
      sessionStorage.removeItem("activeView");
      setSessionExpired(true);
      setCurrentUser(null);
      setView("dashboard");
      setPendingId(null);
      setAuthView("login");
    }, IDLE_TIMEOUT);
  }, [IDLE_TIMEOUT]);

  // Attach / detach activity listeners while a user is logged in
  useEffect(() => {
    if (!currentUser) {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      return;
    }
    const events = [
      "mousemove",
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
      "click",
    ] as const;
    events.forEach((e) =>
      window.addEventListener(e, resetIdleTimer, { passive: true }),
    );
    resetIdleTimer(); // start the timer immediately on login
    return () => {
      events.forEach((e) => window.removeEventListener(e, resetIdleTimer));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [currentUser, resetIdleTimer]);

  // Load suppliers from database on mount
  useEffect(() => {
    if (currentUser) {
      setSuppliersLoading(true);
      const loadSuppliers = async () => {
        try {
          const { api } = await import("./api-client");
          const data = await api.getAllSuppliers();
          setSuppliers(data);
        } catch (error: any) {
          console.error("Failed to load suppliers:", error);
          const message = error?.message || "Failed to load suppliers from database";
          if (
            message.includes("Authentication required") ||
            message.includes("Invalid or expired token") ||
            message.includes("403") ||
            message.includes("401")
          ) {
            sessionStorage.removeItem("currentUser");
            sessionStorage.removeItem("activeView");
            setCurrentUser(null);
            setAuthView("login");
          } else {
            addNote("Failed to load suppliers from database");
          }
        } finally {
          setSuppliersLoading(false);
        }
      };
      loadSuppliers();
    } else {
      setSuppliersLoading(false);
    }
  }, [currentUser]);

  // Poll supplier data periodically so queue counts stay current across roles
  useEffect(() => {
    if (!currentUser) return;
    const intervalId = setInterval(async () => {
      try {
        const { api } = await import("./api-client");
        const data = await api.getAllSuppliers();
        setSuppliers(data);
      } catch {
        // ignore polling errors silently
      }
    }, 15000);
    return () => clearInterval(intervalId);
  }, [currentUser]);

  const refreshSuppliers = async () => {
    try {
      const { api } = await import("./api-client");
      const data = await api.getAllSuppliers();
      setSuppliers(data);
    } catch (error) {
      console.error("Failed to refresh suppliers:", error);
    }
  };

  const addNote = async (msg: string) => {
    setNotes((prev: string[]) => [...prev, msg]);
    setTimeout(() => setNotes((prev: string[]) => prev.slice(1)), 4000);
    setAuditVersion((v) => v + 1);

    // Save note to database
    try {
      const { api } = await import("./api-client");
      await api.addNote(msg);
    } catch (error) {
      console.error("Failed to save note:", error);
    }
  };

  const handleLogin = (user: AuthUser) => {
    if (!user.token || typeof user.token !== 'string' || !user.token.trim()) {
      addNote('Login failed: missing authentication token.');
      return;
    }
    sessionStorage.setItem("currentUser", JSON.stringify(user));
    // Set loading=true before currentUser so SupplierPortal renders the spinner
    // instead of "Portal Access Link Required" during the initial suppliers fetch.
    if (user.role === "Supplier Company") setSuppliersLoading(true);
    setCurrentUser(user);
    setView(
      user.role === "Supplier Company"
        ? "form"
        : user.role === "Administrator"
          ? "admin"
          : "dashboard",
    );
    addNote(`Welcome back, ${user.name}!`);
  };

  const navItems = [
    role !== null &&
      role !== "Supplier Company" &&
      role !== "Administrator" && { id: "dashboard", label: "Dashboard" },
    role === "Requesting Department" && {
      id: "enroll",
      label: "Enroll Supplier",
    },
    role === "Supplier Company" && { id: "form", label: "My Application" },
    [
      "Compliance Officer",
      "Compliance Head",
      "Requesting Department",
      "General Manager",
    ].includes(role || "") && { id: "review", label: "Review Queue" },
    (role === "Administrator" || role === "Compliance Head") && {
      id: "admin",
      label: "User Management",
    },
    role === "Administrator" && { id: "audit", label: "Audit Trail" },
  ].filter(Boolean) as { id: string; label: string }[];

  const signOut = async () => {
    try {
      const { api } = await import("./api-client");
      await api.logAuditEvent({
        eventType: "logout",
        actorName: currentUser?.name ?? undefined,
        actorRole: role as string,
        note: `Signed out: ${currentUser?.email}`,
      });
    } catch {
      /* ignore audit errors on logout */
    }
    sessionStorage.removeItem("currentUser");
    sessionStorage.removeItem("activeView");
    setCurrentUser(null);
    setView("dashboard");
    setPendingId(null);
    setAuthView("login");
    addNote("You have been signed out successfully.");
  };

  // Persist the active view so page refresh restores the last page
  useEffect(() => {
    if (currentUser) {
      try {
        sessionStorage.setItem("activeView", view);
      } catch {
        /* ignore */
      }
    }
  }, [view, currentUser]);

  // Show authentication screens if user is not logged in
  if (!currentUser) {
    return (
      <>
        {sessionExpired && (
          <SessionExpiredModal onDismiss={() => setSessionExpired(false)} />
        )}
        {authView === "forgot" ? (
          <ForgotPasswordForm onBack={() => setAuthView("login")} />
        ) : authView === "reset" ? (
          <ResetPasswordForm
            token={resetToken}
            onSuccess={() => setAuthView("login")}
          />
        ) : (
          <LoginForm
            onLogin={handleLogin}
            onForgotPassword={() => setAuthView("forgot")}
          />
        )}
      </>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Toast notes={notes} />

      <div className="bg-gradient-to-r from-blue-800 to-blue-700 text-white px-4 py-3 flex items-center justify-between shadow-lg">
        <div className="flex items-center gap-3">
          <img
            src={EasyCallLogo}
            alt="EasyCall Logo"
            className="h-8 w-auto object-contain"
          />
          <div>
            <div className="font-bold text-sm">
              Supplier Accreditation Portal
            </div>
            <div className="text-xs opacity-60">ECPG Compliance System</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-xs font-semibold">{currentUser.name}</div>
            <div className="text-xs opacity-60">{currentUser.position}</div>
          </div>
          <button
            onClick={() => setView("password")}
            title="Change Password"
            className="text-xs bg-white bg-opacity-10 hover:bg-opacity-20 border border-white border-opacity-30 px-2.5 py-1 rounded-lg transition"
          >
            Change Password
          </button>
          <span className="text-xs bg-white bg-opacity-20 border border-white border-opacity-30 px-2.5 py-1 rounded-full font-medium">
            {role}
          </span>
          <button
            onClick={signOut}
            className="text-xs opacity-60 hover:opacity-100 underline ml-1"
          >
            Sign Out
          </button>
        </div>
      </div>

      <div className="bg-white border-b shadow-sm px-4">
        <div className="flex gap-0 overflow-x-auto">
          {navItems.map((n: { id: string; label: string }) => (
            <button
              key={n.id}
              onClick={() => setView(n.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition whitespace-nowrap ${view === n.id ? "border-blue-600 text-blue-700" : "border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-300"}`}
            >
              {n.label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-4 md:p-6 max-w-6xl mx-auto">
        {view === "dashboard" &&
          role !== "Supplier Company" &&
          role !== "Administrator" && (
            <Dashboard
              suppliers={suppliers}
              onSelectSupplier={(id) => {
                setPendingId(id);
                setView("review");
              }}
              onNav={setView}
              role={role as Role}
              loading={suppliersLoading}
            />
          )}
        {view === "enroll" &&
          role === "Requesting Department" &&
          currentUser && (
            <EnrollForm
              setSuppliers={setSuppliers}
              addNote={addNote}
              onDone={() => setView("dashboard")}
              actorName={currentUser.name}
              actorRole={currentUser.role}
            />
          )}
        {view === "form" && role === "Supplier Company" && currentUser && (
          <SupplierPortal
            suppliers={suppliers}
            setSuppliers={setSuppliers}
            addNote={addNote}
            actorName={currentUser.name}
            actorRole={currentUser.role}
            currentUserEmail={currentUser.email}
            suppliersLoading={suppliersLoading}
          />
        )}
        {view === "review" &&
          role &&
          [
            "Compliance Officer",
            "Compliance Head",
            "Requesting Department",
            "General Manager",
          ].includes(role) &&
          currentUser && (
            <ReviewPanel
              suppliers={suppliers}
              setSuppliers={setSuppliers}
              role={role}
              addNote={addNote}
              initialId={pendingId}
              actorName={currentUser.name}
              refreshSuppliers={refreshSuppliers}
            />
          )}
        {view === "admin" &&
          (role === "Administrator" || role === "Compliance Head") &&
          currentUser && (
            <AdminPanel
              addNote={addNote}
              currentUserId={currentUser.id}
              currentUserRole={role as string}
            />
          )}
        {view === "audit" && role === "Administrator" && (
          <AuditTrailPanel auditVersion={auditVersion} />
        )}
        {view === "password" && currentUser && (
          <ChangePassword userId={currentUser.id} addNote={addNote} />
        )}
      </div>
    </div>
  );
}
